import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Typography,
  Button,
  Box,
  Container,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  Snackbar,
  CircularProgress,
  Skeleton,
  Pagination,
  Select,
  FormControl,
  MenuItem as SelectMenuItem,
  ToggleButton,
  ToggleButtonGroup,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  Collapse,
  Tooltip,
  Stack,
  Slider,
} from '@mui/material';
import {
  Refresh,
  Face,
  Delete,
  Visibility,
  PersonAdd,
  VpnKey,
  Restore,
  Clear,
  ExpandMore,
  ExpandLess,
  Search,
  FilterList,
  VolumeUp,
  Login,
  Logout,
  CalendarMonth,
  Sync,
  DateRange,
  ArrowDropDown,
  Mic,
  CloudUpload,
  Stop,
  Close,
  SmartToy,
  ArrowBackIos,
  ArrowForwardIos,
  Edit,
  DeleteOutline,
  SwapHoriz,
  Videocam,
  Tune,
  FileDownload,
} from '@mui/icons-material';
import Popover from '@mui/material/Popover';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { DateRangePicker } from 'react-date-range';
import { format, startOfDay, endOfDay, isSameDay, addDays, subDays, startOfMonth, endOfMonth, subMonths, startOfWeek, endOfWeek, eachDayOfInterval, getDay, addMonths, subMonths as subMonthsFn } from 'date-fns';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import AudioPlayer from 'react-h5-audio-player';
import toWav from 'audiobuffer-to-wav';
import 'react-h5-audio-player/lib/styles.css';
import {
  getStoredToken,
  softDeleteFace,
  getFaceImage,
  restoreFace,
  updateFaceImage,
  verifyFaceQualityBase64,
  fetchCrmContacts,
  registerFaceBase64,
  searchCrmContacts,
  checkPersonExists,
  fetchHrmsEmployees,
  searchHrmsEmployees,
  fetchOrgDesignations,
  fetchOrgDepartments,
  updateHrmsEmployeeName,
  getHrmsSyncStatus,
  getCompanyPickerConfig,
  getPersons,
  uploadEmployeeVoice,
  fetchCompanies,
  getApiKeys,
  getKivoBranches,
  refreshKivoCompanyBranches,
  updatePersonName,
  updatePersonDetails,
  updatePersonCameraAccess,
  getCameraStreams,
  listAIVoiceApiKeys,
  getDefaultAIVoiceKeySelection,
  saveAIVoiceApiKey,
  generateEmployeeAIVoice,
  updateHrmsEmployeeValidity,
} from '../utils/api';
import {
  isOrgMasterId,
  lookupOrgMasterNameById,
  normalizeOrgMasterOptions,
  orgMasterOptionId,
  orgMasterOptionLabel,
  personOrgMasterFilterValue,
  resolvePersonOrgMasterDisplay,
  orgMasterPlaceholderOption,
  orgMasterSelectDisplayLabel,
} from '../utils/orgMasterOptions';
import IconButton from '@mui/material/IconButton';
import Divider from '@mui/material/Divider';
import { useRef } from 'react';
import WebcamCapture from './employeeEnrollment/WebcamCapture';
import PageHeader from './PageHeader';
import ClearAllFiltersButton from './ClearAllFiltersButton';
import PeopleExportDialog from './PeopleExportDialog';
import {
  pageFilterAutocompleteSx,
  pageFilterActionButtonSx,
  pageFilterFormControlSx,
  PAGE_FILTER_CONTROL_HEIGHT,
  pageFiltersActionsSx,
  pageFiltersControlsSx,
  pageFiltersStackSx,
  pageFiltersToolbarSx,
} from '../utils/pageFilterStyles';
import SimilarityThresholdDialog from './SimilarityThresholdDialog';
import PersonProfileSwitchDialog from './PersonProfileSwitchDialog';
import { formatDate, formatDateWithSeconds, formatDateWithSecondsWithoutFormat } from '../utils/dateUtils';
import {
  sanitizeNameSearchInput,
  filterPersonNameOptions,
  resolvePersonOptionBySearchText,
  formatPersonNameOptionLabel,
} from '../utils/nameSearchInput';
import { baseURL } from '../config/api';
import { isIntegerPersonId, buildPersonDetailsUrl } from '../utils/personDetailsNavigation';
import { useConfirmDialog } from '../contexts/ConfirmDialogContext';
import { useAuth } from '../contexts/AuthContext';
import { isKivoFeatureEnabled, shouldUseKivoConfigSource } from '../utils/kivoFeature';
import { useImagePreview } from './ImagePreviewDialog';
import PersonSummaryCard from './people/PersonSummaryCard';
import EmployeeFacesDialog from './people/EmployeeFacesDialog';
import { PersonSearch } from './PersonSearch';
import { PersonSummaryCardSkeleton } from './shimmer-ui/PersonSummaryCardSkeleton';


/** Dedupe company rows from GET /integrations/kivo/config (used for enrolled filters + sync dialog). */
const companiesFromKivoConfigData = (data) => {
  const configs = Array.isArray(data?.configs) ? data.configs : [];
  const seen = new Set();
  const companies = [];
  configs.forEach((cfg) => {
    const companyId = cfg.company_id;
    const companyName = cfg.company_name || 'Default Company';
    if (!companyId || seen.has(companyId)) return;
    seen.add(companyId);
    companies.push({ company_id: companyId, company_name: companyName });
  });
  return companies;
};

/** Build unique { name, employee_id } options for Company Records name autocomplete from HRMS rows. */
const extractHrmsNameOptionsFromRecords = (records) => {
  if (!Array.isArray(records) || records.length === 0) return [];
  const names = records
    .map((rec) => {
      const name = rec.name || rec.full_name || `${rec.first_name || ''} ${rec.last_name || ''}`.trim();
      const empId = rec.employee_id || rec.id || rec.user_id || '';
      return name ? { name, employee_id: String(empId || '') } : null;
    })
    .filter((item) => item && item.name);
  return Array.from(new Map(names.map((item) => [item.name, item])).values());
};

const primary = '#40518A';

const revokePreviewUrl = (url) => {
  if (url && String(url).startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};

const directionForFaceAngle = (angle) => {
  const value = String(angle || '').toLowerCase();
  if (value === 'left' || value === 'left_profile') return 'right';
  if (value === 'right' || value === 'right_profile') return 'left';
  if (value === 'custom' || value === 'custom_angle' || value === 'top') return 'top';
  return 'front';
};

const dataUrlToFile = (dataUrl, filename = 'capture.jpg') => {
  const [header, body] = String(dataUrl || '').split(',');
  const mime = header?.match(/:(.*?);/)?.[1] || 'image/jpeg';
  const binary = atob(body || '');
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new File([bytes], filename, { type: mime });
};

const blobToDataUrl = (blob) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onloadend = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

const Employees = () => {
  const { confirm } = useConfirmDialog();
  const { openImagePreview } = useImagePreview();
  const [faceImages, setFaceImages] = useState({});
  const { permissions } = useAuth();
  const kivoFeatureEnabled = isKivoFeatureEnabled(permissions);
  const orgMastersRequireBranch = !shouldUseKivoConfigSource(permissions);
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const resolveTabFromParams = (value) => {
    const normalized = (value || '').toLowerCase();
    if (normalized === 'company_records' && !kivoFeatureEnabled) return 'enrolled';
    if (normalized === 'enrolled' || normalized === 'company_records' || normalized === 'crm') return normalized;
    return 'enrolled';
  };
  const parseBoolParam = (value) => {
    const normalized = String(value || '').toLowerCase();
    return normalized === 'true' || normalized === '1';
  };
  const sanitizeQueryParam = (value) => sanitizeNameSearchInput(value || '');
  const sessionCompanyId = useMemo(() => {
    const sessionUser = sessionStorage.getItem('user');
    if (!sessionUser) return '';
    try {
      const parsed = JSON.parse(sessionUser);
      const companyId = parsed.company_id || parsed.companyId || '';
      return companyId ? String(companyId) : '';
    } catch (err) {
      console.error('Failed to parse session user for company info', err);
      return '';
    }
  }, []);
  const currentUserDisplayName = useMemo(() => {
    const sessionUser = sessionStorage.getItem('user');
    const storedUser = sessionUser || localStorage.getItem('user');
    if (!storedUser) return '';
    try {
      const parsed = JSON.parse(storedUser);
      return (
        parsed.full_name ||
        parsed.username ||
        parsed.email ||
        parsed.name ||
        ''
      );
    } catch (err) {
      console.error('Failed to parse stored user for display name', err);
      return '';
    }
  }, []);
  const [persons, setPersons] = useState(undefined);
  const [enrolledLoadError, setEnrolledLoadError] = useState('');
  const [personThumbnails, setPersonThumbnails] = useState({});
  const personThumbnailsRef = useRef({});
  const thumbnailRequestsRef = useRef(new Set());
  const [pagination, setPagination] = useState({
    total: 0,
    limit: 10,
    offset: 0,
    has_next: false,
    has_prev: false,
    total_pages: 0,
    current_page: 1,
  });
  const [loading, setLoading] = useState(false);
  const [directoryExportOpen, setDirectoryExportOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(parseBoolParam(searchParams.get('enrolled_deleted')));
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null });
  const [faceDetailDialog, setFaceDetailDialog] = useState({ open: false, face: null });
  const [imageDialog, setImageDialog] = useState({ open: false, imageUrl: null, faceName: '' });
  const [editDialog, setEditDialog] = useState({ open: false, face: null });
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [editCameraOn, setEditCameraOn] = useState(false);
  const [editCaptureNonce, setEditCaptureNonce] = useState(0);
  const [editQualityResult, setEditQualityResult] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [isCameraOn, setIsCameraOn] = useState(false);
  const [tab, setTab] = useState(resolveTabFromParams(searchParams.get('tab')));
  const [crmRecords, setCrmRecords] = useState([]);
  const [crmLoading, setCrmLoading] = useState(false);
  const [crmPage, setCrmPage] = useState(1); // server page
  const [crmMeta, setCrmMeta] = useState({ count: 0, current_page: 1 }); // server pagination meta
  const [crmQuery, setCrmQuery] = useState(sanitizeQueryParam(searchParams.get('crm_query'))); // name search query
  const [enrolledByPid, setEnrolledByPid] = useState({}); // { [personId]: { exists: boolean, deleted: boolean } }
  // HRMS/company records state
  const [hrmsRecords, setHrmsRecords] = useState([]);
  const [hrmsLoading, setHrmsLoading] = useState(false);
  const [hrmsSyncing, setHrmsSyncing] = useState(false);
  const [hrmsPage, setHrmsPage] = useState(1);
  const [hrmsLimit, setHrmsLimit] = useState(10);
  const [hrmsMeta, setHrmsMeta] = useState({ count: 0, current_page: 1 });
  const [hrmsQuery, setHrmsQuery] = useState(sanitizeQueryParam(searchParams.get('hrms_query')));
  const [hrmsVoiceMenu, setHrmsVoiceMenu] = useState({ anchorEl: null, employeeId: null });
  const closeHrmsVoiceMenu = () => setHrmsVoiceMenu({ anchorEl: null, employeeId: null });
  const [hrmsDetailDialog, setHrmsDetailDialog] = useState({ open: false, record: null });
  const [hrmsImageDialog, setHrmsImageDialog] = useState({ open: false, images: null, name: '' });
  const [hrmsImageLoading, setHrmsImageLoading] = useState(false);
  const [hrmsManageMenu, setHrmsManageMenu] = useState({ anchorEl: null, employeeId: null });
  /** When set, list is filtered to this employee (from PersonSearch suggestions). */
  const [enrolledSearchPersonId, setEnrolledSearchPersonId] = useState(
    (searchParams.get('person_id') || '').trim() || null
  );
  const closeHrmsManageMenu = () => setHrmsManageMenu({ anchorEl: null, employeeId: null });
  const [nameEditDialog, setNameEditDialog] = useState({
    open: false,
    person: null,
    name: '',
    personId: '',
    employeeCode: '',
    companyId: '',
    branchId: '',
    designation: '',
    department: '',
    saving: false
  });
  const [hrmsNameEditDialog, setHrmsNameEditDialog] = useState({ open: false, record: null, name: '', saving: false });
  const [customTrainingImages, setCustomTrainingImages] = useState([]);
  const customImageInputRef = useRef(null);
  // Removed unused syncLoading state
  const [hrmsNotConfigured, setHrmsNotConfigured] = useState(false);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(searchParams.get('company_id') || '');
  const [enrolledQuery, setEnrolledQuery] = useState(sanitizeQueryParam(searchParams.get('enrolled_query')));
  const enrolledSearchDebounceRef = useRef(null);
  const hrmsSearchDebounceRef = useRef(null);
  const [playingVoice, setPlayingVoice] = useState(null); // Track currently playing voice: { employeeId, voiceType } or null

  // Enrolled tab filters
  const [enrolledCompanyFilter, setEnrolledCompanyFilter] = useState(searchParams.get('enrolled_company') || '');
  const [enrolledBranchFilter, setEnrolledBranchFilter] = useState(searchParams.get('enrolled_branch') || '');
  const [enrolledDesignationFilter, setEnrolledDesignationFilter] = useState(searchParams.get('enrolled_designation') || '');
  const [enrolledDepartmentFilter, setEnrolledDepartmentFilter] = useState(searchParams.get('enrolled_department') || '');
  const [enrolledDefaultsReady, setEnrolledDefaultsReady] = useState(false);
  const enrolledCompanyTouchedRef = useRef(false);
  const didInitialPersonsLoadRef = useRef(false);
  const prevShowDeletedRef = useRef(showDeleted);
  const [enrolledCompanies, setEnrolledCompanies] = useState([]);
  const [enrolledBranches, setEnrolledBranches] = useState([]);
  const [enrolledDesignations, setEnrolledDesignations] = useState([]);
  const [enrolledDepartments, setEnrolledDepartments] = useState([]);

  // Company Records tab filters
  const [hrmsBranchFilter, setHrmsBranchFilter] = useState(searchParams.get('hrms_branch') || '');
  const [hrmsDesignationFilter, setHrmsDesignationFilter] = useState(searchParams.get('hrms_designation') || '');
  const [hrmsDepartmentFilter, setHrmsDepartmentFilter] = useState(searchParams.get('hrms_department') || '');
  const [hrmsFilterBranches, setHrmsFilterBranches] = useState([]);
  const [hrmsDesignations, setHrmsDesignations] = useState([]);
  const [hrmsDepartments, setHrmsDepartments] = useState([]);
  const [hrmsEmployeeNames, setHrmsEmployeeNames] = useState([]); // For autocomplete in company records

  // Edit dialog companies and branches
  const [editDialogCompanies, setEditDialogCompanies] = useState([]);
  const [editDialogBranches, setEditDialogBranches] = useState([]);
  const [editDialogDesignations, setEditDialogDesignations] = useState([]);
  const [editDialogDepartments, setEditDialogDepartments] = useState([]);
  const [activityCameras, setActivityCameras] = useState([]);
  const [restrictedCameraDialog, setRestrictedCameraDialog] = useState({
    open: false,
    person: null,
    selectedIds: [],
    saving: false,
  });
  const [similarityThresholdDialog, setSimilarityThresholdDialog] = useState({
    open: false,
    person: null,
  });

  // Voice Upload Modal state
  const [voiceModalOpen, setVoiceModalOpen] = useState(false);
  const [voiceModalEmployee, setVoiceModalEmployee] = useState(null); // { employeeId, employeeName, personId }
  const [checkinVoiceBlob, setCheckinVoiceBlob] = useState(null);
  const [checkoutVoiceBlob, setCheckoutVoiceBlob] = useState(null);
  const [isRecordingCheckin, setIsRecordingCheckin] = useState(false);
  const [isRecordingCheckout, setIsRecordingCheckout] = useState(false);
  const [voiceUploading, setVoiceUploading] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  // Audio URLs for player
  const [checkinAudioUrl, setCheckinAudioUrl] = useState(null);
  const [checkoutAudioUrl, setCheckoutAudioUrl] = useState(null);
  const [aiVoiceDialogOpen, setAiVoiceDialogOpen] = useState(false);
  const [aiVoiceType, setAiVoiceType] = useState('checkin');
  const [aiVoiceApiKeyInput, setAiVoiceApiKeyInput] = useState('');
  const [aiVoiceSavedKeys, setAiVoiceSavedKeys] = useState([]);
  const [aiVoiceSelectedKeyId, setAiVoiceSelectedKeyId] = useState('');
  const [aiVoiceText, setAiVoiceText] = useState('');
  const [aiVoiceLoadingKeys, setAiVoiceLoadingKeys] = useState(false);
  const [aiVoiceGenerating, setAiVoiceGenerating] = useState(false);

  // Sync Company Records Dialog state
  const [syncDialogOpen, setSyncDialogOpen] = useState(false); // For date picker dialog

  // Find Person Modal state
  const [findPersonModalOpen, setFindPersonModalOpen] = useState(false);
  const [findPersonModalPerson, setFindPersonModalPerson] = useState(null);
  const [selectedAngle, setSelectedAngle] = useState('');
  const [similarityThreshold, setSimilarityThreshold] = useState('0.3');
  /** 'audit' = In/Out (audit) logs; 'activity' = activity monitoring logs */
  const [findPersonLogType, setFindPersonLogType] = useState('audit');
  const [findPersonLoading, setFindPersonLoading] = useState(false);
  /** Object URLs for Find Person modal face thumbnails (revoked on close / reload). */
  const [findPersonFaceThumbUrls, setFindPersonFaceThumbUrls] = useState({});
  const findPersonFaceThumbUrlsRef = useRef({});
  const [profileSwitchDialog, setProfileSwitchDialog] = useState({ open: false, person: null });
  const [syncDateRange, setSyncDateRange] = useState([
    {
      startDate: new Date(),
      endDate: new Date(),
      key: 'selection'
    }
  ]);
  const [syncFromDate, setSyncFromDate] = useState('');
  const [syncToDate, setSyncToDate] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState({ from: false, to: false });
  const [tempDate, setTempDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Sync dialog selectors state
  const [syncSelectedCompany, setSyncSelectedCompany] = useState(null);
  const [syncSelectedBranch, setSyncSelectedBranch] = useState(null);
  const [syncSelectedApiKey, setSyncSelectedApiKey] = useState(null);
  const [syncCompanies, setSyncCompanies] = useState([]);
  const [syncBranches, setSyncBranches] = useState([]);
  const [syncApiKeys, setSyncApiKeys] = useState([]);
  const [syncApiKeysLoading, setSyncApiKeysLoading] = useState(false);
  const [useDateRange, setUseDateRange] = useState(false);
  const [syncBranchesRefreshing, setSyncBranchesRefreshing] = useState(false);
  /** Branch Select: value can be '' while renderValue shows text — keep label shrunk + outline notched. */
  const syncBranchFieldLabelNotched = Boolean(
    syncSelectedBranch ||
    syncBranchesRefreshing ||
    (syncSelectedCompany && syncBranches.length === 0),
  );
  const [syncBranchMenuWidth, setSyncBranchMenuWidth] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  // Helpers for datetime-local values (local time, no timezone)
  const pad2 = (n) => String(n).padStart(2, '0');
  const toLocalDateTimeString = (date) => {
    const d = new Date(date);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:${pad2(d.getSeconds())}`;
  };
  // Default filter for current month: from local 00:00:00 to current local time
  const firstOfMonth = (() => {
    const d = new Date();
    const first = new Date(d.getFullYear(), d.getMonth(), 1, 0, 0, 0);
    return toLocalDateTimeString(first);
  })();
  const nowLocal = toLocalDateTimeString(new Date());
  const [crmSince, setCrmSince] = useState(firstOfMonth);
  const [crmUntil, setCrmUntil] = useState(nowLocal);
  const [hrmsSince, setHrmsSince] = useState(firstOfMonth);
  const [hrmsUntil, setHrmsUntil] = useState(nowLocal);
  // Filter date range picker state (for local filtering)
  const [filterDateRange, setFilterDateRange] = useState([{
    startDate: startOfMonth(new Date()),
    endDate: new Date(),
    key: 'selection'
  }]);
  const [filterDateAnchor, setFilterDateAnchor] = useState(null);
  const [enrollDialog, setEnrollDialog] = useState({ open: false, record: null, name: '', personId: '' });
  const [addAngleDialog, setAddAngleDialog] = useState({ open: false, name: '', personId: '' });
  // Multi-angle enrollment state
  const [enrollFrontFile, setEnrollFrontFile] = useState(null);
  const [enrollLeftFile, setEnrollLeftFile] = useState(null);
  const [enrollRightFile, setEnrollRightFile] = useState(null);
  const [enrollCustomFile, setEnrollCustomFile] = useState(null);
  const [enrollFrontPreview, setEnrollFrontPreview] = useState(null);
  const [enrollLeftPreview, setEnrollLeftPreview] = useState(null);
  const [enrollRightPreview, setEnrollRightPreview] = useState(null);
  const [enrollCustomPreview, setEnrollCustomPreview] = useState(null);
  const [enrollCustomLabel, setEnrollCustomLabel] = useState('');
  const [enrollActiveAngle, setEnrollActiveAngle] = useState('front'); // 'front' | 'left' | 'right' | 'custom'

  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncInfo, setSyncInfo] = useState(null);
  const hrmsSyncPollRef = useRef(null);
  const loadFacesRequestIdRef = useRef(0);

  useEffect(() => {
    const loadActivityCameras = async () => {
      try {
        const res = await getCameraStreams();
        const items = Array.isArray(res?.items) ? res.items : [];
        const normalized = items
          .filter((cam) => String(cam?.camera_type || '').trim().toUpperCase() === 'ACTIVITY')
          .map((cam) => ({
            id: String(cam?.id || '').trim(),
            label: String(cam?.label || cam?.id || '').trim(),
            description: String(
              cam?.description ||
              cam?.camera_description ||
              cam?.camera_desc ||
              ''
            ).trim(),
          }))
          .filter((cam) => cam.id && cam.label);
        setActivityCameras(normalized);
      } catch (error) {
        console.error('Error loading activity cameras:', error);
        setActivityCameras([]);
      }
    };
    loadActivityCameras();
  }, []);
  // Per-person expand state for grouped rows
  const [facesModalPerson, setFacesModalPerson] = useState(null);
  const [enrolledManageMenu, setEnrolledManageMenu] = useState({ anchorEl: null, person: null });
  const closeEnrolledManageMenu = () => setEnrolledManageMenu({ anchorEl: null, person: null });
  const [enrolledVoiceMenu, setEnrolledVoiceMenu] = useState({ anchorEl: null, person: null });
  const closeEnrolledVoiceMenu = () => setEnrolledVoiceMenu({ anchorEl: null, person: null });
  const [validityDialog, setValidityDialog] = useState({
    open: false,
    person: null,
    validFrom: '',
    validTill: '',
    unlimited: true,
    saving: false,
  });
  /** Nested picker reuses `DateRangePickerContent` (same as sync-style range UI + times). */
  const [validityPickerOpen, setValidityPickerOpen] = useState(false);
  const [validityDateRange, setValidityDateRange] = useState([
    { startDate: new Date(), endDate: new Date(), key: 'selection' },
  ]);
  const [validityStartTime, setValidityStartTime] = useState('00:00');
  const [validityEndTime, setValidityEndTime] = useState('23:59');

  const toDateInputValue = (raw) => {
    if (!raw) return '';
    const dt = new Date(raw);
    if (Number.isNaN(dt.getTime())) return '';
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const d = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const combineLocalDateAndTime = (dateObj, timeHHmm) => {
    const d = new Date(dateObj);
    const parts = String(timeHHmm || '00:00').split(':');
    const h = parseInt(parts[0], 10) || 0;
    const m = parseInt(parts[1], 10) || 0;
    d.setHours(h, m, 0, 0);
    return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}T${pad2(d.getHours())}:${pad2(d.getMinutes())}:00`;
  };

  const openValidityDialog = (person) => {
    const vf = person?.valid_from;
    const vt = person?.valid_till;
    const fromExisting = toDateInputValue(vf);
    const tillExisting = toDateInputValue(vt);
    let startD = vf ? new Date(vf) : new Date();
    let endD = vt ? new Date(vt) : vf ? new Date(startD) : new Date();
    if (Number.isNaN(startD.getTime())) startD = new Date();
    if (Number.isNaN(endD.getTime())) endD = new Date(startD.getTime());
    const st = vf ? format(new Date(vf), 'HH:mm') : '00:00';
    const et = vt ? format(new Date(vt), 'HH:mm') : '23:59';
    setValidityDateRange([{ startDate: startD, endDate: endD, key: 'selection' }]);
    setValidityStartTime(st);
    setValidityEndTime(et);
    setValidityPickerOpen(false);
    setValidityDialog({
      open: true,
      person,
      validFrom: vf ? combineLocalDateAndTime(startD, st) : '',
      validTill: vt ? combineLocalDateAndTime(endD, et) : '',
      unlimited: !fromExisting && !tillExisting,
      saving: false,
    });
  };

  const closeValidityDialog = () => {
    setValidityPickerOpen(false);
    setValidityDialog({ open: false, person: null, validFrom: '', validTill: '', unlimited: true, saving: false });
  };

  const applyValidityRangePicker = () => {
    const sel = validityDateRange[0];
    if (!sel?.startDate || !sel?.endDate) {
      showSnackbar('Please select a date range', 'warning');
      return;
    }
    const fromStr = combineLocalDateAndTime(sel.startDate, validityStartTime);
    const tillStr = combineLocalDateAndTime(sel.endDate, validityEndTime);
    if (fromStr > tillStr) {
      showSnackbar('Start date/time cannot be after end date/time', 'error');
      return;
    }
    setValidityDialog((prev) => ({
      ...prev,
      validFrom: fromStr,
      validTill: tillStr,
      unlimited: false,
    }));
    setValidityPickerOpen(false);
  };


  // Reusable Date Range Picker Content Component
  const DateRangePickerContent = ({
    dateRange,
    onDateRangeChange,
    /** When false (default), caps selection at today (sync-style). When true, allows future end dates (validity). */
    disableMaxDate = false,
    showTimeFields = false,
    startTime = '00:00',
    endTime = '23:59',
    onStartTimeChange,
    onEndTimeChange,
  }) => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        width: showTimeFields ? 'fit-content' : undefined,
        maxWidth: showTimeFields ? '100%' : undefined,
      }}
    >
      {/* Quick Select Presets */}
      <Box sx={{
        minWidth: 140,
        flexShrink: 0,
        borderRight: isMobile ? 'none' : '1px solid',
        borderBottom: isMobile ? '1px solid' : 'none',
        borderColor: 'divider',
        p: 2,
        display: 'flex',
        flexDirection: isMobile ? 'row' : 'column',
        flexWrap: isMobile ? 'wrap' : 'nowrap',
        gap: 0.5
      }}>
        <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1, width: isMobile ? '100%' : 'auto' }}>
          QUICK SELECT
        </Typography>
        {[
          { label: 'Today', getValue: () => ({ startDate: new Date(), endDate: new Date() }) },
          { label: 'Yesterday', getValue: () => ({ startDate: subDays(new Date(), 1), endDate: subDays(new Date(), 1) }) },
          { label: 'Last 7 Days', getValue: () => ({ startDate: subDays(new Date(), 6), endDate: new Date() }) },
          { label: 'Last 30 Days', getValue: () => ({ startDate: subDays(new Date(), 29), endDate: new Date() }) },
          { label: 'This Month', getValue: () => ({ startDate: startOfMonth(new Date()), endDate: new Date() }) },
          { label: 'Last Month', getValue: () => ({ startDate: startOfMonth(subMonths(new Date(), 1)), endDate: endOfMonth(subMonths(new Date(), 1)) }) },
        ].map((preset) => (
          <Button
            key={preset.label}
            size="small"
            variant="text"
            onClick={() => {
              const { startDate, endDate } = preset.getValue();
              onDateRangeChange([{ startDate, endDate, key: 'selection' }]);
            }}
            sx={{
              justifyContent: 'flex-start',
              textTransform: 'none',
              px: 1.5,
              py: 0.75,
              color: 'text.secondary',
              '&:hover': {
                backgroundColor: 'action.hover',
                color: 'primary.main',
              },
            }}
          >
            {preset.label}
          </Button>
        ))}
      </Box>

      {/* Date Range Picker */}
      <Box sx={{
        flex: showTimeFields ? '0 1 auto' : 1,
        minWidth: 0,
        p: 2,
        '& .rdrCalendarWrapper': {
          fontSize: '0.85rem',
        },
        '& .rdrMonth': {
          width: { xs: '280px', sm: '300px' },
        },
        '& .rdrDefinedRangesWrapper': {
          display: 'none',
        },
      }}>
        <Box sx={{ mb: 2, p: 1.5, backgroundColor: 'grey.100', borderRadius: 1 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Selected Range:</strong>{' '}
            {isSameDay(dateRange[0].startDate, dateRange[0].endDate)
              ? format(dateRange[0].startDate, 'MMM dd, yyyy')
              : `${format(dateRange[0].startDate, 'MMM dd, yyyy')} - ${format(dateRange[0].endDate, 'MMM dd, yyyy')}`}
          </Typography>
        </Box>
        <Box
          sx={{
            width: showTimeFields ? 'max-content' : undefined,
            maxWidth: showTimeFields ? '100%' : undefined,
          }}
        >
          <DateRangePicker
            ranges={dateRange}
            onChange={(ranges) => onDateRangeChange([ranges.selection])}
            moveRangeOnFirstSelection={false}
            months={isMobile ? 1 : 2}
            direction={isMobile ? 'vertical' : 'horizontal'}
            rangeColors={[primary]}
            staticRanges={[]}
            inputRanges={[]}
            {...(!disableMaxDate ? { maxDate: new Date() } : {})}
          />
          {showTimeFields && (
            <Box
              sx={{
                px: 0,
                pb: 2,
                pt: 1,
                display: 'flex',
                gap: 1,
                flexDirection: { xs: 'column', sm: 'row' },
                width: '100%',
                boxSizing: 'border-box',
              }}
            >
              <TextField
                label="Start Time"
                type="time"
                size="small"
                value={startTime}
                onChange={(e) => onStartTimeChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                fullWidth
              />
              <TextField
                label="End Time"
                type="time"
                size="small"
                value={endTime}
                onChange={(e) => onEndTimeChange?.(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ step: 60 }}
                fullWidth
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );

  // Format angle label: drop "_profile" suffix for left/right
  const formatAngleLabel = useCallback((angle, metadata = {}) => {
    if (metadata && metadata.custom_angle_label) return metadata.custom_angle_label;
    if (!angle) return '';
    const s = String(angle).toLowerCase();
    if (s === 'left_profile') return 'left';
    if (s === 'right_profile') return 'right';
    if (s === 'custom_angle') return 'custom';
    return s.replace('_', ' ');
  }, []);
  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSyncFetchBranches = useCallback(async () => {
    if (!syncSelectedCompany?.company_id) return;
    if (syncBranchesRefreshing) return;
    setSyncBranchesRefreshing(true);
    try {
      await refreshKivoCompanyBranches(syncSelectedCompany.company_id);
      const branchesData = await getKivoBranches(syncSelectedCompany.company_id);
      const branches = branchesData?.branches || [];
      setSyncBranches(branches);
      if (branches.length > 0) {
        showSnackbar('Branches fetched successfully', 'success');
        setSyncSelectedBranch(null);
        setSyncSelectedApiKey(null);
        setSyncApiKeys([]);
      } else {
        showSnackbar(
          'Kivo did not return any branches for this company. Check the company in Kivo.',
          'warning',
        );
      }
    } catch (err) {
      showSnackbar(err?.message || 'Failed to fetch branches', 'error');
    } finally {
      setSyncBranchesRefreshing(false);
    }
  }, [
    getKivoBranches,
    refreshKivoCompanyBranches,
    showSnackbar,
    syncBranchesRefreshing,
    syncSelectedCompany,
  ]);

  const clearEnrolledSearchDebounce = useCallback(() => {
    if (enrolledSearchDebounceRef.current) {
      clearTimeout(enrolledSearchDebounceRef.current);
      enrolledSearchDebounceRef.current = null;
    }
  }, []);

  const clearHrmsSearchDebounce = useCallback(() => {
    if (hrmsSearchDebounceRef.current) {
      clearTimeout(hrmsSearchDebounceRef.current);
      hrmsSearchDebounceRef.current = null;
    }
  }, []);

  useEffect(() => () => {
    clearEnrolledSearchDebounce();
    clearHrmsSearchDebounce();
  }, [clearEnrolledSearchDebounce, clearHrmsSearchDebounce]);

  const loadEditDialogOrgMasters = useCallback(async (companyId, branchId) => {
    const cid = String(companyId || '').trim();
    const bid = String(branchId || '').trim();
    const requireBranch = !shouldUseKivoConfigSource(permissions);
    if (!cid || (requireBranch && !bid)) {
      setEditDialogDesignations([]);
      setEditDialogDepartments([]);
      return;
    }
    try {
      const [designationRes, departmentRes] = await Promise.all([
        fetchOrgDesignations({
          permissions,
          companyId: cid,
          branchId: bid || null,
          includeInactive: false,
          limit: 200,
        }),
        fetchOrgDepartments({
          permissions,
          companyId: cid,
          branchId: bid || null,
          includeInactive: false,
          limit: 200,
        }),
      ]);
      setEditDialogDesignations(
        normalizeOrgMasterOptions(
          Array.isArray(designationRes?.designations) ? designationRes.designations : []
        )
      );
      setEditDialogDepartments(
        normalizeOrgMasterOptions(
          Array.isArray(departmentRes?.departments) ? departmentRes.departments : []
        )
      );
    } catch (error) {
      console.error('Error loading designation/department options:', error);
      setEditDialogDesignations([]);
      setEditDialogDepartments([]);
    }
  }, [permissions]);

  const openNameEditDialog = async (person) => {
    const editableDesignation = personOrgMasterFilterValue(person, 'designation');
    const editableDepartment = personOrgMasterFilterValue(person, 'department');
    setNameEditDialog({
      open: true,
      person,
      name: person?.name || '',
      personId: person?.person_id || '',
      employeeCode: person?.employee_code || '',
      companyId: person?.company_id || '',
      branchId: person?.branch_id || '',
      designation: typeof editableDesignation === 'string' ? editableDesignation : String(editableDesignation || ''),
      department: typeof editableDepartment === 'string' ? editableDepartment : String(editableDepartment || ''),
      saving: false,
    });

    // Load companies/branches/designation/department options for the dropdowns
    try {
      const companiesData = await fetchCompanies(100, 0, false);
      setEditDialogCompanies(companiesData?.companies || []);

      // If person has a company, load branches for that company
      if (person?.company_id) {
        const branchesData = await getKivoBranches(person.company_id);
        setEditDialogBranches(branchesData?.branches || []);
        await loadEditDialogOrgMasters(person.company_id, person?.branch_id);
      } else {
        setEditDialogBranches([]);
        setEditDialogDesignations([]);
        setEditDialogDepartments([]);
      }
    } catch (error) {
      console.error('Error loading companies/branches:', error);
      setEditDialogDesignations([]);
      setEditDialogDepartments([]);
    }
  };

  const closeNameEditDialog = () => {
    setNameEditDialog({
      open: false,
      person: null,
      name: '',
      personId: '',
      employeeCode: '',
      companyId: '',
      branchId: '',
      designation: '',
      department: '',
      saving: false
    });
    setCustomTrainingImages([]);
    setEditDialogCompanies([]);
    setEditDialogBranches([]);
    setEditDialogDesignations([]);
    setEditDialogDepartments([]);
  };

  const handleCustomImageSelect = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Create preview URL
    const previewUrl = URL.createObjectURL(file);

    // Add to the list of custom images with empty label
    setCustomTrainingImages(prev => [...prev, {
      file,
      previewUrl,
      id: Date.now(),
      label: '' // Initialize with empty label
    }]);

    // Reset input so same file can be selected again
    if (customImageInputRef.current) {
      customImageInputRef.current.value = '';
    }
  };

  const handleUpdateImageLabel = (imageId, newLabel) => {
    setCustomTrainingImages(prev =>
      prev.map(img => img.id === imageId ? { ...img, label: newLabel } : img)
    );
  };

  const handleRemoveCustomImage = (imageId) => {
    setCustomTrainingImages(prev => {
      const image = prev.find(img => img.id === imageId);
      if (image?.previewUrl) {
        URL.revokeObjectURL(image.previewUrl);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const handleEditDialogCompanyChange = async (companyId) => {
    setNameEditDialog((prev) => ({
      ...prev,
      companyId,
      branchId: '',
      designation: '',
      department: '',
    }));
    setEditDialogDesignations([]);
    setEditDialogDepartments([]);

    if (companyId) {
      try {
        const branchesData = await getKivoBranches(companyId);
        setEditDialogBranches(branchesData?.branches || []);
        if (!orgMastersRequireBranch) {
          await loadEditDialogOrgMasters(companyId, '');
        }
      } catch (error) {
        console.error('Error loading branches:', error);
        setEditDialogBranches([]);
      }
    } else {
      setEditDialogBranches([]);
    }
  };

  const handleEditDialogBranchChange = async (branchId) => {
    let companyId = '';
    setNameEditDialog((prev) => {
      companyId = String(prev.companyId || '').trim();
      return {
        ...prev,
        branchId,
        designation: '',
        department: '',
      };
    });
    await loadEditDialogOrgMasters(companyId, branchId);
  };

  const editDesignationOptions = useMemo(() => {
    const list = normalizeOrgMasterOptions(editDialogDesignations);
    const selected = String(nameEditDialog?.designation || '').trim();
    if (selected && !list.some((item) => item.id === selected)) {
      const placeholder = orgMasterPlaceholderOption(
        selected,
        list,
        nameEditDialog?.person,
        'designation',
      );
      return placeholder ? [placeholder, ...list] : list;
    }
    return list;
  }, [editDialogDesignations, nameEditDialog?.designation, nameEditDialog?.person]);

  const editDepartmentOptions = useMemo(() => {
    const list = normalizeOrgMasterOptions(editDialogDepartments);
    const selected = String(nameEditDialog?.department || '').trim();
    if (selected && !list.some((item) => item.id === selected)) {
      const placeholder = orgMasterPlaceholderOption(
        selected,
        list,
        nameEditDialog?.person,
        'department',
      );
      return placeholder ? [placeholder, ...list] : list;
    }
    return list;
  }, [editDialogDepartments, nameEditDialog?.department, nameEditDialog?.person]);

  const handleSavePersonName = async () => {
    const oldPersonId = nameEditDialog?.person?.person_id;
    const newName = String(nameEditDialog?.name || '').trim();
    const newEmployeeCode = String(nameEditDialog?.employeeCode || '').trim();
    const newCompanyId = String(nameEditDialog?.companyId || '').trim();
    const newBranchId = String(nameEditDialog?.branchId || '').trim();
    const newDesignation = String(nameEditDialog?.designation || '').trim();
    const newDepartment = String(nameEditDialog?.department || '').trim();
    const identityLocked = isIntegerPersonId(oldPersonId);
    const currentEmployeeCode = String(nameEditDialog?.person?.employee_code || '').trim();
    const currentDesignation = String(
      personOrgMasterFilterValue(nameEditDialog?.person, 'designation') || ''
    ).trim();
    const currentDepartment = String(
      personOrgMasterFilterValue(nameEditDialog?.person, 'department') || ''
    ).trim();

    if (!oldPersonId) {
      showSnackbar('Person ID is missing', 'error');
      return;
    }
    if (!newName) {
      showSnackbar('Name is required', 'error');
      return;
    }

    try {
      setNameEditDialog((prev) => ({ ...prev, saving: true }));

      // Update name
      await updatePersonName(oldPersonId, newName);

      // Update editable details (employee_code/company/branch — not person_id rename)
      const hasLocalOrgUpdates = !identityLocked && (newCompanyId || newBranchId);
      const hasEmployeeCodeChange = !identityLocked && newEmployeeCode !== currentEmployeeCode;
      const hasDesignationChange = newDesignation !== currentDesignation;
      const hasDepartmentChange = newDepartment !== currentDepartment;
      if (hasLocalOrgUpdates || hasEmployeeCodeChange || hasDesignationChange || hasDepartmentChange) {
        try {
          await updatePersonDetails(
            oldPersonId,
            oldPersonId,
            newCompanyId || null,
            newBranchId || null,
            hasDesignationChange ? (newDesignation || null) : undefined,
            hasDepartmentChange ? (newDepartment || null) : undefined,
            hasEmployeeCodeChange ? (newEmployeeCode || null) : undefined,
          );
        } catch (error) {
          console.error('Error updating person details:', error);
          showSnackbar('Name updated but failed to update person details', 'warning');
        }
      }

      const updatedAtIso = new Date().toISOString();
      const updatedByName = currentUserDisplayName || 'system';

      setPersons((prev) => prev.map((p) => {
        if (p.person_id !== oldPersonId) return p;
        const designationLabel = hasDesignationChange
          ? (
            lookupOrgMasterNameById(newDesignation, editDesignationOptions)
            || (isOrgMasterId(newDesignation) ? null : (newDesignation || null))
          )
          : p.designation;
        const departmentLabel = hasDepartmentChange
          ? (
            lookupOrgMasterNameById(newDepartment, editDepartmentOptions)
            || (isOrgMasterId(newDepartment) ? null : (newDepartment || null))
          )
          : p.department;
        return {
          ...p,
          name: newName,
          employee_code: hasEmployeeCodeChange ? (newEmployeeCode || null) : p.employee_code,
          company_id: !identityLocked ? (newCompanyId || p.company_id) : p.company_id,
          branch_id: !identityLocked ? (newBranchId || p.branch_id) : p.branch_id,
          designation: designationLabel,
          department: departmentLabel,
          designation_id: hasDesignationChange
            ? (isOrgMasterId(newDesignation) ? newDesignation : null)
            : p.designation_id,
          department_id: hasDepartmentChange
            ? (isOrgMasterId(newDepartment) ? newDepartment : null)
            : p.department_id,
          updated_at: updatedAtIso,
          updated_by: updatedByName,
          updated_by_name: updatedByName,
        };
      }));

      // Upload custom training images if any are selected
      if (customTrainingImages.length > 0) {
        let successCount = 0;
        let failCount = 0;

        for (const image of customTrainingImages) {
          try {
            // Convert file to base64
            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();
              reader.onload = () => resolve(reader.result);
              reader.onerror = reject;
              reader.readAsDataURL(image.file);
            });

            // Upload the image with label
            const res = await registerFaceBase64(
              newName,
              oldPersonId,
              dataUrl,
              'jpeg',
              'custom',
              image.label.trim() || undefined // Use label if provided
            );

            if (res && res.success) {
              successCount++;
            } else {
              failCount++;
            }
          } catch (error) {
            console.error('Upload error for image:', error);
            failCount++;
          }
        }

        // Clean up preview URLs
        customTrainingImages.forEach(img => {
          if (img.previewUrl) {
            URL.revokeObjectURL(img.previewUrl);
          }
        });

        // Show upload results
        if (successCount > 0) {
          showSnackbar(`Employee details updated and ${successCount} custom training image(s) added successfully`, 'success');
          loadFaces(pagination.limit, pagination.offset);
        } else if (failCount > 0) {
          showSnackbar(`Employee details updated, but failed to add ${failCount} image(s)`, 'warning');
        }
      } else {
        showSnackbar('Employee details updated successfully', 'success');
      }

      await loadFaces(pagination.limit, pagination.offset);
      closeNameEditDialog();
    } catch (error) {
      showSnackbar(error.message || 'Failed to update employee details', 'error');
      setNameEditDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleSaveValidity = async () => {
    const employeeId = validityDialog?.person?.person_id;
    if (!employeeId) {
      showSnackbar('Employee ID is missing', 'error');
      return;
    }
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const validFromPayload = validityDialog.unlimited ? null : (validityDialog.validFrom || null);
    const validTillPayload = validityDialog.unlimited ? null : (validityDialog.validTill || null);
    // If not unlimited, at least one boundary should be set
    if (!validityDialog.unlimited && !validFromPayload && !validTillPayload) {
      showSnackbar('Please select Valid From or Valid Till', 'error');
      return;
    }
    // Range check
    if (validFromPayload && validTillPayload && validFromPayload > validTillPayload) {
      showSnackbar('Valid From cannot be after Valid Till', 'error');
      return;
    }
    try {
      setValidityDialog((prev) => ({ ...prev, saving: true }));

      await updateHrmsEmployeeValidity(employeeId, validFromPayload, validTillPayload, tz);

      setPersons((prev) =>
        prev.map((p) =>
          String(p.person_id) === String(employeeId)
            ? { ...p, valid_from: validFromPayload, valid_till: validTillPayload }
            : p
        )
      );

      showSnackbar(
        validityDialog.unlimited ? 'Employee validity set to Unlimited' : 'Employee validity updated',
        'success'
      );
      closeValidityDialog();
    } catch (error) {
      showSnackbar(error.message || 'Failed to update validity', 'error');
      setValidityDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const openHrmsNameEditDialog = (record) => {
    const existingName = record?.name || record?.full_name || `${record?.first_name || ''} ${record?.last_name || ''}`.trim();
    setHrmsNameEditDialog({
      open: true,
      record,
      name: existingName || '',
      saving: false,
    });
  };

  const closeHrmsNameEditDialog = () => {
    setHrmsNameEditDialog({ open: false, record: null, name: '', saving: false });
  };

  const handleSaveHrmsEmployeeName = async () => {
    const employeeId = hrmsNameEditDialog?.record?.employee_id || hrmsNameEditDialog?.record?.id || hrmsNameEditDialog?.record?.user_id;
    const newName = String(hrmsNameEditDialog?.name || '').trim();

    if (!employeeId) {
      showSnackbar('Employee ID is missing', 'error');
      return;
    }
    if (!newName) {
      showSnackbar('Name is required', 'error');
      return;
    }

    try {
      setHrmsNameEditDialog((prev) => ({ ...prev, saving: true }));
      await updateHrmsEmployeeName(employeeId, newName);

      const [firstName, ...restName] = newName.split(/\s+/);
      const lastName = restName.join(' ') || '';
      setHrmsRecords((prev) => prev.map((rec) => {
        const recEmpId = rec?.employee_id || rec?.id || rec?.user_id;
        if (String(recEmpId) !== String(employeeId)) return rec;
        return {
          ...rec,
          name: newName,
          full_name: newName,
          first_name: firstName || '',
          last_name: lastName || '',
        };
      }));

      showSnackbar('Employee name updated successfully', 'success');
      closeHrmsNameEditDialog();
    } catch (error) {
      showSnackbar(error.message || 'Failed to update employee name', 'error');
      setHrmsNameEditDialog((prev) => ({ ...prev, saving: false }));
    }
  };

  const handleSavePersonNameWithConfirm = useCallback(async () => {
    const approved = await confirm({
      title: 'Confirm Edit Employee',
      message: 'Are you sure you want to save employee details?',
      confirmLabel: 'Save',
      danger: false,
    });
    if (!approved) return;
    await handleSavePersonName();
  }, [confirm, handleSavePersonName]);

  const handleSaveHrmsEmployeeNameWithConfirm = useCallback(async () => {
    const approved = await confirm({
      title: 'Confirm Edit Employee',
      message: 'Are you sure you want to save employee name?',
      confirmLabel: 'Save',
      danger: false,
    });
    if (!approved) return;
    await handleSaveHrmsEmployeeName();
  }, [confirm, handleSaveHrmsEmployeeName]);

  const handleSaveValidityWithConfirm = useCallback(async () => {
    const approved = await confirm({
      title: 'Confirm Validity Update',
      message: 'Are you sure you want to save employee validity tenure?',
      confirmLabel: 'Save',
      danger: false,
    });
    if (!approved) return;
    await handleSaveValidity();
  }, [confirm, handleSaveValidity]);

  const handleSaveRestrictedCameras = useCallback(async () => {
    const personId = String(restrictedCameraDialog?.person?.person_id || '').trim();
    if (!personId) return;
    try {
      setRestrictedCameraDialog((prev) => ({ ...prev, saving: true }));
      const ids = Array.isArray(restrictedCameraDialog.selectedIds) ? restrictedCameraDialog.selectedIds : [];
      await updatePersonCameraAccess(personId, ids);
      setPersons((prev) =>
        prev.map((p) =>
          String(p?.person_id || '') === personId
            ? { ...p, restricted_camera_ids: [...ids] }
            : p
        )
      );
      showSnackbar('Restricted cameras updated successfully', 'success');
      setRestrictedCameraDialog({ open: false, person: null, selectedIds: [], saving: false });
    } catch (error) {
      console.error('Failed to update restricted cameras:', error);
      showSnackbar('Failed to update restricted cameras', 'error');
      setRestrictedCameraDialog((prev) => ({ ...prev, saving: false }));
    }
  }, [restrictedCameraDialog, showSnackbar]);

  const handleSimilarityThresholdSaved = useCallback((personId, threshold) => {
    setPersons((prev) =>
      prev.map((p) =>
        String(p?.person_id || '') === String(personId)
          ? { ...p, similarity_threshold: threshold }
          : p
      )
    );
  }, []);

  const handleSaveRestrictedCamerasWithConfirm = useCallback(async () => {
    const approved = await confirm({
      title: 'Confirm Restricted Cameras',
      message: 'Are you sure you want to save restricted camera access?',
      confirmLabel: 'Save',
      danger: false,
    });
    if (!approved) return;
    await handleSaveRestrictedCameras();
  }, [confirm, handleSaveRestrictedCameras]);

  const getStatusLabel = useCallback((person) => {
    if (!person || !Array.isArray(person.faces)) {
      return 'Active';
    }
    const allDeleted = person.faces.every((face) => !!face.deleted_at);
    const anyDeleted = person.faces.some((face) => !!face.deleted_at);
    if (allDeleted) return 'Deleted';
    if (anyDeleted) return 'Partial';
    return 'Active';
  }, []);

  const resolveUpdatedByName = useCallback((person) => {
    const rawValue =
      person?.updated_by_name ??
      person?.updated_by ??
      person?.last_updated_by ??
      person?.updatedBy ??
      person?.updated_by_user?.name ??
      person?.updated_by_user?.full_name ??
      person?.updated_by_user?.username;

    if (!rawValue) return '-';
    if (typeof rawValue === 'string') return rawValue.trim() || '-';
    if (typeof rawValue === 'object') {
      const nestedValue = rawValue?.name ?? rawValue?.full_name ?? rawValue?.username;
      if (!nestedValue) return '-';
      return String(nestedValue).trim() || '-';
    }
    return String(rawValue);
  }, []);

  const resolveUpdatedAt = useCallback((person) => {
    const rawValue =
      person?.updated_at ??
      person?.last_updated ??
      person?.updatedAt ??
      person?.updated_on ??
      person?.modified_at ??
      person?.modified_on;
    const formatted = formatDate(rawValue);
    return formatted && formatted !== '-' ? formatted : '-';
  }, []);

  const resolveDesignation = useCallback((person) => {
    const fromMasters = resolvePersonOrgMasterDisplay(
      person,
      'designation',
      enrolledDesignations,
      editDialogDesignations,
    );
    if (fromMasters) return fromMasters;

    const rawValue =
      person?.designation_name ??
      person?.job_title ??
      person?.title ??
      person?.position;

    if (!rawValue) return '-';
    if (typeof rawValue === 'string') {
      const trimmed = rawValue.trim();
      if (!trimmed || isOrgMasterId(trimmed)) return '-';
      return trimmed;
    }
    if (typeof rawValue === 'object') {
      const nestedValue =
        rawValue?.name ??
        rawValue?.title ??
        rawValue?.designation ??
        rawValue?.position;
      if (!nestedValue) return '-';
      return String(nestedValue).trim() || '-';
    }
    return String(rawValue);
  }, [enrolledDesignations, editDialogDesignations]);

  const resolveDepartment = useCallback((person) => {
    const fromMasters = resolvePersonOrgMasterDisplay(
      person,
      'department',
      enrolledDepartments,
      editDialogDepartments,
    );
    if (fromMasters) return fromMasters;

    const rawValue =
      person?.department_name ??
      person?.dept ??
      person?.dept_name ??
      person?.organization_unit ??
      person?.org_unit;

    if (!rawValue) return '-';
    if (typeof rawValue === 'string') {
      const trimmed = rawValue.trim();
      if (!trimmed || isOrgMasterId(trimmed)) return '-';
      return trimmed;
    }
    if (typeof rawValue === 'object') {
      const nestedValue =
        rawValue?.name ??
        rawValue?.title ??
        rawValue?.department ??
        rawValue?.dept;
      if (!nestedValue) return '-';
      return String(nestedValue).trim() || '-';
    }
    return String(rawValue);
  }, [enrolledDepartments, editDialogDepartments]);

  const selectedCompanyName = useMemo(() => {
    if (!enrolledCompanyFilter) return 'All Companies';
    const match = enrolledCompanies.find(
      (company) => String(company.company_id) === String(enrolledCompanyFilter)
    );
    return match?.company_name || 'Selected Company';
  }, [enrolledCompanies, enrolledCompanyFilter]);

  const selectedBranchName = useMemo(() => {
    if (!enrolledBranchFilter) return 'All Branches';
    const match = enrolledBranches.find(
      (branch) => String(branch.branch_id) === String(enrolledBranchFilter)
    );
    return match?.branch_address || 'Selected Branch';
  }, [enrolledBranches, enrolledBranchFilter]);

  const selectedHrmsCompanyName = useMemo(() => {
    if (!selectedCompanyId) return 'All Companies';
    const match = companyOptions.find(
      (company) => String(company.company_id || '') === String(selectedCompanyId)
    );
    return match?.company_name || 'Selected Company';
  }, [companyOptions, selectedCompanyId]);

  const selectedHrmsBranchName = useMemo(() => {
    if (!hrmsBranchFilter) return 'All Branches';
    const match = hrmsFilterBranches.find(
      (branch) => String(branch.branch_id) === String(hrmsBranchFilter)
    );
    return match?.branch_address || match?.branch_name || 'Selected Branch';
  }, [hrmsFilterBranches, hrmsBranchFilter]);

  const renderSelectValueWithTooltip = useCallback((label, { disableTooltip = false } = {}) => {
    const safeLabel = String(label || '');
    const shouldDisable =
      disableTooltip ||
      !safeLabel;
    return (
      <Tooltip
        title={safeLabel}
        arrow
        placement="bottom"
        PopperProps={{ disablePortal: true }}
        disableHoverListener={shouldDisable}
      >
        <Box
          component="span"
          sx={{
            display: 'inline-block',
            maxWidth: '100%',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            verticalAlign: 'bottom',
          }}
        >
          {safeLabel}
        </Box>
      </Tooltip>
    );
  }, []);

  const renderMenuItemLabel = useCallback((label) => {
    const safeLabel = String(label || '');
    return (
      <Tooltip
        title={safeLabel}
        arrow
        placement="right"
        disableHoverListener={!safeLabel}
        slotProps={{
          tooltip: {
            sx: {
              maxWidth: 520,
              whiteSpace: 'normal',
              wordBreak: 'break-word',
            },
          },
          popper: {
            sx: { zIndex: (theme) => theme.zIndex.modal + 2 },
          },
        }}
      >
        <Box
          component="span"
          sx={{
            display: 'block',
            width: '100%',
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {safeLabel}
        </Box>
      </Tooltip>
    );
  }, []);

  const formatEnrolledPersonNameOption = useCallback((option) => {
    return formatPersonNameOptionLabel(option, { includeCode: true });
  }, []);

  const filterEnrolledPersonNameOptions = useCallback((options, state) => {
    return filterPersonNameOptions(options, state);
  }, []);


  const mainTableColumns = useMemo(() => {
    const safePersons = Array.isArray(persons) ? persons : [];

    const angleLabelForPerson = (person) => {
      if (!person || !Array.isArray(person.faces) || person.faces.length === 0) {
        return '-';
      }
      return person.faces
        .map((face) => formatAngleLabel(face.face_angle, face.metadata))
        .filter(Boolean)
        .join(', ');
    };

    const columnDefinitions = [
      {
        id: 'name',
        label: 'Name',
        accessor: (p) => p?.name || '-',
      },
      // {
      //   id: 'personId',
      //   label: 'Person ID',
      //   accessor: (p) => p?.person_id || '-',
      // },
      {
        id: 'employeeCode',
        label: 'Employee Code',
        accessor: (p) => p?.employee_code || '-',
      },
      {
        id: 'designation',
        label: 'Designation',
        accessor: resolveDesignation,
      },
      {
        id: 'department',
        label: 'Department',
        accessor: resolveDepartment,
      },
      {
        id: 'updatedBy',
        label: 'Updated By',
        accessor: (p) => {
          const name = resolveUpdatedByName(p);
          const time = resolveUpdatedAt(p);
          if (name === '-' && time === '-') return '-';
          return `${name} ${time}`.trim();
        },
      },
      {
        id: 'angles',
        label: 'Angles',
        accessor: angleLabelForPerson,
      },
      {
        id: 'voice',
        label: 'Voice',
        accessor: (p) => {
          const hasCheckin = p?.checkin_voice_path && p.checkin_voice_path !== '';
          const hasCheckout = p?.checkout_voice_path && p.checkout_voice_path !== '';
          if (hasCheckin && hasCheckout) return 'Check-in, Check-out';
          if (hasCheckin) return 'Check-in';
          if (hasCheckout) return 'Check-out';
          return 'No voice';
        },
      },
      // {
      //   id: 'status',
      //   label: 'Status',
      //   accessor: getStatusLabel,
      // },
      {
        id: 'details',
        label: 'Action',
        accessor: () => 'View Details',
      },
    ];

    const toLength = (value) => String(value ?? '').length;
    const maxCharsByColumn = {
      name: 18,
      personId: 12,
      employeeCode: 16,
      designation: 20,
      department: 20,
      updatedBy: 20,
      angles: 12,
      voice: 13,
      status: 12,
      details: 16,
    };
    return columnDefinitions.map((column) => {
      const maxDataLength = safePersons.reduce(
        (max, person) => Math.max(max, toLength(column.accessor(person))),
        0
      );
      const maxChars = maxCharsByColumn[column.id] || 20;
      const widthInChars = Math.min(Math.max(column.label.length, maxDataLength, 8) + 4, maxChars);
      return {
        ...column,
        width: `${widthInChars}ch`,
      };
    });
  }, [persons, formatAngleLabel, getStatusLabel, resolveDesignation, resolveDepartment, resolveUpdatedByName, resolveUpdatedAt]);

  const columnWidthMap = useMemo(
    () =>
      mainTableColumns.reduce((acc, column) => {
        acc[column.id] = column.width;
        return acc;
      }, {}),
    [mainTableColumns]
  );

  const getTableCellWidthProps = (columnId, variant = 'body', width = null) => {
    const widthValue = width ? width : columnWidthMap[columnId] || 'auto';
    return {
      sx: {
        width: widthValue,
        minWidth: widthValue,
        maxWidth: isMobile ? 'unset' : widthValue,
        whiteSpace: variant === 'head' ? 'nowrap' : 'normal',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        ...(variant === 'body' ? { wordBreak: 'break-word' } : {}),
        ...(columnId === 'details' ? {
          paddingRight: '24px',
          overflow: 'visible',
        } : {}),
      },
    };
  };

  const renderEnrolledAngles = (person) => {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Tooltip title="View face angles">
          <Button
            size="small"
            variant="outlined"
            onClick={() => setFacesModalPerson(person)}
            sx={{
              textTransform: 'none',
              borderColor: primary,
              color: primary,
              '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
            }}
          >
            Faces
          </Button>
        </Tooltip>
      </Box>
    );
  };

  const renderEnrolledVoice = (person) => {
    const menuOpen = Boolean(
      enrolledVoiceMenu.anchorEl &&
      enrolledVoiceMenu.person &&
      enrolledVoiceMenu.person.person_id === person.person_id
    );
    const hasIn = !!(person.checkin_voice_path && person.checkin_voice_path !== '');
    const hasOut = !!(person.checkout_voice_path && person.checkout_voice_path !== '');
    const playDisabled = !!playingVoice || showDeleted;

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          endIcon={menuOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          onClick={(e) =>
            setEnrolledVoiceMenu((prev) =>
              prev.anchorEl && prev.person?.person_id === person.person_id
                ? { anchorEl: null, person: null }
                : { anchorEl: e.currentTarget, person }
            )
          }
          sx={{
            textTransform: 'none',
            borderColor: primary,
            color: primary,
            '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
          }}
        >
          Voice
        </Button>
        <Menu
          anchorEl={enrolledVoiceMenu.anchorEl}
          open={menuOpen}
          onClose={closeEnrolledVoiceMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 200 } }}
        >
          <MenuItem
            disabled={!hasIn || playDisabled}
            onClick={() => {
              if (!hasIn || playDisabled) return;
              closeEnrolledVoiceMenu();
              playVoice(person.person_id, 'checkin');
            }}
          >
            <ListItemIcon>
              <VolumeUp fontSize="small" sx={{ color: hasIn && !playDisabled ? 'primary.main' : undefined }} />
            </ListItemIcon>
            <ListItemText primary="Play IN" />
          </MenuItem>
          <MenuItem
            disabled={!hasOut || playDisabled}
            onClick={() => {
              if (!hasOut || playDisabled) return;
              closeEnrolledVoiceMenu();
              playVoice(person.person_id, 'checkout');
            }}
          >
            <ListItemIcon>
              <VolumeUp
                fontSize="small"
                sx={{ color: hasOut && !playDisabled ? 'secondary.main' : undefined }}
              />
            </ListItemIcon>
            <ListItemText primary="Play OUT" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledVoiceMenu();
              if (hasIn && hasOut) {
                openVoiceModal(person.person_id, person.name, person.person_id, 'update');
              } else {
                openVoiceModal(person.person_id, person.name, person.person_id);
              }
            }}
          >
            <ListItemIcon>
              <Sync fontSize="small" sx={{ color: showDeleted ? undefined : 'primary.main' }} />
            </ListItemIcon>
            <ListItemText primary="Update voice" />
          </MenuItem>
        </Menu>
      </Box>
    );
  };

  const renderEnrolledActions = (person) => {
    const menuOpen = Boolean(
      enrolledManageMenu.anchorEl &&
      enrolledManageMenu.person &&
      enrolledManageMenu.person.person_id === person.person_id
    );
    const logsDisabled =
      !person.faces || person.faces.length === 0 || person.faces.every((f) => f.deleted_at);

    const openRestrictedCameras = () => {
      const restrictedIds = Array.isArray(person?.restricted_camera_ids)
        ? person.restricted_camera_ids.map((id) => String(id || '').trim()).filter(Boolean)
        : [];
      setRestrictedCameraDialog({
        open: true,
        person,
        selectedIds: Array.from(new Set(restrictedIds)),
        saving: false,
      });
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Button
          size="small"
          variant="outlined"
          endIcon={menuOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          onClick={(e) =>
            setEnrolledManageMenu((prev) =>
              prev.anchorEl && prev.person?.person_id === person.person_id
                ? { anchorEl: null, person: null }
                : { anchorEl: e.currentTarget, person }
            )
          }
          sx={{
            textTransform: 'none',
            borderColor: primary,
            color: primary,
            '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
          }}
        >
          Manage
        </Button>
        <Menu
          anchorEl={enrolledManageMenu.anchorEl}
          open={menuOpen}
          onClose={closeEnrolledManageMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 220 } }}
        >
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              openNameEditDialog(person);
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit details" />
          </MenuItem>
          <MenuItem
            disabled={logsDisabled || showDeleted}
            onClick={() => {
              if (logsDisabled || showDeleted) return;
              closeEnrolledManageMenu();
              handleFindPerson(person);
            }}
          >
            <ListItemIcon>
              <Search fontSize="small" sx={{ color: logsDisabled || showDeleted ? undefined : primary }} />
            </ListItemIcon>
            <ListItemText primary="Find person in logs" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              setProfileSwitchDialog({ open: true, person });
            }}
          >
            <ListItemIcon>
              <SwapHoriz fontSize="small" sx={{ color: showDeleted ? undefined : primary }} />
            </ListItemIcon>
            <ListItemText primary="Switch profile" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              openValidityDialog(person);
            }}
          >
            <ListItemIcon>
              <CalendarMonth fontSize="small" sx={{ color: showDeleted ? undefined : '#5F7EBC' }} />
            </ListItemIcon>
            <ListItemText primary="Add validity tenure" />
          </MenuItem>
          <MenuItem
            disabled={showDeleted}
            onClick={() => {
              if (showDeleted) return;
              closeEnrolledManageMenu();
              openRestrictedCameras();
            }}
          >
            <ListItemIcon>
              <Videocam fontSize="small" sx={{ color: showDeleted ? undefined : '#5F7EBC' }} />
            </ListItemIcon>
            <ListItemText primary="Restricted cameras" />
          </MenuItem>
          <MenuItem
            onClick={() => {
              closeEnrolledManageMenu();
              setSimilarityThresholdDialog({ open: true, person });
            }}
          >
            <ListItemIcon>
              <Tune fontSize="small" sx={{ color: '#5F7EBC' }} />
            </ListItemIcon>
            <ListItemText primary="Set similarity threshold" />
          </MenuItem>
          <Divider />
          {!showDeleted ? (
            <MenuItem
              onClick={() => {
                closeEnrolledManageMenu();
                handleDeleteAllFaces(person);
              }}
              sx={{ color: 'error.main' }}
            >
              <ListItemIcon>
                <DeleteOutline fontSize="small" sx={{ color: 'error.main' }} />
              </ListItemIcon>
              <ListItemText primary="Deactivate all faces" primaryTypographyProps={{ sx: { color: 'error.main' } }} />
            </MenuItem>
          ) : (
            <MenuItem
              onClick={() => {
                closeEnrolledManageMenu();
                handleRestoreAllFaces(person);
              }}
              sx={{ color: 'success.main' }}
            >
              <ListItemIcon>
                <Restore fontSize="small" sx={{ color: 'success.main' }} />
              </ListItemIcon>
              <ListItemText primary="Activate all faces" primaryTypographyProps={{ sx: { color: 'success.main' } }} />
            </MenuItem>
          )}
        </Menu>
      </Box>
    );
  };

  const displayOrDash = (value) => {
    if (value == null) return '—';
    const text = String(value).trim();
    if (!text || text === '-') return '—';
    return text;
  };

  const resolveEnrolledStatus = (person) => {
    if (showDeleted) return { label: 'Deactive', tone: 'deactive' };
    const label = getStatusLabel(person);
    if (label === 'Deleted') return { label: 'Deactive', tone: 'deactive' };
    if (label === 'Partial') return { label: 'Partial', tone: 'partial' };
    return { label: 'Active', tone: 'active' };
  };

  const renderHrmsVoice = (hasCheckinVoice, hasCheckoutVoice, empId, name) => (
    (() => {
      const employeeId = String(empId || '');
      const menuOpen = Boolean(
        hrmsVoiceMenu.anchorEl
        && hrmsVoiceMenu.employeeId === employeeId
      );
      const playDisabled = !!playingVoice;

      return (
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            size="small"
            variant="outlined"
            endIcon={menuOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
            aria-haspopup="menu"
            aria-expanded={menuOpen ? 'true' : undefined}
            onClick={(event) => setHrmsVoiceMenu((prev) =>
              prev.anchorEl && prev.employeeId === employeeId
                ? { anchorEl: null, employeeId: null }
                : { anchorEl: event.currentTarget, employeeId }
            )}
            sx={{
              textTransform: 'none',
              borderColor: primary,
              color: primary,
              '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
            }}
          >
            Voice
          </Button>
          <Menu
            anchorEl={hrmsVoiceMenu.anchorEl}
            open={menuOpen}
            onClose={closeHrmsVoiceMenu}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ sx: { minWidth: 200 } }}
          >
            <MenuItem
              disabled={!hasCheckinVoice || playDisabled}
              onClick={() => {
                if (!hasCheckinVoice || playDisabled) return;
                closeHrmsVoiceMenu();
                playVoice(empId, 'checkin');
              }}
            >
              <ListItemIcon>
                <VolumeUp fontSize="small" sx={{ color: hasCheckinVoice && !playDisabled ? 'primary.main' : undefined }} />
              </ListItemIcon>
              <ListItemText primary="Play IN" />
            </MenuItem>
            <MenuItem
              disabled={!hasCheckoutVoice || playDisabled}
              onClick={() => {
                if (!hasCheckoutVoice || playDisabled) return;
                closeHrmsVoiceMenu();
                playVoice(empId, 'checkout');
              }}
            >
              <ListItemIcon>
                <VolumeUp fontSize="small" sx={{ color: hasCheckoutVoice && !playDisabled ? 'secondary.main' : undefined }} />
              </ListItemIcon>
              <ListItemText primary="Play OUT" />
            </MenuItem>
            <MenuItem
              onClick={() => {
                closeHrmsVoiceMenu();
                openVoiceModal(
                  empId,
                  name,
                  empId,
                  hasCheckinVoice && hasCheckoutVoice ? 'update' : 'add',
                );
              }}
            >
              <ListItemIcon>
                <Sync fontSize="small" sx={{ color: 'primary.main' }} />
              </ListItemIcon>
              <ListItemText primary={hasCheckinVoice && hasCheckoutVoice ? 'Update voice' : 'Add voice'} />
            </MenuItem>
          </Menu>
        </Box>
      );
    })()
  );

  const renderHrmsActions = (rec, empId, name, hasCheckinVoice, hasCheckoutVoice) => {
    const employeeId = String(empId || '');
    const menuOpen = Boolean(hrmsManageMenu.anchorEl && hrmsManageMenu.employeeId === employeeId);
    const isEnrolled = Boolean(rec.is_enrolled || enrolledByPid[employeeId]?.exists);

    return (
      <Box
        sx={{
          display: 'flex',
          gap: 0.75,
          alignItems: 'center',
          flexWrap: 'no-wrap',
          width: '100%',
        }}>

        {renderHrmsVoice(hasCheckinVoice, hasCheckoutVoice, empId, name)}
        <Button
          size="small"
          variant="outlined"
          endIcon={menuOpen ? <ExpandLess sx={{ fontSize: 18 }} /> : <ExpandMore sx={{ fontSize: 18 }} />}
          aria-haspopup="menu"
          aria-expanded={menuOpen ? 'true' : undefined}
          onClick={(event) => setHrmsManageMenu((prev) =>
            prev.anchorEl && prev.employeeId === employeeId
              ? { anchorEl: null, employeeId: null }
              : { anchorEl: event.currentTarget, employeeId }
          )}
          sx={{
            textTransform: 'none',
            borderColor: primary,
            color: primary,
            '&:hover': { borderColor: primary, backgroundColor: 'rgba(64, 81, 138, 0.08)' },
          }}
        >
          Manage
        </Button>
        <Menu
          anchorEl={hrmsManageMenu.anchorEl}
          open={menuOpen}
          onClose={closeHrmsManageMenu}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{ sx: { minWidth: 200 } }}
        >
          <MenuItem
            onClick={() => {
              closeHrmsManageMenu();
              openHrmsNameEditDialog(rec);
            }}
          >
            <ListItemIcon>
              <Edit fontSize="small" />
            </ListItemIcon>
            <ListItemText primary="Edit Name" />
          </MenuItem>
          {!isEnrolled && <MenuItem
            onClick={() => {
              closeHrmsManageMenu();
              openEnrollFromHrms(rec);
            }}
          >
            <ListItemIcon>
              <PersonAdd fontSize="small" />
            </ListItemIcon>
            <ListItemText primary={'Enroll'} />
          </MenuItem>}
        </Menu>
      </Box>
    );
  };

  const editSelectMenuProps = {
    PaperProps: {
      sx: {
        mt: 1,
        minWidth: 0,
        maxWidth: {
          xs: 120,    // extra small screens
          sm: 180,    // small screens
          md: 280,    // medium screens
          lg: 340,    // large screens
          xl: 420     // extra large screens
        },
        flex: { xs: 1, sm: .5 },
        maxHeight: { xs: 240, sm: 320 },
      },
    },
    MenuListProps: {
      dense: isMobile,
    },
  };

  /**
   * @type {(limits:number,offset:number,query:string,personId:string,companyId:string,branchId:string,designation:string,department:string) => Promise<void>}
   */
  const loadFaces = useCallback(async (
    limit = 10,
    offset = 0,
    query = enrolledQuery,
    personId = undefined,
    companyId = enrolledCompanyFilter,
    branchId = enrolledBranchFilter,
    designation = enrolledDesignationFilter,
    department = enrolledDepartmentFilter
  ) => {
    // `undefined` means "use the URL-hydrated person filter"; an explicit value
    // (including `null`) always wins so callers can override/clear it.
    const resolvedPersonId = personId === undefined ? (enrolledSearchPersonId || null) : personId;
    const requestId = ++loadFacesRequestIdRef.current;
    setLoading(true);
    setEnrolledLoadError('');
    try {
      const fetchPage = async (targetOffset) => {
        const data = await getPersons(
          limit,
          targetOffset,
          showDeleted,
          query && query.trim() ? query.trim() : null,
          resolvedPersonId,
          companyId,
          branchId,
          false,
          designation,
          department,
          false,
          false
        );
        console.log('Persons API response:', data);

        if (data.persons && data.pagination) {
          return {
            persons: data.persons,
            pagination: {
              ...data.pagination,
              limit: Number(data.pagination.limit) || limit,
              offset: Number(data.pagination.offset) || targetOffset,
              total: Number(data.pagination.total) || 0,
              total_pages: Number(data.pagination.total_pages) || 0,
              current_page: Number(data.pagination.current_page) || Math.floor(targetOffset / limit) + 1,
            },
          };
        }

        const personsArray = Array.isArray(data) ? data : [];
        return {
          persons: personsArray,
          pagination: {
            total: personsArray.length,
            limit,
            offset: targetOffset,
            has_next: false,
            has_prev: targetOffset > 0,
            total_pages: personsArray.length > 0 ? Math.ceil(personsArray.length / limit) : 0,
            current_page: Math.floor(targetOffset / limit) + 1,
          },
        };
      };

      let result = await fetchPage(offset);
      const total = Number(result.pagination?.total) || 0;
      const resultOffset = Number(result.pagination?.offset) || 0;

      if (result.persons.length === 0 && total > 0 && resultOffset >= total) {
        const safeOffset = Math.max(0, (Math.ceil(total / limit) - 1) * limit);
        result = await fetchPage(safeOffset);
      }

      if (requestId !== loadFacesRequestIdRef.current) {
        return;
      }

      const employeeCodes = result.persons
        .map(p => ({ name: p.name, person_id: p.person_id, employee_code: p.employee_code }))
        .filter(p => p.employee_code);
      if (employeeCodes.length > 0) {
        console.log('Processing persons with employee codes:', employeeCodes);
      }

      setPersons(result.persons);
      setPagination(result.pagination);
      setEnrolledLoadError('');
    } catch (error) {
      if (requestId !== loadFacesRequestIdRef.current) {
        return;
      }
      console.error('Error loading persons:', error);
      const message = error?.message || 'Failed to load persons';
      setEnrolledLoadError(message);
      showSnackbar('Failed to load persons', 'error');
    } finally {
      if (requestId === loadFacesRequestIdRef.current) {
        setLoading(false);
      }
    }
  }, [showDeleted, enrolledQuery, enrolledCompanyFilter, enrolledBranchFilter, enrolledDesignationFilter, enrolledDepartmentFilter, enrolledSearchPersonId]);

  const loadPersonThumbnail = useCallback((person) => {
    const personId = person?.person_id;
    if (!personId || thumbnailRequestsRef.current.has(personId)) return;
    const faces = Array.isArray(person.faces) ? person.faces : [];
    const activeFaces = faces.filter(
      (face) => !face.deleted_at && face.face_id
    );

    // Prefer front face, then any active face.
    // If no active face exists (deactivated view), use any available face.
    const preferredFace =
      activeFaces.find(
        (face) => String(face.face_angle || "").trim().toLowerCase() === "front"
      ) ||
      activeFaces[0] ||
      faces.find((face) => face.face_id) ||
      null;
    const version = preferredFace
      ? `${preferredFace.face_id}:${preferredFace.last_updated || ''}`
      : 'none';
    const cached = personThumbnailsRef.current[personId];
    if (cached && cached.version === version) return;
    if (!preferredFace?.face_id) {
      personThumbnailsRef.current[personId] = { version, url: null };
      setPersonThumbnails((prev) => ({ ...prev, [personId]: null }));
      return;
    }
    thumbnailRequestsRef.current.add(personId);
    (async () => {
      try {
        const blob = await getFaceImage(preferredFace.face_id);
        const dataUrl = await blobToDataUrl(blob);
        personThumbnailsRef.current[personId] = { version, url: dataUrl };
        setPersonThumbnails((prev) => ({ ...prev, [personId]: dataUrl }));
      } catch (err) {
        console.error('Failed to load employee thumbnail:', err);
        personThumbnailsRef.current[personId] = { version, url: null };
        setPersonThumbnails((prev) => ({ ...prev, [personId]: null }));
      } finally {
        thumbnailRequestsRef.current.delete(personId);
      }
    })();
  }, []);

  // Companies for enrolled filters come from mount `loadCompanies` (single getKivoConfig).
  // This only loads branch list for the dropdowns.
  const loadEnrolledFilters = useCallback(async (companyId = null) => {
    try {
      const branchesData = await getKivoBranches(companyId);
      if (branchesData?.branches) {
        setEnrolledBranches(branchesData.branches.map(branch => ({
          branch_id: branch.branch_id,
          branch_name: branch.branch_name,
          branch_address: branch.branch_address || branch.branch_name,
          company_id: branch.company_id,
        })));
      }
    } catch (error) {
      console.error('Error loading enrolled filters:', error);
    }
  }, []);

  const loadEnrolledDesignationOptions = useCallback(async (companyId, branchId) => {
    try {
      const [designationRes, departmentRes] = await Promise.all([
        fetchOrgDesignations({
          permissions,
          companyId: companyId || null,
          branchId,
          includeInactive: false,
          limit: 200,
        }),
        fetchOrgDepartments({
          permissions,
          companyId: companyId || null,
          branchId,
          includeInactive: false,
          limit: 200,
        }),
      ]);
      setEnrolledDesignations(
        normalizeOrgMasterOptions(
          Array.isArray(designationRes?.designations) ? designationRes.designations : []
        )
      );
      setEnrolledDepartments(
        normalizeOrgMasterOptions(
          Array.isArray(departmentRes?.departments) ? departmentRes.departments : []
        )
      );
    } catch (error) {
      console.error('Error loading enrolled designation/department filters:', error);
      setEnrolledDesignations([]);
      setEnrolledDepartments([]);
    }
  }, [permissions]);

  // Company pickers: kivo_config when Kivo is on, GET /companies when off.
  useEffect(() => {
    const loadCompanies = async () => {
      try {
        const res = await getCompanyPickerConfig(permissions);
        const configs = res?.configs || (res ? [res] : []);
        setCompanyOptions(configs);
        setEnrolledCompanies(companiesFromKivoConfigData(res));
        const defaultConfigId = res?.default_config_id || (configs[0]?.config_id ?? '');
        const defaultCfg = configs.find((c) => c.config_id === defaultConfigId) || configs[0];
        if (!selectedCompanyId) {
          const sessionMatch = sessionCompanyId
            ? configs.find((c) => String(c.company_id || '') === String(sessionCompanyId))
            : null;
          if (sessionMatch?.company_id) {
            setSelectedCompanyId(sessionMatch.company_id);
          } else if (defaultCfg?.company_id) {
            setSelectedCompanyId(defaultCfg.company_id);
          }
        }
        if (!enrolledCompanyFilter) {
          const sessionMatch = sessionCompanyId
            ? configs.find((c) => String(c.company_id || '') === String(sessionCompanyId))
            : null;
          const nextCompanyId = sessionMatch?.company_id || defaultCfg?.company_id || '';
          if (nextCompanyId) {
            setEnrolledCompanyFilter(nextCompanyId);
          }
        }
      } catch (err) {
        console.debug('Could not load company tokens', err);
      } finally {
        setEnrolledDefaultsReady(true);
      }
    };
    loadCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload when Kivo source flag is known
  }, [kivoFeatureEnabled]);

  useEffect(() => {
    if (!kivoFeatureEnabled && tab === 'company_records') {
      setTab('enrolled');
    }
  }, [kivoFeatureEnabled, tab]);

  useEffect(() => {
    if (tab === 'enrolled') {
      // Avoid firing "no-company" calls on first load.
      // After defaults are ready, these will run with the default company_id.
      if (!enrolledDefaultsReady) return;
      // Allow "All Companies" after user explicitly changes the company filter.
      if (!enrolledCompanyFilter && !enrolledCompanyTouchedRef.current) return;
      loadEnrolledFilters(enrolledCompanyFilter || null);
    }
  }, [tab, enrolledDefaultsReady, enrolledCompanyFilter, loadEnrolledFilters]);

  useEffect(() => {
    if (tab !== 'enrolled') return;
    if (!enrolledDefaultsReady) return;

    // Prevent "no company" designation/department calls during initial page load.
    // If user explicitly clears the company filter (All Companies), allow loading across all companies.
    if (!enrolledCompanyFilter && !enrolledCompanyTouchedRef.current) return;
    const branchId = enrolledBranchFilter && enrolledBranchFilter.trim() ? enrolledBranchFilter : null;
    loadEnrolledDesignationOptions(enrolledCompanyFilter || null, branchId);
  }, [tab, enrolledDefaultsReady, enrolledCompanyFilter, enrolledBranchFilter, loadEnrolledDesignationOptions]);


  // Show toast passed via navigation state (e.g., after registration)
  useEffect(() => {
    const toast = location?.state && location.state.toast;
    if (toast && toast.message) {
      showSnackbar(toast.message, toast.severity || 'success');
      // Clear state to avoid repeating on back/forward
      navigate('.', { replace: true, state: {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handlePageChange = (event, page) => {
    const newOffset = (page - 1) * pagination.limit;
    loadFaces(pagination.limit, newOffset);
  };

  const handleLimitChange = (event) => {
    const newLimit = parseInt(event.target.value);
    loadFaces(newLimit, 0);
  };

  const handleToggleDeleted = (event, newValue) => {
    // Only update if a value is selected (prevents deselecting both buttons)
    if (newValue !== null) {
      clearEnrolledSearchDebounce();
      setShowDeleted(newValue === 'deleted');
      // Reset pagination when switching views
      setPagination(prev => ({ ...prev, offset: 0, current_page: 1 }));
    }
  };

  // eslint-disable-next-line no-unused-vars
  const handleViewFaceDetails = async (face) => { };

  // eslint-disable-next-line no-unused-vars
  const handleViewFaceImage = async (face) => {
    try {
      const imageBlob = await getFaceImage(face.face_id);
      const imageUrl = URL.createObjectURL(imageBlob);
      setImageDialog({ open: true, imageUrl, faceName: face.name });
    } catch (error) {
      console.error('Error loading face image:', error);
      showSnackbar('Failed to load face image', 'error');
    }
  };

  const handleFindPerson = (person) => {
    if (!person || !person.faces || person.faces.length === 0) {
      showSnackbar('No faces found for this employee', 'error');
      return;
    }

    // Filter out deleted faces
    const availableFaces = person.faces.filter(f => !f.deleted_at);
    if (availableFaces.length === 0) {
      showSnackbar('No active faces found for this employee', 'error');
      return;
    }

    // Prefer front angle, otherwise use first available
    const frontFace = availableFaces.find(f => f.face_angle === 'front');
    const defaultFace = frontFace || availableFaces[0];

    setFindPersonModalPerson(person);
    setSelectedAngle(String(defaultFace.face_id));
    setSimilarityThreshold('0.3');
    setFindPersonLogType('audit');
    setFindPersonModalOpen(true);
  };

  const handleFindPersonModalClose = () => {
    setFindPersonModalOpen(false);
    setFindPersonModalPerson(null);
    setSelectedAngle('');
    setSimilarityThreshold('0.3');
    setFindPersonLogType('audit');
    setFindPersonLoading(false);
    Object.values(findPersonFaceThumbUrlsRef.current).forEach((u) => {
      if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
    });
    findPersonFaceThumbUrlsRef.current = {};
    setFindPersonFaceThumbUrls({});
  };

  useEffect(() => {
    if (!findPersonModalOpen || !findPersonModalPerson?.faces?.length) {
      Object.values(findPersonFaceThumbUrlsRef.current).forEach((u) => {
        if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
      });
      findPersonFaceThumbUrlsRef.current = {};
      setFindPersonFaceThumbUrls({});
      return undefined;
    }
    let cancelled = false;
    const faces = findPersonModalPerson.faces.filter((f) => !f.deleted_at);

    const loadThumbs = async () => {
      const next = {};
      await Promise.all(
        faces.map(async (f) => {
          const idKey = String(f.face_id);
          try {
            const blob = await getFaceImage(f.face_id);
            if (!cancelled) {
              next[idKey] = URL.createObjectURL(blob);
            }
          } catch {
            if (!cancelled) next[idKey] = '';
          }
        }),
      );
      if (cancelled) {
        Object.values(next).forEach((u) => {
          if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
        });
        return;
      }
      Object.values(findPersonFaceThumbUrlsRef.current).forEach((u) => {
        if (u && String(u).startsWith('blob:')) URL.revokeObjectURL(u);
      });
      findPersonFaceThumbUrlsRef.current = next;
      setFindPersonFaceThumbUrls({ ...next });
    };

    loadThumbs();
    return () => {
      cancelled = true;
    };
  }, [findPersonModalOpen, findPersonModalPerson]);

  const handleFindPersonSubmit = async () => {
    if (!findPersonModalPerson || !selectedAngle) {
      showSnackbar('Please select an angle', 'error');
      return;
    }

    const threshold = parseFloat(similarityThreshold);
    if (isNaN(threshold) || threshold < 0 || threshold > 1) {
      showSnackbar('Similarity threshold must be between 0 and 1', 'error');
      return;
    }

    try {
      setFindPersonLoading(true);

      // Find the selected face
      const selectedFace = findPersonModalPerson.faces.find(
        (f) => String(f.face_id) === String(selectedAngle),
      );
      if (!selectedFace) {
        showSnackbar('Selected face not found', 'error');
        return;
      }

      // Get the face image
      const imageBlob = await getFaceImage(selectedFace.face_id);

      // Convert blob to base64
      const reader = new FileReader();
      reader.onloadend = () => {
        const dataUrl = reader.result; // Full data URL: "data:image/jpeg;base64,..."

        const logsPath = findPersonLogType === 'activity' ? '/activity' : '/in-out';
        navigate(logsPath, {
          state: {
            findPersonImage: dataUrl,
            personId: findPersonModalPerson.person_id,
            personName: findPersonModalPerson.name,
            similarityThreshold: threshold,
          },
        });

        handleFindPersonModalClose();
      };
      reader.onerror = () => {
        showSnackbar('Failed to convert image to base64', 'error');
        setFindPersonLoading(false);
      };
      reader.readAsDataURL(imageBlob);
    } catch (error) {
      console.error('Error in find person:', error);
      showSnackbar(error.message || 'Failed to process image', 'error');
      setFindPersonLoading(false);
    }
  };

  const handleOpenEdit = (face) => {
    setEditDialog({ open: true, face });
    setSelectedFile(null);
    setEditCameraOn(false);
    setEditQualityResult(null);
    revokePreviewUrl(previewUrl);
    setPreviewUrl(null);
  };

  const handleCloseEdit = () => {
    setEditDialog({ open: false, face: null });
    setEditCameraOn(false);
    setEditQualityResult(null);
    revokePreviewUrl(previewUrl);
    setPreviewUrl(null);
  };

  const startEditCapture = () => {
    setEditQualityResult(null);
    setEditCaptureNonce((previous) => previous + 1);
    setEditCameraOn(true);
  };

  const applyVerifiedEditImage = async (imageBase64, file) => {
    setEditQualityResult({
      loading: true,
      success: false,
      message: 'Checking image quality...',
    });
    try {
      const result = await verifyFaceQualityBase64(imageBase64);
      setEditQualityResult({ ...result, loading: false });
      if (!result?.success) {
        setSelectedFile(null);
        showSnackbar(result?.message || 'Image rejected. Please capture a clearer photo.', 'error');
        setEditCaptureNonce((previous) => previous + 1);
        return;
      }
      setSelectedFile(file);
      revokePreviewUrl(previewUrl);
      setPreviewUrl(imageBase64);
      setEditCameraOn(false);
    } catch (error) {
      console.error('Image quality verification failed:', error);
      const errorResult = {
        loading: false,
        success: false,
        message: error.message || 'Failed to verify image quality',
        face_count: 0,
        pose_quality: 0,
        lighting_quality: 0,
        quality_score: 0,
        similarity: null,
        encoding_dimension: 0,
      };
      setEditQualityResult(errorResult);
      setSelectedFile(null);
      showSnackbar(errorResult.message, 'error');
    }
  };

  const handleEditAutoCaptured = (imageBase64) => {
    if (!imageBase64) return;
    try {
      const file = dataUrlToFile(imageBase64, 'capture.jpg');
      revokePreviewUrl(previewUrl);
      setPreviewUrl(imageBase64);
      setEditCameraOn(false);
      setSelectedFile(null);
      void applyVerifiedEditImage(imageBase64, file);
    } catch (error) {
      console.error('Failed to store captured image:', error);
      showSnackbar('Failed to capture photo', 'error');
      setEditCaptureNonce((previous) => previous + 1);
    }
  };

  const onFileChange = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    setEditCameraOn(false);
    revokePreviewUrl(previewUrl);
    const objectUrl = URL.createObjectURL(file);
    setPreviewUrl(objectUrl);
    if (!editDialog.open) {
      setSelectedFile(file);
      return;
    }
    setSelectedFile(null);
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      if (typeof dataUrl !== 'string') return;
      revokePreviewUrl(objectUrl);
      setPreviewUrl(dataUrl);
      void applyVerifiedEditImage(dataUrl, file);
    };
    reader.onerror = () => {
      showSnackbar('Unable to read selected image. Please try another file.', 'error');
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          facingMode: 'user'
        },
        audio: false
      });

      streamRef.current = stream;
      setIsCameraOn(true);

      // Wait for video element to be rendered
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
        }
      }, 100);
    } catch (err) {
      console.error('Camera error:', err);
      showSnackbar('Unable to access camera', 'error');
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach(t => t.stop()); } catch (e) { }
      streamRef.current = null;
    }
    if (videoRef.current) {
      try { videoRef.current.pause(); } catch (e) { }
      videoRef.current.srcObject = null;
    }
    setIsCameraOn(false);
  };

  const toggleCamera = async () => {
    if (streamRef.current) {
      stopCamera();
    } else {
      await startCamera();
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      setSelectedFile(file);
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(blob));
      // stop camera after capture
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(t => t.stop());
        streamRef.current = null;
      }
      if (videoRef.current) {
        videoRef.current.pause();
        videoRef.current.srcObject = null;
      }
      setIsCameraOn(false);
    }, 'image/jpeg', 0.92);
  };

  // eslint-disable-next-line no-unused-vars
  const retakeCapture = async () => { };

  const submitEdit = async () => {
    if (!editDialog.face || !selectedFile) {
      showSnackbar('Please select or capture an image', 'warning');
      return;
    }
    try {
      const updated = await updateFaceImage(editDialog.face.face_id, selectedFile);
      const faceId = editDialog.face.face_id;
      const refreshedAt = new Date().toISOString();
      showSnackbar('Image updated successfully', 'success');
      handleCloseEdit();
      setFacesModalPerson((current) => {
        if (!current?.faces?.some((face) => face.face_id === faceId)) return current;
        const personId = current.person_id;
        if (personId) delete personThumbnailsRef.current[personId];
        return {
          ...current,
          faces: current.faces.map((face) => (
            face.face_id === faceId
              ? {
                  ...face,
                  last_updated: refreshedAt,
                  pose_quality: updated?.pose_quality ?? face.pose_quality,
                  lighting_quality: updated?.lighting_quality ?? face.lighting_quality,
                }
              : face
          )),
        };
      });
      loadFaces(pagination.limit, pagination.offset);
    } catch (err) {
      console.error('Update failed:', err);
      showSnackbar(err.message || 'Failed to update image', 'error');
    }
  };

  const handleSoftDeleteFace = (face) => {
    setConfirmDialog({
      open: true,
      title: 'Soft Delete Face',
      message: `Are you sure you want to soft delete the face for ${face.name}? This can be restored later.`,
      onConfirm: () => performSoftDelete(face.face_id),
    });
  };

  const handleRestoreFace = (face) => {
    setConfirmDialog({
      open: true,
      title: 'Activate Face',
      message: `Are you sure you want to activate the face for ${face.name}? This will make it active again.`,
      onConfirm: () => performRestore(face.face_id),
    });
  };

  const performSoftDelete = async (faceId) => {
    try {
      const response = await softDeleteFace(faceId);

      if (response.ok) {
        // Update currently opened employee faces immediately.
        setFacesModalPerson((previousPerson) => {
          if (!previousPerson) return null;

          const updatedFaces = (previousPerson.faces || []).map((face) =>
            String(face.face_id) === String(faceId)
              ? {
                ...face,
                deleted_at: new Date().toISOString(),
              }
              : face
          );

          const activeFaces = updatedFaces.filter(
            (face) => !face.deleted_at
          );

          // Close dialog automatically if no active faces remain.
          if (!showDeleted && activeFaces.length === 0) {
            return null;
          }

          return {
            ...previousPerson,
            faces: updatedFaces,
          };
        });
        const currentPerson = facesModalPerson;

        if (currentPerson?.person_id) {
          delete personThumbnailsRef.current[currentPerson.person_id];

          setPersonThumbnails((previous) => ({
            ...previous,
            [currentPerson.person_id]: null,
          }));

          thumbnailRequestsRef.current.delete(currentPerson.person_id);
        }

        // Refresh employee list without affecting the dialog update.
        loadFaces(pagination.limit, pagination.offset);

        showSnackbar('Face soft deleted successfully', 'success');
      } else {
        showSnackbar('Failed to soft delete face', 'error');
      }
    } catch (error) {
      console.error('Error soft deleting face:', error);
      showSnackbar('Failed to soft delete face', 'error');
    }
  };

  const performRestore = async (faceId) => {
    try {
      const response = await restoreFace(faceId);

      if (response) {
        setFacesModalPerson((previousPerson) => {
          if (!previousPerson) return null;

          const updatedFaces = (previousPerson.faces || []).map((face) =>
            String(face.face_id) === String(faceId)
              ? { ...face, deleted_at: null }
              : face
          );

          return {
            ...previousPerson,
            faces: updatedFaces,
          };
        });

        showSnackbar('Face activated successfully', 'success');

        loadFaces(pagination.limit, pagination.offset);
      } else {
        showSnackbar('Failed to activate face', 'error');
      }
    } catch (error) {
      console.error('Error restoring face:', error);
      showSnackbar('Failed to activate face', 'error');
    }
  };

  const handleDeleteAllFaces = (person) => {
    const activeFaces = person.faces?.filter(face => !face.deleted_at) || [];
    if (activeFaces.length === 0) {
      showSnackbar('No active faces to delete', 'warning');
      return;
    }
    setConfirmDialog({
      open: true,
      title: 'Delete All Faces',
      message: `Are you sure you want to delete all ${activeFaces.length} face(s) for ${person.name}? This can be activated later.`,
      onConfirm: () => performDeleteAllFaces(activeFaces),
    });
  };

  const performDeleteAllFaces = async (faces) => {
    try {
      let successCount = 0;
      let failCount = 0;

      for (const face of faces) {
        try {
          const response = await softDeleteFace(face.face_id);
          if (response.ok) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Error deleting face ${face.face_id}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        showSnackbar(`Successfully deleted ${successCount} face(s)`, 'success');
        loadFaces(pagination.limit, pagination.offset);
      }
      if (failCount > 0) {
        showSnackbar(`Failed to delete ${failCount} face(s)`, 'error');
      }
    } catch (error) {
      console.error('Error deleting faces:', error);
      showSnackbar('Failed to delete faces', 'error');
    }
  };

  const handleRestoreAllFaces = (person) => {
    const deletedFaces = person.faces?.filter(face => face.deleted_at) || [];
    if (deletedFaces.length === 0) {
      showSnackbar('No deleted faces to activate', 'warning');
      return;
    }
    setConfirmDialog({
      open: true,
      title: 'Activate All Faces',
      message: `Are you sure you want to activate all ${deletedFaces.length} face(s) for ${person.name}? This will make them active again.`,
      onConfirm: () => performRestoreAllFaces(deletedFaces),
    });
  };

  const performRestoreAllFaces = async (faces) => {
    try {
      let successCount = 0;
      let failCount = 0;

      for (const face of faces) {
        try {
          const response = await restoreFace(face.face_id);
          if (response) {
            successCount++;
          } else {
            failCount++;
          }
        } catch (error) {
          console.error(`Error activating face ${face.face_id}:`, error);
          failCount++;
        }
      }

      if (successCount > 0) {
        showSnackbar(`Successfully activated ${successCount} face(s)`, 'success');
        loadFaces(pagination.limit, pagination.offset);
      }
      if (failCount > 0) {
        showSnackbar(`Failed to activate ${failCount} face(s)`, 'error');
      }
    } catch (error) {
      console.error('Error activating faces:', error);
      showSnackbar('Failed to activate faces', 'error');
    }
  };

  useEffect(() => {
    // Check if user is authenticated
    const token = getStoredToken();
    if (!token) {
      navigate('/login');
      return;
    }

    const showDeletedChanged = prevShowDeletedRef.current !== showDeleted;
    prevShowDeletedRef.current = showDeleted;

    // Avoid duplicate initial calls (React 18 StrictMode runs effects twice in dev).
    if (tab !== 'enrolled') {
      if (!didInitialPersonsLoadRef.current || showDeletedChanged) {
        didInitialPersonsLoadRef.current = true;
        loadFaces();
      }
      return;
    }

    // Enrolled tab: wait for default company (or explicit All Companies), then load once and on showDeleted changes.
    if (!enrolledDefaultsReady) return;
    if (!enrolledCompanyFilter && !enrolledCompanyTouchedRef.current) return;
    if (!didInitialPersonsLoadRef.current || showDeletedChanged) {
      didInitialPersonsLoadRef.current = true;
      loadFaces(
        pagination.limit,
        0,
        enrolledQuery,
        null,
        enrolledCompanyFilter,
        enrolledBranchFilter,
        enrolledDesignationFilter,
        enrolledDepartmentFilter
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, showDeleted, tab, enrolledDefaultsReady, enrolledCompanyFilter]);

  useEffect(() => {
    const storedTime = localStorage.getItem('crm_last_sync_time');
    if (storedTime) {
      setLastSyncTime(storedTime);
    }
  }, []);

  const handleRegisterEmployee = () => {
    navigate('/employees/register');
  };

  const handleTabChange = (e, value) => {
    if (value === 'company_records' && !kivoFeatureEnabled) {
      setTab('enrolled');
      return;
    }
    setTab(value);
    if (value === 'crm') {
      // Auto-load first page of CRM records when tab opens
      importCrm();
    }
  };

  useEffect(() => {
    setSearchParams((prevParams) => {
      const params = new URLSearchParams(prevParams);
      if (tab && tab !== 'enrolled') params.set('tab', tab); else params.delete('tab');
      if (enrolledQuery) params.set('enrolled_query', enrolledQuery); else params.delete('enrolled_query');
      if (enrolledSearchPersonId) params.set('person_id', enrolledSearchPersonId); else params.delete('person_id');
      if (enrolledCompanyFilter) params.set('enrolled_company', enrolledCompanyFilter); else params.delete('enrolled_company');
      if (enrolledBranchFilter) params.set('enrolled_branch', enrolledBranchFilter); else params.delete('enrolled_branch');
      if (enrolledDesignationFilter) params.set('enrolled_designation', enrolledDesignationFilter); else params.delete('enrolled_designation');
      if (enrolledDepartmentFilter) params.set('enrolled_department', enrolledDepartmentFilter); else params.delete('enrolled_department');
      if (showDeleted) params.set('enrolled_deleted', 'true'); else params.delete('enrolled_deleted');
      if (selectedCompanyId) params.set('company_id', selectedCompanyId); else params.delete('company_id');
      if (hrmsBranchFilter) params.set('hrms_branch', hrmsBranchFilter); else params.delete('hrms_branch');
      if (hrmsDesignationFilter) params.set('hrms_designation', hrmsDesignationFilter); else params.delete('hrms_designation');
      if (hrmsDepartmentFilter) params.set('hrms_department', hrmsDepartmentFilter); else params.delete('hrms_department');
      if (hrmsQuery) params.set('hrms_query', hrmsQuery); else params.delete('hrms_query');
      if (crmQuery) params.set('crm_query', crmQuery); else params.delete('crm_query');
      return params.toString() !== prevParams.toString() ? params : prevParams;
    }, { replace: true });
  }, [
    crmQuery,
    enrolledBranchFilter,
    enrolledCompanyFilter,
    enrolledDepartmentFilter,
    enrolledSearchPersonId,
    enrolledDesignationFilter,
    enrolledQuery,
    hrmsBranchFilter,
    hrmsDepartmentFilter,
    hrmsDesignationFilter,
    hrmsQuery,
    selectedCompanyId,
    showDeleted,
    tab,
    setSearchParams,
  ]);

  const handleRefresh = () => {
    if (tab === 'crm') {
      if ((crmQuery || '').trim()) {
        searchCrm(crmMeta.current_page || 1);
      } else {
        importCrm(crmMeta.current_page || 1);
      }
    } else if (tab === 'company_records') {
      if ((hrmsQuery || '').trim()) {
        searchHrms(hrmsMeta.current_page || 1);
      } else {
        importHrms(hrmsMeta.current_page || 1, false);
      }
    } else {
      loadFaces(pagination.limit, pagination.offset);
    }
  };

  // CRM Functions
  const importCrm = async (page = crmPage) => {
    try {
      setCrmLoading(true);
      // Normalize datetime strings to cover full-day extremes when only a date is provided
      const normalizeStart = (v) => {
        if (!v) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T00:00:00`;
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return `${v}:00`;
        return v;
      };
      const normalizeEnd = (v) => {
        if (!v) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T23:59:59`;
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return `${v}:59`;
        return v;
      };
      const since = normalizeStart(crmSince);
      const until = normalizeEnd(crmUntil) || normalizeEnd(toLocalDateTimeString(new Date()));
      const res = await fetchCrmContacts({ since, until, page, sort: '-created_at' });
      // Extract synced_at from backend response
      const syncedAt = res?.synced_at || new Date().toISOString();
      setLastSyncTime(syncedAt);
      localStorage.setItem('crm_last_sync_time', syncedAt);
      // Try to locate records array in response
      const records = res?.data || res?.payload || res?.contacts || res?.list || [];
      setCrmRecords(Array.isArray(records) ? records : []);
      // Server pagination meta: count = total pages, current_page = current page
      const meta = res?.meta || {};
      const totalPages = parseInt(meta.count ?? meta.total_pages ?? 0, 10) || 0;
      const currentPage = parseInt(meta.current_page ?? meta.page ?? page, 10) || page;
      setCrmMeta({ count: totalPages, current_page: currentPage });
      localStorage.setItem('crm_last_import_at', until);
      setCrmPage(currentPage);
      showSnackbar(`Loaded ${Array.isArray(records) ? records.length : 0} CRM records (page ${currentPage}/${totalPages || 1})`, 'success');
    } catch (e) {
      console.error('CRM import error:', e);
      showSnackbar(e.message || 'Failed to import CRM data', 'error');
    } finally {
      setCrmLoading(false);
    }
  };

  const searchCrm = async (page = 1) => {
    try {
      setCrmLoading(true);
      const q = (crmQuery || '').trim();
      if (!q) {
        // if query cleared, fallback to import with current filters
        await importCrm(1);
        return;
      }
      const res = await searchCrmContacts({ q, page, sort: '-created_at' });
      // Extract synced_at from backend response
      const syncedAt = res?.synced_at || new Date().toISOString();
      setLastSyncTime(syncedAt);
      localStorage.setItem('crm_last_sync_time', syncedAt);
      const records = res?.data || res?.payload || res?.contacts || res?.list || [];
      setCrmRecords(Array.isArray(records) ? records : []);
      const meta = res?.meta || {};
      const totalPages = parseInt(meta.count ?? meta.total_pages ?? 0, 10) || 0;
      const currentPage = parseInt(meta.current_page ?? meta.page ?? page, 10) || page;
      setCrmMeta({ count: totalPages, current_page: currentPage });
      setCrmPage(currentPage);
      showSnackbar(`Search results: ${Array.isArray(records) ? records.length : 0} (page ${currentPage}/${totalPages || 1})`, 'success');
    } catch (e) {
      console.error('CRM search error:', e);
      showSnackbar(e.message || 'Failed to search CRM data', 'error');
    } finally {
      setCrmLoading(false);
    }
  };

  const handleClearSearch = () => {
    setCrmQuery('');
    importCrm(1);
  };

  // Company records functions
  // Default shouldSync to false to avoid unintended background syncs.
  const importHrms = async (page = hrmsPage, shouldSync = false, opts = {}) => {
    const {
      silent = false,
      from_date = null,
      to_date = null,
      companyId = null,
      branchId = null,
      apiKeyId = null,
      designation = undefined,
      department = undefined,
      listLimit = undefined,
    } = opts || {};
    try {
      if (!shouldSync) {
        setHrmsLoading(true);
      }
      setHrmsNotConfigured(false);
      // Use filter values if not provided in opts, otherwise use selectedCompanyId as fallback
      const targetCompanyId = companyId || selectedCompanyId || (companyOptions[0]?.company_id || '');
      // Use branchId from opts if provided, otherwise use hrmsBranchFilter state
      // If branchId is null or empty string, don't filter by branch (show all branches)
      // Priority: opts.branchId > hrmsBranchFilter state
      let targetBranchId = null;
      if (branchId !== undefined) {
        // branchId was explicitly passed in opts (could be null, empty string, or a valid ID)
        // If it's null, empty string, or falsy, set to null (no filter - show all branches)
        if (branchId && typeof branchId === 'string' && branchId.trim()) {
          targetBranchId = branchId;
        } else {
          targetBranchId = null; // Explicitly null means no branch filter
        }
      } else {
        // branchId not provided in opts, use state value
        if (hrmsBranchFilter && hrmsBranchFilter.trim()) {
          targetBranchId = hrmsBranchFilter;
        } else {
          targetBranchId = null; // No branch filter - show all branches
        }
      }

      const normalizeFilterValue = (value) => {
        if (value === undefined) return undefined;
        if (value === null) return null;
        const trimmed = String(value).trim();
        return trimmed ? trimmed : null;
      };

      let targetDesignation = null;
      if (designation !== undefined) {
        targetDesignation = normalizeFilterValue(designation);
      } else if (hrmsDesignationFilter && hrmsDesignationFilter.trim()) {
        targetDesignation = hrmsDesignationFilter.trim();
      }

      let targetDepartment = null;
      if (department !== undefined) {
        targetDepartment = normalizeFilterValue(department);
      } else if (hrmsDepartmentFilter && hrmsDepartmentFilter.trim()) {
        targetDepartment = hrmsDepartmentFilter.trim();
      }
      const normalizeStart = (v) => {
        if (!v) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T00:00:00`;
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return `${v}:00`;
        return v;
      };
      const normalizeEnd = (v) => {
        if (!v) return '';
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) return `${v}T23:59:59`;
        if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(v)) return `${v}:59`;
        return v;
      };
      let res;
      if (shouldSync) {
        // For sync flow with dates: use from_date/to_date (no sync_token on backend)
        // For sync flow without dates: use sync_token on backend
        const params = { page, sync: true };
        if (from_date || to_date) {
          // Date-based sync: pass from_date and to_date to backend
          // Backend will NOT use sync_token when dates are provided
          if (from_date) params.from_date = from_date;
          if (to_date) params.to_date = to_date;
          console.log('[HRMS] Date-based sync with from_date/to_date:', { from_date, to_date });
        } else {
          // Sync All: no dates, backend will use sync_token
          console.log('[HRMS] Sync All (using sync_token on backend)');
        }
        if (targetCompanyId) params.companyId = targetCompanyId;
        // Only add branchId if it's a valid non-empty string (null/undefined/empty means show all branches)
        // Don't add branchId parameter at all when showing all branches
        if (targetBranchId && typeof targetBranchId === 'string' && targetBranchId.trim().length > 0) {
          params.branchId = targetBranchId;
        }
        if (apiKeyId) params.apiKeyId = apiKeyId;
        if (targetDesignation) params.designation = targetDesignation;
        if (targetDepartment) params.department = targetDepartment;
        if (listLimit != null && Number(listLimit) > 0) {
          params.limit = Number(listLimit);
        }
        // Mark syncing state and keep table visible
        setHrmsSyncing(true);
        res = await fetchHrmsEmployees(params);
      } else {
        // For listing (no sync): no date filters
        const params = { page, sync: false, limit: Number(listLimit) || hrmsLimit };
        if (targetCompanyId) params.companyId = targetCompanyId;
        // Only add branchId if it's a valid non-empty string (null/undefined/empty means show all branches)
        // Don't add branchId parameter at all when showing all branches
        if (targetBranchId && typeof targetBranchId === 'string' && targetBranchId.trim().length > 0) {
          params.branchId = targetBranchId;
        }
        if (targetDesignation) params.designation = targetDesignation;
        if (targetDepartment) params.department = targetDepartment;
        res = await fetchHrmsEmployees(params);
      }
      // If successful, ensure flag is reset
      setHrmsNotConfigured(false);
      const syncedAt = res?.synced_at || new Date().toISOString();
      setLastSyncTime(syncedAt);
      localStorage.setItem('hrms_last_sync_time', syncedAt);
      const records = res?.data || res?.employees || res?.records || [];
      setHrmsRecords(Array.isArray(records) ? records : []);
      const meta = res?.meta || {};
      const totalPages = parseInt(meta.count ?? meta.total_pages ?? 0, 10) || 0;
      const currentPage = parseInt(meta.current_page ?? meta.page ?? page, 10) || page;
      setHrmsMeta({ count: totalPages, current_page: currentPage, total: meta.total || 0 });
      setHrmsPage(currentPage);
      // Name autocomplete: only names on the current page (no second employees request). Use "Search by Name or Employee Code" + Enter for full DB search.
      if (!shouldSync && targetCompanyId) {
        setHrmsEmployeeNames(extractHrmsNameOptionsFromRecords(records));
      } else if (!shouldSync && !targetCompanyId) {
        setHrmsEmployeeNames([]);
      }
      if (shouldSync) {
        if (res?.sync_summary) {
          // Legacy sync path: finished within request
          const s = res.sync_summary;
          showSnackbar(`Company records sync completed: ${s.records_processed || 0} processed`, 'success');
          setHrmsSyncing(false);
        } else if (res?.sync_started) {
          // New background sync path
          showSnackbar('Company records sync started in background', 'info');
          // Begin polling sync status until completion (poll with company_id filter)
          if (hrmsSyncPollRef.current) clearInterval(hrmsSyncPollRef.current);
          hrmsSyncPollRef.current = setInterval(async () => {
            try {
              const statusData = await getHrmsSyncStatus(selectedCompanyId, hrmsBranchFilter);
              if (statusData?.success && statusData?.last_sync) {
                setSyncInfo(statusData.last_sync);
                const st = String(statusData.last_sync.status || '').toLowerCase();
                if (st !== 'in_progress') {
                  clearInterval(hrmsSyncPollRef.current);
                  hrmsSyncPollRef.current = null;
                  setHrmsSyncing(false);
                  // Silent refresh of list to avoid flicker
                  await importHrms(page, false, { silent: true });
                  showSnackbar(`Company records sync ${st === 'completed' ? 'completed' : 'ended'}`, st === 'completed' ? 'success' : 'warning');
                }
              }
            } catch (pollErr) {
              console.debug('Sync status poll failed:', pollErr);
            }
          }, 2000);
        } else {
          // Synchronous path (server finished sync within this request)
          setHrmsSyncing(false);
          if (!silent) {
            showSnackbar('Company records sync completed', 'success');
          }
        }
      } else {
        if (!silent) {
          const syncMsg = `Loaded ${Array.isArray(records) ? records.length : 0} company records`;
          showSnackbar(`${syncMsg} (page ${currentPage}/${totalPages || 1})`, 'success');
        }
      }
    } catch (e) {
      console.error('Company records import error:', e);
      const msg = String(e?.message || '').toLowerCase();
      if (msg.includes('kivo not configured')) {
        setHrmsNotConfigured(true);
        showSnackbar('Kivo not configured. Please connect with Kivo first.', 'warning');
      } else {
        showSnackbar(e.message || 'Failed to import company records', 'error');
      }
      if (shouldSync) {
        setHrmsSyncing(false);
      }
    } finally {
      if (!shouldSync) setHrmsLoading(false);
    }
  };

  const searchHrms = async (page = 1, queryOverride = null, limitOverride = hrmsLimit) => {
    try {
      setHrmsLoading(true);
      setHrmsNotConfigured(false);
      const targetCompanyId = selectedCompanyId || (companyOptions[0]?.company_id || '');
      const q = ((queryOverride ?? hrmsQuery) || '').trim();
      if (!q) {
        await importHrms(1, false);
        return;
      }
      const res = await searchHrmsEmployees({
        q,
        page,
        limit: limitOverride,
        companyId: targetCompanyId,
        designation: hrmsDesignationFilter || null,
        department: hrmsDepartmentFilter || null,
      });
      // If successful, ensure flag is reset
      setHrmsNotConfigured(false);
      const syncedAt = res?.synced_at || new Date().toISOString();
      setLastSyncTime(syncedAt);
      localStorage.setItem('hrms_last_sync_time', syncedAt);
      const records = res?.data || res?.employees || res?.records || [];
      setHrmsRecords(Array.isArray(records) ? records : []);
      const meta = res?.meta || {};
      const totalPages = parseInt(meta.count ?? meta.total_pages ?? 0, 10) || 0;
      const currentPage = parseInt(meta.current_page ?? meta.page ?? page, 10) || page;
      const totalRecords = parseInt(meta.total ?? 0, 10) || (Array.isArray(records) ? records.length : 0);
      setHrmsMeta({ count: totalPages, current_page: currentPage, total: totalRecords });
      setHrmsPage(currentPage);
    } catch (e) {
      console.error('Company records search error:', e);
      const msg = String(e?.message || '').toLowerCase();
      if (msg.includes('kivo not configured')) {
        setHrmsNotConfigured(true);
        showSnackbar('Kivo not configured. Please connect with Kivo first.', 'warning');
      } else {
        showSnackbar(e.message || 'Failed to search company records', 'error');
      }
    } finally {
      setHrmsLoading(false);
    }
  };

  const handleClearHrmsSearch = () => {
    clearHrmsSearchDebounce();
    setHrmsQuery('');
    importHrms(1, false);
  };

  const handleHrmsLimitChange = (event) => {
    const newLimit = Number(event.target.value);
    setHrmsLimit(newLimit);
    if (hrmsQuery?.trim()) {
      searchHrms(1, null, newLimit);
    } else {
      importHrms(1, false, { listLimit: newLimit });
    }
  };

  const handleViewHrmsImages = (rec, name) => {
    setHrmsImageLoading(false);
    setHrmsImageDialog({
      open: true,
      name,
      images: {
        front: rec.front_picture_url || '',
        left: rec.left_picture_url || '',
        right: rec.right_picture_url || '',
        custom: rec.custom_angle_picture_url || '',
        customLabel: rec.custom_angle_label || 'Custom',
      },
    });
  };

  // Clean up company record image blob URLs when dialog closes.
  const handleCloseHrmsImageDialog = () => {
    if (hrmsImageDialog.images) {
      Object.values(hrmsImageDialog.images)
        .filter((imageUrl) => typeof imageUrl === 'string' && imageUrl.startsWith('blob:'))
        .forEach((imageUrl) => URL.revokeObjectURL(imageUrl));
    }
    setHrmsImageDialog({ open: false, images: null, name: '' });
  };


  // Mark already-enrolled CRM contacts and prevent re-enrollment
  useEffect(() => {
    const pids = (crmRecords || []).map(rec => String(rec.id || rec.person_id || rec.contact_id || ''))
      .filter(pid => pid);
    if (!pids.length) return;
    const toCheck = pids.filter(pid => enrolledByPid[pid] === undefined);
    if (!toCheck.length) return;
    let cancelled = false;
    (async () => {
      try {
        const results = await Promise.all(toCheck.map(pid => (
          checkPersonExists(pid, true).catch(() => ({ exists: false, deleted: false }))
        )));
        if (cancelled) return;
        const updates = {};
        toCheck.forEach((pid, idx) => { updates[pid] = results[idx]; });
        setEnrolledByPid(prev => ({ ...prev, ...updates }));
      } catch (_) {
        // ignore errors
      }
    })();
    return () => { cancelled = true; };
  }, [crmRecords, enrolledByPid]);

  // Mark already-enrolled company records without extra network calls.
  // Use server-provided `is_enrolled` from the company record response to update the local map.
  useEffect(() => {
    if (!Array.isArray(hrmsRecords) || hrmsRecords.length === 0) return;
    const updates = {};
    for (const rec of hrmsRecords) {
      const pid = String(rec.employee_id || rec.id || '');
      if (!pid) continue;
      if (enrolledByPid[pid] !== undefined) continue; // keep any known value
      if (typeof rec.is_enrolled === 'boolean') {
        updates[pid] = { exists: !!rec.is_enrolled, deleted: false };
      }
    }
    if (Object.keys(updates).length > 0) {
      setEnrolledByPid(prev => ({ ...prev, ...updates }));
    }
  }, [hrmsRecords, enrolledByPid]);

  // Auto-load company record data and sync status once when the tab is opened.
  // Also stop any polling when leaving the tab to avoid background calls.
  useEffect(() => {
    if (tab !== 'company_records' || !kivoFeatureEnabled) {
      if (hrmsSyncPollRef.current) {
        clearInterval(hrmsSyncPollRef.current);
        hrmsSyncPollRef.current = null;
      }
      return;
    }
    // Load current company records from local DB (no sync)
    // importHrms(1, false); // Removed: now handled by selectedCompanyId effect below
    // Fetch last sync status once (no company filter on initial load)
    getHrmsSyncStatus()
      .then((data) => {
        if (data?.success && data?.last_sync) {
          setSyncInfo(data.last_sync);
        }
      })
      .catch((error) => {
        console.error('Error fetching sync status:', error);
      });
  }, [tab, kivoFeatureEnabled]);

  // Load branches when company is selected in Company Records tab
  const loadHrmsBranches = useCallback(async (companyId) => {
    if (!companyId) {
      setHrmsFilterBranches([]);
      return;
    }
    try {
      const branchesData = await getKivoBranches(companyId);
      if (branchesData?.branches) {
        setHrmsFilterBranches(branchesData.branches.map(branch => ({
          branch_id: branch.branch_id,
          branch_name: branch.branch_name,
          branch_address: branch.branch_address || branch.branch_name,
          company_id: branch.company_id
        })));
      } else {
        setHrmsFilterBranches([]);
      }
    } catch (error) {
      console.error('Error loading HRMS branches:', error);
      setHrmsFilterBranches([]);
    }
  }, []);

  const loadHrmsDesignationOptions = useCallback(async (companyId, branchId) => {
    if (!companyId) {
      setHrmsDesignations([]);
      setHrmsDepartments([]);
      return;
    }
    try {
      const [designationRes, departmentRes] = await Promise.all([
        fetchOrgDesignations({
          permissions,
          companyId,
          branchId,
          includeInactive: false,
          limit: 200,
        }),
        fetchOrgDepartments({
          permissions,
          companyId,
          branchId,
          includeInactive: false,
          limit: 200,
        }),
      ]);
      const designationList = Array.isArray(designationRes?.designations) ? designationRes.designations : [];
      const departmentList = Array.isArray(departmentRes?.departments) ? departmentRes.departments : [];
      setHrmsDesignations(designationList);
      setHrmsDepartments(departmentList);
    } catch (error) {
      console.error('Error loading HRMS designation/department filters:', error);
      setHrmsDesignations([]);
      setHrmsDepartments([]);
    }
  }, [permissions]);

  useEffect(() => {
    if (tab !== 'company_records' || !kivoFeatureEnabled) return;
    if (!selectedCompanyId) {
      setHrmsDesignations([]);
      setHrmsDepartments([]);
      return;
    }
    const branchId = hrmsBranchFilter && hrmsBranchFilter.trim() ? hrmsBranchFilter : null;
    loadHrmsDesignationOptions(selectedCompanyId, branchId);
  }, [tab, selectedCompanyId, hrmsBranchFilter, loadHrmsDesignationOptions]);

  // Reload company records when company selection changes or tab becomes active
  useEffect(() => {
    if (kivoFeatureEnabled && tab === 'company_records' && selectedCompanyId) {
      loadHrmsBranches(selectedCompanyId);
      importHrms(1, false);
      // Fetch sync status for this specific company
      getHrmsSyncStatus(selectedCompanyId, hrmsBranchFilter)
        .then((data) => {
          if (data?.success && data?.last_sync) {
            setSyncInfo(data.last_sync);
          } else {
            setSyncInfo(null); // No sync found for this company
          }
        })
        .catch((error) => {
          console.error('Error fetching company sync status:', error);
        });
    } else if (kivoFeatureEnabled && tab === 'company_records' && !selectedCompanyId) {
      setHrmsFilterBranches([]);
      setHrmsBranchFilter('');
      setHrmsEmployeeNames([]); // Clear employee names when no company selected
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId, tab, loadHrmsBranches]);

  // Cleanup poller on unmount
  useEffect(() => {
    return () => {
      if (hrmsSyncPollRef.current) {
        clearInterval(hrmsSyncPollRef.current);
        hrmsSyncPollRef.current = null;
      }
    };
  }, []);

  const openEnrollFromCrm = async (rec) => {
    const name = rec.name || rec.full_name || rec.display || `${rec.first_name || ''} ${rec.last_name || ''}`.trim();
    const pidRaw = rec.id || rec.person_id || rec.contact_id || '';
    const pid = String(pidRaw);
    let existInfo = enrolledByPid[pid];
    if (!existInfo) {
      try {
        existInfo = await checkPersonExists(pid, true);
        setEnrolledByPid(prev => ({ ...prev, [pid]: existInfo }));
      } catch (_) {
        existInfo = null;
      }
    }
    if (existInfo && existInfo.exists && !existInfo.deleted) {
      // Already enrolled: offer to add another angle
      setAddAngleDialog({ open: true, name, personId: pid });
    } else {
      setEnrollDialog({ open: true, record: rec, name, personId: pid });
    }
    setSelectedFile(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    // Do not auto-start camera; only start when user clicks "Start Camera"
  };

  const openEnrollFromHrms = async (rec) => {
    const name = rec.name || rec.full_name || `${rec.first_name || ''} ${rec.last_name || ''}`.trim();
    const pidRaw = rec.employee_id || rec.id || rec.person_id || '';
    const pid = String(pidRaw);
    let existInfo = enrolledByPid[pid];
    if (!existInfo) {
      try {
        existInfo = await checkPersonExists(pid, true);
        setEnrolledByPid(prev => ({ ...prev, [pid]: existInfo }));
      } catch (_) {
        existInfo = null;
      }
    }
    if (existInfo && existInfo.exists && !existInfo.deleted) {
      // Already enrolled: offer to add another angle
      setAddAngleDialog({ open: true, name, personId: pid });
    } else {
      setEnrollDialog({ open: true, record: rec, name, personId: pid });
    }
    setSelectedFile(null);
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const closeEnrollDialog = () => {
    setEnrollDialog({ open: false, record: null, name: '', personId: '' });
    // Clear enroll angle state and previews
    [enrollFrontPreview, enrollLeftPreview, enrollRightPreview, enrollCustomPreview].forEach((u) => { try { if (u) URL.revokeObjectURL(u); } catch (_) { } });
    setEnrollFrontPreview(null); setEnrollLeftPreview(null); setEnrollRightPreview(null); setEnrollCustomPreview(null);
    setEnrollFrontFile(null); setEnrollLeftFile(null); setEnrollRightFile(null); setEnrollCustomFile(null);
    setEnrollCustomLabel('');
    setEnrollActiveAngle('front');
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.srcObject = null; }
    setIsCameraOn(false);
  };

  // eslint-disable-next-line no-unused-vars
  const openAddAngleDialog = (face) => { };

  const closeAddAngleDialog = () => {
    setAddAngleDialog({ open: false, name: '', personId: '' });
    if (previewUrl) { URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
    if (videoRef.current) { videoRef.current.pause(); videoRef.current.srcObject = null; }
    setIsCameraOn(false);
  };

  const submitEnroll = async () => {
    try {
      if (!enrollDialog.name || !enrollDialog.personId) {
        showSnackbar('Name and Person ID are required', 'warning');
        return;
      }
      // Require front image
      if (!enrollFrontFile && !enrollFrontPreview) {
        showSnackbar('Front profile is required', 'warning');
        return;
      }

      const toDataUrl = (objUrlOrFile) => new Promise((resolve, reject) => {
        if (!objUrlOrFile) return resolve(null);
        if (objUrlOrFile instanceof File) {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(objUrlOrFile);
        } else if (typeof objUrlOrFile === 'string' && objUrlOrFile.startsWith('blob:')) {
          // Fetch blob and convert to data URL
          fetch(objUrlOrFile).then(res => res.blob()).then(blob => {
            const fr = new FileReader();
            fr.onload = () => resolve(fr.result);
            fr.onerror = reject;
            fr.readAsDataURL(blob);
          }).catch(reject);
        } else {
          resolve(null);
        }
      });

      // Build images set
      const frontDataUrl = await toDataUrl(enrollFrontFile || enrollFrontPreview);
      const leftDataUrl = await toDataUrl(enrollLeftFile || enrollLeftPreview);
      const rightDataUrl = await toDataUrl(enrollRightFile || enrollRightPreview);
      const customDataUrl = await toDataUrl(enrollCustomFile || enrollCustomPreview);

      if (customDataUrl && !enrollCustomLabel.trim()) {
        showSnackbar('Please provide a label for the custom angle image.', 'warning');
        return;
      }

      // Submit sequentially (front mandatory)
      const results = [];
      if (frontDataUrl) {
        results.push(await registerFaceBase64(enrollDialog.name, enrollDialog.personId, frontDataUrl, 'jpeg'));
      }
      if (leftDataUrl) {
        results.push(await registerFaceBase64(enrollDialog.name, enrollDialog.personId, leftDataUrl, 'jpeg'));
      }
      if (rightDataUrl) {
        results.push(await registerFaceBase64(enrollDialog.name, enrollDialog.personId, rightDataUrl, 'jpeg'));
      }
      if (customDataUrl) {
        results.push(await registerFaceBase64(
          enrollDialog.name,
          enrollDialog.personId,
          customDataUrl,
          'jpeg',
          'custom_angle',
          enrollCustomLabel.trim()
        ));
      }

      const ok = results.every(r => r && r.success);
      if (ok) {
        showSnackbar('Enrolled with angles successfully', 'success');
        closeEnrollDialog();
        loadFaces(pagination.limit, pagination.offset);
      } else {
        const firstErr = results.find(r => !r?.success)?.message || 'One or more enrollments failed';
        showSnackbar(firstErr, 'warning');
        loadFaces(pagination.limit, pagination.offset);
      }
    } catch (e) {
      console.error('Enroll error:', e);
      showSnackbar(e.message || 'Failed to enroll', 'error');
    }
  };

  const onEnrollFileChange = (angle, e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    if (angle === 'front') { if (enrollFrontPreview) URL.revokeObjectURL(enrollFrontPreview); setEnrollFrontFile(file); setEnrollFrontPreview(url); }
    if (angle === 'left') { if (enrollLeftPreview) URL.revokeObjectURL(enrollLeftPreview); setEnrollLeftFile(file); setEnrollLeftPreview(url); }
    if (angle === 'right') { if (enrollRightPreview) URL.revokeObjectURL(enrollRightPreview); setEnrollRightFile(file); setEnrollRightPreview(url); }
    if (angle === 'custom') { if (enrollCustomPreview) URL.revokeObjectURL(enrollCustomPreview); setEnrollCustomFile(file); setEnrollCustomPreview(url); }
    e.target.value = null;
  };

  const capturePhotoForEnroll = () => {
    if (!videoRef.current) return;
    const video = videoRef.current;
    const canvas = document.createElement('canvas');
    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob((blob) => {
      if (!blob) return;
      const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
      const url = URL.createObjectURL(blob);
      if (enrollActiveAngle === 'front') { if (enrollFrontPreview) URL.revokeObjectURL(enrollFrontPreview); setEnrollFrontFile(file); setEnrollFrontPreview(url); }
      if (enrollActiveAngle === 'left') { if (enrollLeftPreview) URL.revokeObjectURL(enrollLeftPreview); setEnrollLeftFile(file); setEnrollLeftPreview(url); }
      if (enrollActiveAngle === 'right') { if (enrollRightPreview) URL.revokeObjectURL(enrollRightPreview); setEnrollRightFile(file); setEnrollRightPreview(url); }
      if (enrollActiveAngle === 'custom') { if (enrollCustomPreview) URL.revokeObjectURL(enrollCustomPreview); setEnrollCustomFile(file); setEnrollCustomPreview(url); }
    }, 'image/jpeg', 0.92);
  };

  const submitAddAngle = async () => {
    try {
      if (!addAngleDialog.name || !addAngleDialog.personId) {
        showSnackbar('Name and Person ID are required', 'warning');
        return;
      }
      let dataUrl = null;
      if (previewUrl && previewUrl.startsWith('blob:')) {
        const canvas = document.createElement('canvas');
        const v = videoRef.current;
        if (v && v.videoWidth) {
          canvas.width = v.videoWidth; canvas.height = v.videoHeight;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
          dataUrl = canvas.toDataURL('image/jpeg', 0.92);
        }
      }
      if (!dataUrl && selectedFile) {
        dataUrl = await new Promise((resolve, reject) => {
          const r = new FileReader();
          r.onload = () => resolve(r.result);
          r.onerror = reject;
          r.readAsDataURL(selectedFile);
        });
      }
      if (!dataUrl && videoRef.current && streamRef.current) {
        const canvas = document.createElement('canvas');
        const v = videoRef.current;
        canvas.width = v.videoWidth || 640; canvas.height = v.videoHeight || 480;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(v, 0, 0, canvas.width, canvas.height);
        dataUrl = canvas.toDataURL('image/jpeg', 0.92);
      }
      if (!dataUrl) {
        showSnackbar('Please capture or choose an image', 'warning');
        return;
      }
      const res = await registerFaceBase64(addAngleDialog.name, addAngleDialog.personId, dataUrl, 'jpeg');
      if (res && res.success) {
        showSnackbar('Angle added successfully', 'success');
        closeAddAngleDialog();
        loadFaces(pagination.limit, pagination.offset);
        handleRefresh();
      } else {
        showSnackbar(res?.message || 'Failed to add angle', 'error');
      }
    } catch (e) {
      console.error('Add-angle error:', e);
      showSnackbar(e.message || 'Failed to add angle', 'error');
    }
  };

  const getQualityColor = (quality) => {
    if (quality >= 0.9) return '#138636';
    if (quality >= 0.7) return '#FFBC11';
    return '#C91432';
  };

  const playVoice = async (employeeId, voiceType) => {
    // Prevent multiple simultaneous playback
    if (playingVoice) {
      showSnackbar('Please wait for the current voice to finish playing', 'warning');
      return;
    }

    try {
      console.log(`Playing ${voiceType} voice for employee ${employeeId}...`);

      // Set playing state immediately
      setPlayingVoice({ employeeId, voiceType });

      const token = getStoredToken();
      console.log('Token available:', !!token);

      if (!token) {
        setPlayingVoice(null);
        throw new Error('Authentication required - please login again');
      }

      // Fetch voice file from authenticated API endpoint using configured base URL
      const response = await fetch(`${baseURL}/api/v1/voice/${employeeId}/${voiceType}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'audio/wav'
        }
      });

      console.log('Response status:', response.status, 'Content-Type:', response.headers.get('content-type'));

      if (!response.ok) {
        setPlayingVoice(null);
        if (response.status === 401) {
          throw new Error('Session expired - please login again');
        }
        if (response.status === 404) {
          throw new Error('Voice file not found');
        }
        const errorText = await response.text();
        console.error('Voice fetch error:', response.status, errorText);
        throw new Error(`Failed to load voice: ${response.status}`);
      }

      // Check content type - should be audio/wav, not JSON
      const contentType = response.headers.get('content-type');
      if (contentType && contentType.includes('application/json')) {
        setPlayingVoice(null);
        const errorJson = await response.json();
        throw new Error(errorJson.detail || 'Server returned error');
      }

      // Get the array buffer and create blob with explicit WAV MIME type
      const arrayBuffer = await response.arrayBuffer();

      if (arrayBuffer.byteLength < 100) {
        setPlayingVoice(null);
        throw new Error('Voice file is empty or corrupted');
      }

      const blob = new Blob([arrayBuffer], { type: 'audio/wav' });
      const audioUrl = URL.createObjectURL(blob);

      console.log(' Audio blob created, size:', blob.size, 'bytes');

      const audio = new Audio();

      // Set up event handlers before setting src
      audio.onloadedmetadata = () => {
        console.log('Audio loaded! Duration:', audio.duration, 'seconds');
      };

      audio.oncanplaythrough = async () => {
        try {
          await audio.play();
          showSnackbar(`Playing ${voiceType} voice`, 'info');
        } catch (playError) {
          console.error('Play error:', playError);
          setPlayingVoice(null);
          showSnackbar(`Failed to play: ${playError.message}`, 'error');
        }
      };

      audio.onended = () => {
        console.log('Playback completed');
        URL.revokeObjectURL(audioUrl);
        setPlayingVoice(null); // Reset playing state when audio ends
      };

      audio.onerror = (err) => {
        console.error('Audio error:', err, audio.error);
        URL.revokeObjectURL(audioUrl);
        setPlayingVoice(null); // Reset playing state on error
        showSnackbar(`Failed to play voice: ${audio.error?.message || 'Unknown error'}`, 'error');
      };

      // Set source after event handlers
      audio.src = audioUrl;
      audio.load();

    } catch (error) {
      console.error('Error:', error);
      setPlayingVoice(null);
      showSnackbar(`Failed to play voice: ${error.message}`, 'error');
    }
  };

  // Voice Modal Functions
  const openVoiceModal = (employeeId, employeeName, personId = null, mode = 'add') => {
    setVoiceModalEmployee({ employeeId, employeeName, personId, mode });
    setCheckinVoiceBlob(null);
    setCheckoutVoiceBlob(null);
    setIsRecordingCheckin(false);
    setIsRecordingCheckout(false);
    setVoiceModalOpen(true);
  };

  const closeVoiceModal = () => {
    // Stop any active recording
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
    setVoiceModalOpen(false);
    setVoiceModalEmployee(null);
    setCheckinVoiceBlob(null);
    setCheckoutVoiceBlob(null);
    setIsRecordingCheckin(false);
    setIsRecordingCheckout(false);
  };

  const loadSavedAIVoiceKeys = async (autoSelectDefault = false) => {
    setAiVoiceLoadingKeys(true);
    try {
      const res = await listAIVoiceApiKeys();
      const keys = Array.isArray(res?.data) ? res.data : [];
      setAiVoiceSavedKeys(keys);
      if (autoSelectDefault) {
        const defaultKey = getDefaultAIVoiceKeySelection(keys);
        if (defaultKey) {
          setAiVoiceSelectedKeyId(defaultKey.credentialId);
          setAiVoiceApiKeyInput(defaultKey.hint);
        }
      }
      return keys;
    } catch (err) {
      console.error('Failed to load saved AI voice keys:', err);
      setAiVoiceSavedKeys([]);
      return [];
    } finally {
      setAiVoiceLoadingKeys(false);
    }
  };

  const openAIVoiceDialog = async (voiceType) => {
    if (!voiceModalEmployee) return;
    const employeeName = voiceModalEmployee?.employeeName || 'Employee';
    const defaultText = voiceType === 'checkout' ? `Goodbye ${employeeName}` : `Welcome ${employeeName}`;
    setAiVoiceType(voiceType);
    setAiVoiceText(defaultText);
    setAiVoiceApiKeyInput('');
    setAiVoiceSelectedKeyId('');
    setAiVoiceDialogOpen(true);
    await loadSavedAIVoiceKeys(true);
  };

  const closeAIVoiceDialog = () => {
    setAiVoiceDialogOpen(false);
    setAiVoiceApiKeyInput('');
    setAiVoiceSelectedKeyId('');
    setAiVoiceText('');
  };

  const base64ToWavBlob = (base64Audio) => {
    const byteChars = window.atob(base64Audio);
    const byteNumbers = new Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i += 1) {
      byteNumbers[i] = byteChars.charCodeAt(i);
    }
    return new Blob([new Uint8Array(byteNumbers)], { type: 'audio/wav' });
  };

  const handleFileUpload = (event, voiceType) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Strict .wav file validation - check both extension and MIME type
    const fileName = file.name.toLowerCase();
    const isWavExtension = fileName.endsWith('.wav') || fileName.endsWith('.wave');
    const isWavMimeType = file.type === 'audio/wav' || file.type === 'audio/wave' || file.type === 'audio/x-wav';

    if (!isWavExtension) {
      showSnackbar('Please upload a .wav extension audio file only', 'error');
      event.target.value = ''; // Reset input
      return;
    }

    if (voiceType === 'checkin') {
      setCheckinVoiceBlob(file);
    } else {
      setCheckoutVoiceBlob(file);
    }
    showSnackbar(`${voiceType === 'checkin' ? 'IN' : 'OUT'} voice file selected`, 'success');
  };

  const startRecording = async (voiceType) => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // Let browser choose supported mimeType (Safari doesn't support webm)
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());

        // Create blob from recorded chunks
        const audioBlob = new Blob(audioChunksRef.current, { type: mediaRecorder.mimeType });

        try {
          const wavBlob = await convertToWav(audioBlob);
          if (voiceType === 'checkin') {
            setCheckinVoiceBlob(wavBlob);
            setIsRecordingCheckin(false);
          } else {
            setCheckoutVoiceBlob(wavBlob);
            setIsRecordingCheckout(false);
          }
          showSnackbar(`${voiceType === 'checkin' ? 'IN' : 'OUT'} voice recorded successfully`, 'success');
        } catch (err) {
          console.error('Error converting to WAV:', err);
          showSnackbar('Failed to process recording', 'error');
          if (voiceType === 'checkin') {
            setIsRecordingCheckin(false);
          } else {
            setIsRecordingCheckout(false);
          }
        }
      };

      // Start recording with timeslice for better performance
      mediaRecorder.start(100);
      if (voiceType === 'checkin') {
        setIsRecordingCheckin(true);
      } else {
        setIsRecordingCheckout(true);
      }
    } catch (err) {
      console.error('Error accessing microphone:', err);
      showSnackbar('Could not access microphone. Please allow microphone permission.', 'error');
    }
  };

  const stopRecording = (voiceType) => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state === 'recording') {
      mediaRecorderRef.current.stop();
    }
  };

  // Convert audio blob to wav blob using audiobuffer-to-wav library
  const convertToWav = async (audioBlob) => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const arrayBuffer = await audioBlob.arrayBuffer();
    const audioBuffer = await audioContext.decodeAudioData(arrayBuffer);

    // Use the optimized library for WAV conversion
    const wavBuffer = toWav(audioBuffer);
    audioContext.close(); // Clean up
    return new Blob([wavBuffer], { type: 'audio/wav' });
  };

  // Create audio URLs when blobs change
  React.useEffect(() => {
    if (checkinVoiceBlob) {
      const url = URL.createObjectURL(checkinVoiceBlob);
      setCheckinAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setCheckinAudioUrl(null);
    }
  }, [checkinVoiceBlob]);

  React.useEffect(() => {
    if (checkoutVoiceBlob) {
      const url = URL.createObjectURL(checkoutVoiceBlob);
      setCheckoutAudioUrl(url);
      return () => URL.revokeObjectURL(url);
    } else {
      setCheckoutAudioUrl(null);
    }
  }, [checkoutVoiceBlob]);

  const saveVoices = async () => {
    if (!voiceModalEmployee) return;

    const { employeeId, personId } = voiceModalEmployee;
    const targetId = personId || employeeId;

    if (!checkinVoiceBlob && !checkoutVoiceBlob) {
      showSnackbar('Please upload or record at least one voice', 'warning');
      return;
    }

    setVoiceUploading(true);
    try {
      let successCount = 0;

      if (checkinVoiceBlob) {
        await uploadEmployeeVoice(targetId, 'checkin', checkinVoiceBlob);
        successCount++;
      }

      if (checkoutVoiceBlob) {
        await uploadEmployeeVoice(targetId, 'checkout', checkoutVoiceBlob);
        successCount++;
      }

      showSnackbar(`${successCount} voice(s) uploaded successfully`, 'success');
      closeVoiceModal();

      // Refresh the data
      if (tab === 'enrolled') {
        loadFaces(pagination.limit, pagination.offset, enrolledQuery);
      } else if (tab === 'company') {
        if (hrmsQuery?.trim()) {
          searchHrms(hrmsMeta.current_page);
        } else {
          importHrms(hrmsMeta.current_page, false);
        }
      }
    } catch (err) {
      console.error('Error uploading voice:', err);
      showSnackbar(`Failed to upload voice: ${err.message}`, 'error');
    } finally {
      setVoiceUploading(false);
    }
  };

  const handleGenerateAIVoice = async () => {
    if (!voiceModalEmployee) return;
    const { employeeId, personId } = voiceModalEmployee;
    const targetId = personId || employeeId;
    const trimmedKey = aiVoiceApiKeyInput.trim();
    const trimmedText = aiVoiceText.trim();

    if (!aiVoiceSelectedKeyId && !trimmedKey) {
      showSnackbar('Please select a saved key or enter a new API key', 'warning');
      return;
    }

    setAiVoiceGenerating(true);
    try {
      let payload = {};
      if (aiVoiceSelectedKeyId) {
        payload = {
          credential_id: aiVoiceSelectedKeyId,
          provider: 'cartesia',
          credential_type: 'voice_generation',
          text: trimmedText,
        };
      } else {
        const saveRes = await saveAIVoiceApiKey(trimmedKey);
        const savedCredentialId = saveRes?.data?.credential_id;
        if (savedCredentialId) {
          payload = {
            credential_id: savedCredentialId,
            provider: 'cartesia',
            credential_type: 'voice_generation',
            text: trimmedText,
          };
        } else {
          payload = {
            api_key: trimmedKey,
            provider: 'cartesia',
            credential_type: 'voice_generation',
            text: trimmedText,
          };
        }
      }

      payload.save_to_db = false;
      const res = await generateEmployeeAIVoice(targetId, aiVoiceType, payload);
      const audioBase64 = res?.audio_base64;
      if (!audioBase64) {
        throw new Error('Generated voice preview not received');
      }
      const wavBlob = base64ToWavBlob(audioBase64);
      if (aiVoiceType === 'checkin') {
        setCheckinVoiceBlob(wavBlob);
      } else {
        setCheckoutVoiceBlob(wavBlob);
      }
      showSnackbar(`${aiVoiceType === 'checkin' ? 'IN' : 'OUT'} voice generated. Click Save Voices to persist.`, 'success');
      closeAIVoiceDialog();

      if (tab === 'enrolled') {
        loadFaces(pagination.limit, pagination.offset, enrolledQuery);
      } else if (tab === 'company') {
        if (hrmsQuery?.trim()) {
          searchHrms(hrmsMeta.current_page);
        } else {
          importHrms(hrmsMeta.current_page, false);
        }
      }
    } catch (err) {
      console.error('AI voice generation failed:', err);
      showSnackbar(`Failed to generate AI voice: ${err.message}`, 'error');
    } finally {
      setAiVoiceGenerating(false);
    }
  };

  const handleOpenFaceGallery = async (person, face) => {
    if (!face?.face_id) return;

    try {
      const faces = Array.isArray(person?.faces)
        ? person.faces.filter((item) => !item.deleted_at)
        : [];

      const imageMap = { ...faceImages };

      const missingFaces = faces.filter(
        (item) => !imageMap[item.face_id]
      );

      const loadedImages = await Promise.all(
        missingFaces.map(async (item) => {
          try {
            const blob = await getFaceImage(item.face_id);

            return {
              faceId: item.face_id,
              imageUrl: URL.createObjectURL(blob),
            };
          } catch (error) {
            console.error(
              'Error loading face:',
              item.face_id,
              error
            );
            return null;
          }
        })
      );

      const newlyLoaded = {};

      loadedImages.forEach((result) => {
        if (!result) return;

        imageMap[result.faceId] = result.imageUrl;
        newlyLoaded[result.faceId] = result.imageUrl;
      });

      if (Object.keys(newlyLoaded).length > 0) {
        setFaceImages((prev) => ({
          ...prev,
          ...newlyLoaded,
        }));
      }

      const images = faces
        .map((item) => {
          const src = imageMap[item.face_id];
          if (!src) return null;

          const label =
            item.face_angle || item.name || 'Face';

          return {
            src,
            title: label,
            alt: `Face - ${label}`,
          };
        })
        .filter(Boolean);

      const clickedSrc = imageMap[face.face_id];

      if (!clickedSrc || images.length === 0) return;

      const index = images.findIndex(
        (item) => item.src === clickedSrc
      );

      const frontFace = faces.find(
        (item) => item.face_angle === 'front'
      );

      openImagePreview({
        src: clickedSrc,
        title: face.face_angle || face.name || 'Face Image',
        alt: `Face - ${face.face_angle || face.name || 'Face'}`,
        images,
        index: Math.max(0, index),
        employeeName: person?.name || 'Unknown person',
        employeeId:
          person?.employee_code || person?.person_id || '',
        employeeImage:
          (frontFace && imageMap[frontFace.face_id]) ||
          images[0]?.src ||
          '',
      });
    } catch (error) {
      console.error('Error opening face gallery:', error);
    }
  };

  const hasActiveEnrolledFilters = useMemo(() => (
    !!(enrolledQuery?.trim()) ||
    !!enrolledCompanyFilter ||
    !!enrolledBranchFilter ||
    !!enrolledDesignationFilter ||
    !!enrolledDepartmentFilter ||
    showDeleted
  ), [
    enrolledBranchFilter,
    enrolledCompanyFilter,
    enrolledDepartmentFilter,
    enrolledDesignationFilter,
    enrolledQuery,
    showDeleted,
  ]);

  const hasActiveCompanyFilters = useMemo(() => (
    !!(hrmsQuery?.trim()) ||
    !!hrmsBranchFilter ||
    !!hrmsDesignationFilter ||
    !!hrmsDepartmentFilter
  ), [hrmsBranchFilter, hrmsDepartmentFilter, hrmsDesignationFilter, hrmsQuery]);

  const handleClearAllFilters = useCallback(() => {
    if (tab === 'enrolled') {
      clearEnrolledSearchDebounce();
      setEnrolledQuery('');
      setEnrolledCompanyFilter('');
      setEnrolledBranchFilter('');
      setEnrolledDesignationFilter('');
      setEnrolledDepartmentFilter('');
      setEnrolledSearchPersonId(null);
      setShowDeleted(false);
      enrolledCompanyTouchedRef.current = true;
      loadFaces(pagination.limit, 0, '', null, '', '', '', '');
      return;
    }
    if (tab === 'company') {
      clearHrmsSearchDebounce();
      setHrmsQuery('');
      setHrmsBranchFilter('');
      setHrmsDesignationFilter('');
      setHrmsDepartmentFilter('');
      importHrms(1, false);
    }
  }, [
    clearEnrolledSearchDebounce,
    clearHrmsSearchDebounce,
    importHrms,
    loadFaces,
    pagination.limit,
    tab,
  ]);

  const personSearchQueryParams = useMemo(() => ({
    offset: 0,
    include_deleted: showDeleted === true ? 'true' : 'false',
    is_intrusion: 'false',
    is_visitor: 'false',
    is_vendor: 'false',
    ...(enrolledCompanyFilter ? { company_id: enrolledCompanyFilter } : {}),
    ...(enrolledBranchFilter ? { branch_id: enrolledBranchFilter } : {}),
    ...(enrolledDesignationFilter ? { designation: enrolledDesignationFilter } : {}),
    ...(enrolledDepartmentFilter ? { department: enrolledDepartmentFilter } : {}),
  }), [
    showDeleted,
    enrolledCompanyFilter,
    enrolledBranchFilter,
    enrolledDesignationFilter,
    enrolledDepartmentFilter,
  ]);

  return (
    <Box>
      <Container maxWidth="lg">
        <PageHeader
          title="Employee Management"
          subtitle="Manage employee face records and information"
        />

        {/* Filters Section - Above Tabs */}
        <Box sx={pageFiltersToolbarSx}>
          <Stack {...pageFiltersStackSx}>
            <Box sx={pageFiltersControlsSx}>
              {tab === 'enrolled' && (
                <>
                  {/* Enrolled Tab Filters */}
                  <PersonSearch
                    queryParams={personSearchQueryParams}
                    onSelect={(emp) => {
                      if (!emp || !emp.person_id) return;
                      setEnrolledSearchPersonId(emp.person_id);
                      loadFaces(pagination.limit, 0, undefined, emp.person_id);
                    }}
                    onQueryChange={() => {
                      if (enrolledSearchPersonId) {
                        loadFaces(pagination.limit, 0, undefined, null);
                      }
                      setEnrolledSearchPersonId(null);
                    }}
                  />

                  <FormControl
                    sx={{
                      minWidth: { xs: '100%', sm: 150 },
                      flex: { xs: 1, sm: .5 }
                    }}
                    size="small"
                  >
                    <InputLabel
                      id="company-select-label"
                      shrink={enrolledCompanyFilter === '' ? true : undefined}
                    >
                      Company
                    </InputLabel>
                    <Select
                      labelId="company-select-label"
                      value={enrolledCompanyFilter}
                      label="Company"
                      displayEmpty
                      renderValue={(value) =>
                        renderSelectValueWithTooltip(value ? selectedCompanyName : 'All Companies')
                      }
                      onChange={(e) => {
                        clearEnrolledSearchDebounce();
                        enrolledCompanyTouchedRef.current = true;
                        setEnrolledCompanyFilter(e.target.value);
                        setEnrolledBranchFilter('');
                        setEnrolledDesignationFilter('');
                        setEnrolledDepartmentFilter('');
                        setEnrolledDesignations([]);
                        setEnrolledDepartments([]);
                        loadFaces(pagination.limit, 0, enrolledQuery, null, e.target.value, enrolledBranchFilter, enrolledDesignationFilter, enrolledDepartmentFilter);
                      }}
                      MenuProps={{
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        },
                        getContentAnchorEl: null, // For MUI v4; remove for MUI v5+
                        PaperProps: {
                          sx: {
                            mt: 1, // margin-top below the select field
                            minWidth: 'unset',
                            flex: { xs: 1, sm: .5 }
                          }
                        }
                      }}
                    >
                      <SelectMenuItem value="">All Companies</SelectMenuItem>
                      {enrolledCompanyFilter && !enrolledCompanies.some(
                        (company) => String(company.company_id) === String(enrolledCompanyFilter)
                      ) && (
                          <SelectMenuItem value={enrolledCompanyFilter}>
                            {selectedCompanyName}
                          </SelectMenuItem>
                        )}
                      {enrolledCompanies.map((company) => (
                        <SelectMenuItem key={company.company_id} value={company.company_id}>
                          {renderMenuItemLabel(company.company_name)}
                        </SelectMenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    sx={{
                      minWidth: { xs: '100%', sm: 150 },
                      flex: { xs: 1, sm: .5 },
                      width: { xs: '100%' }, // Ensure FormControl takes full width on mobile
                    }}
                    size="small"
                  >
                    <InputLabel shrink={enrolledBranchFilter === '' ? true : undefined}>
                      Branch
                    </InputLabel>
                    <Select
                      value={enrolledBranchFilter}
                      label="Branch"
                      displayEmpty
                      renderValue={(value) =>
                        renderSelectValueWithTooltip(value ? selectedBranchName : 'All Branches')
                      }
                      onChange={(e) => {
                        clearEnrolledSearchDebounce();
                        setEnrolledBranchFilter(e.target.value);
                        setEnrolledDesignationFilter('');
                        setEnrolledDepartmentFilter('');
                        loadFaces(pagination.limit, 0, enrolledQuery, null, enrolledCompanyFilter, e.target.value, enrolledDesignationFilter, enrolledDepartmentFilter);
                      }}
                      disabled={enrolledCompanyFilter ? enrolledBranches.filter(b => b.company_id === enrolledCompanyFilter).length === 0 : enrolledBranches.length === 0}
                      MenuProps={!isMobile ? {
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        },
                        getContentAnchorEl: null, // For MUI v4; remove for MUI v5+
                        PaperProps: {
                          sx: {
                            mt: 1, // margin-top below the select field
                            minWidth: { xs: '100%', sm: 0 }, // Ensures dropdown matches parent width on mobile
                            width: { xs: '100%', sm: 'auto' }, // Match parent width on mobile
                            maxWidth: {
                              xs: '100%', // Full width on mobile
                              sm: 180,
                              md: 280,
                              lg: 340,
                              xl: 420
                            },
                            flex: { xs: 1, sm: .5 }
                          }
                        }
                      } : {
                        PaperProps: {
                          sx: {
                            mt: 1,
                            maxHeight: { xs: 280, sm: 360 },
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: 520 },
                          }
                        },
                        MenuListProps: {
                          dense: isMobile,
                          sx: { py: 0 },
                        },
                      }
                      }
                      sx={{
                        width: { xs: '100%' }, // Make Select full width on mobile
                        minWidth: { xs: '100%', sm: 0 }
                      }}
                    >
                      <SelectMenuItem value="">All Branches</SelectMenuItem>
                      {enrolledBranches
                        .filter((branch) => !enrolledCompanyFilter || branch.company_id === enrolledCompanyFilter)
                        .map((branch) => {
                          const companyName = enrolledCompanies.find((c) => String(c.company_id) === String(branch.company_id))?.company_name;
                          const label = enrolledCompanyFilter
                            ? branch.branch_address
                            : `${branch.branch_address}${companyName ? ` (${companyName})` : ''}`;
                          return (
                            <SelectMenuItem key={branch.branch_id} value={branch.branch_id}>
                              {renderMenuItemLabel(label)}
                            </SelectMenuItem>
                          );
                        })}
                    </Select>
                  </FormControl>

                  <FormControl
                    sx={{
                      minWidth: { xs: '100%', sm: 170 },
                      flex: { xs: 1, sm: .5 },
                      width: { xs: '100%' },
                    }}
                    size="small"
                  >
                    <InputLabel shrink={enrolledDesignationFilter === '' ? true : undefined}>
                      Designation
                    </InputLabel>
                    <Select
                      value={enrolledDesignationFilter}
                      label="Designation"
                      displayEmpty
                      renderValue={(value) =>
                        renderSelectValueWithTooltip(value ? value : 'All Designations')
                      }
                      onChange={(e) => {
                        clearEnrolledSearchDebounce();
                        setEnrolledDesignationFilter(e.target.value);
                        loadFaces(
                          pagination.limit,
                          0,
                          enrolledQuery,
                          null,
                          enrolledCompanyFilter,
                          enrolledBranchFilter,
                          e.target.value,
                          enrolledDepartmentFilter
                        );
                      }}
                      disabled={enrolledDesignations.length === 0}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 1,
                            maxHeight: { xs: 280, sm: 360 },
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: 520 },
                          },
                        },
                        MenuListProps: {
                          dense: isMobile,
                          sx: { py: 0 },
                        },
                      }}
                      sx={{
                        width: { xs: '100%' },
                        minWidth: { xs: '100%', sm: 0 },
                        '& .MuiSelect-select': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    >
                      <SelectMenuItem value="">All Designations</SelectMenuItem>
                      {enrolledDesignations.map((designation) => (
                        <SelectMenuItem
                          key={orgMasterOptionId(designation)}
                          value={orgMasterOptionId(designation)}
                        >
                          {renderMenuItemLabel(orgMasterOptionLabel(designation))}
                        </SelectMenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    sx={{
                      minWidth: { xs: '100%', sm: 170 },
                      flex: { xs: 1, sm: .5 },
                      width: { xs: '100%' },
                    }}
                    size="small"
                  >
                    <InputLabel shrink={enrolledDepartmentFilter === '' ? true : undefined}>
                      Department
                    </InputLabel>
                    <Select
                      value={enrolledDepartmentFilter}
                      label="Department"
                      displayEmpty
                      renderValue={(value) =>
                        renderSelectValueWithTooltip(value ? value : 'All Departments')
                      }
                      onChange={(e) => {
                        clearEnrolledSearchDebounce();
                        setEnrolledDepartmentFilter(e.target.value);
                        loadFaces(
                          pagination.limit,
                          0,
                          enrolledQuery,
                          null,
                          enrolledCompanyFilter,
                          enrolledBranchFilter,
                          enrolledDesignationFilter,
                          e.target.value
                        );
                      }}
                      disabled={enrolledDepartments.length === 0}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 1,
                            maxHeight: { xs: 280, sm: 360 },
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: 520 },
                          },
                        },
                        MenuListProps: {
                          dense: isMobile,
                          sx: { py: 0 },
                        },
                      }}
                      sx={{
                        width: { xs: '100%' },
                        minWidth: { xs: '100%', sm: 0 },
                        '& .MuiSelect-select': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    >
                      <SelectMenuItem value="">All Departments</SelectMenuItem>
                      {enrolledDepartments.map((department) => (
                        <SelectMenuItem
                          key={orgMasterOptionId(department)}
                          value={orgMasterOptionId(department)}
                        >
                          {renderMenuItemLabel(orgMasterOptionLabel(department))}
                        </SelectMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}

              {kivoFeatureEnabled && tab === 'company_records' && (
                <>
                  {/* Company Records Tab Filters */}
                  <Autocomplete
                    sx={pageFilterAutocompleteSx(180)}
                    freeSolo
                    size="small"
                    options={hrmsEmployeeNames}
                    getOptionLabel={(option) => typeof option === 'string' ? option : `${option.name || ''}`}
                    value={hrmsQuery || null}
                    inputValue={hrmsQuery}
                    onInputChange={(event, newInputValue, reason) => {
                      // Clear icon fires both onInputChange(clear) and onChange(clear). Only reset text here;
                      // importHrms runs once from onChange below.
                      if (reason === 'clear') {
                        clearHrmsSearchDebounce();
                        setHrmsQuery('');
                        return;
                      }
                      const nextQuery = sanitizeNameSearchInput(newInputValue || '');
                      setHrmsQuery(nextQuery);
                      if (reason === 'reset') return;
                    }}
                    onChange={(event, newValue, reason) => {
                      if (reason === 'clear') {
                        handleClearHrmsSearch();
                        return;
                      }
                      clearHrmsSearchDebounce();
                      const query = sanitizeNameSearchInput(
                        typeof newValue === 'string' ? newValue : (newValue ? newValue.name : '')
                      );
                      setHrmsQuery(query);
                      if (query && query.trim()) {
                        searchHrms(1, query);
                      } else {
                        importHrms(1, false);
                      }
                    }}
                    renderInput={(params) => (
                      <Tooltip
                        title={hrmsQuery || ''}
                        arrow
                        placement="bottom"
                        PopperProps={{ disablePortal: true }}
                        disableHoverListener={!hrmsQuery}
                      >
                        <TextField
                          {...params}
                          label="Search by Name"
                          InputLabelProps={{ shrink: true }}
                          variant="outlined"
                          size="small"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              clearHrmsSearchDebounce();
                              const query = sanitizeNameSearchInput(e.currentTarget.value || '');
                              setHrmsQuery(query);
                              if (query && query.trim()) {
                                searchHrms(1, query);
                              } else {
                                importHrms(1, false);
                              }
                            }
                          }}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      </Tooltip>
                    )}
                  />

                  <FormControl
                    sx={{
                      minWidth: { xs: '100%', sm: 180 },
                      flex: { xs: 1, sm: 'none' }
                    }}
                    size="small"
                  >
                    <InputLabel>Company</InputLabel>
                    <Select
                      label="Company"
                      value={selectedCompanyId}
                      onChange={(e) => {
                        const selectedCompanyIdValue = e.target.value;
                        setSelectedCompanyId(selectedCompanyIdValue);
                        setHrmsBranchFilter('');
                        setHrmsDesignationFilter('');
                        setHrmsDepartmentFilter('');
                        clearHrmsSearchDebounce();
                        setHrmsQuery(''); // Clear search query when changing company
                        importHrms(1, false, {
                          companyId: selectedCompanyIdValue ? selectedCompanyIdValue : null,
                          branchId: null,
                          designation: '',
                          department: '',
                        });
                      }}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 0.5,
                            maxHeight: { xs: 280, sm: 360 },
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: 520 },
                          },
                        },
                        MenuListProps: {
                          dense: isMobile,
                          sx: { py: 0 },
                        },
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    >
                      {/* <SelectMenuItem value="" sx={{ py: 1 }}>
                        All Companies
                      </SelectMenuItem> */}
                      {companyOptions.map((cfg) => (
                        <SelectMenuItem
                          key={cfg.config_id || cfg.company_id || cfg.access_token}
                          value={cfg.company_id || ''}
                          sx={{
                            py: 1,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                            alignItems: 'flex-start',
                          }}
                        >
                          {cfg.company_name || 'Default Company'}
                          {cfg.is_default ? ' • default' : ''}
                        </SelectMenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    sx={{
                      minWidth: { xs: '100%', sm: 150 },
                      flex: { xs: 1, sm: 'none' }
                    }}
                    size="small"
                  >
                    <InputLabel shrink={hrmsBranchFilter === '' ? true : undefined}>
                      Branch
                    </InputLabel>
                    <Select
                      value={hrmsBranchFilter}
                      label="Branch"
                      displayEmpty
                      renderValue={(value) =>
                        renderSelectValueWithTooltip(value ? selectedHrmsBranchName : 'All Branches')
                      }
                      onChange={(e) => {
                        const selectedBranchId = e.target.value;
                        setHrmsBranchFilter(selectedBranchId);
                        const branchIdToPass = selectedBranchId && selectedBranchId.trim() ? selectedBranchId : null;
                        setHrmsDesignationFilter('');
                        setHrmsDepartmentFilter('');
                        clearHrmsSearchDebounce();
                        setHrmsQuery(''); // Clear search query when changing branch
                        importHrms(1, false, { branchId: branchIdToPass, designation: '', department: '' });
                      }}
                      disabled={!selectedCompanyId || hrmsFilterBranches.length === 0}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 0.5,
                            maxHeight: { xs: 280, sm: 360 },
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: 520 },
                          },
                        },
                        MenuListProps: {
                          dense: isMobile,
                          sx: { py: 0 },
                        },
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    >
                      <SelectMenuItem value="" sx={{ py: 1 }}>All Branches</SelectMenuItem>
                      {hrmsFilterBranches.map((branch) => (
                        <SelectMenuItem
                          key={branch.branch_id}
                          value={branch.branch_id}
                          sx={{
                            py: 1,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                            alignItems: 'flex-start',
                          }}
                        >
                          {renderMenuItemLabel(branch.branch_address || branch.branch_name)}
                        </SelectMenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    sx={{
                      minWidth: { xs: '100%', sm: 170 },
                      flex: { xs: 1, sm: 'none' }
                    }}
                    size="small"
                  >
                    <InputLabel shrink={hrmsDesignationFilter === '' ? true : undefined}>
                      Designation
                    </InputLabel>
                    <Select
                      value={hrmsDesignationFilter}
                      label="Designation"
                      displayEmpty
                      renderValue={(value) =>
                        renderSelectValueWithTooltip(value ? value : 'All Designations')
                      }
                      onChange={(e) => {
                        const nextDesignation = e.target.value;
                        setHrmsDesignationFilter(nextDesignation);
                        clearHrmsSearchDebounce();
                        setHrmsQuery('');
                        importHrms(1, false, { designation: nextDesignation });
                      }}
                      disabled={!selectedCompanyId || hrmsDesignations.length === 0}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 0.5,
                            maxHeight: { xs: 280, sm: 360 },
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: 520 },
                          },
                        },
                        MenuListProps: {
                          dense: isMobile,
                          sx: { py: 0 },
                        },
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    >
                      <SelectMenuItem value="" sx={{ py: 1 }}>All Designations</SelectMenuItem>
                      {hrmsDesignations.map((designation) => (
                        <SelectMenuItem
                          key={designation}
                          value={designation}
                          sx={{
                            py: 1,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                            alignItems: 'flex-start',
                          }}
                        >
                          {renderMenuItemLabel(designation)}
                        </SelectMenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl
                    sx={{
                      minWidth: { xs: '100%', sm: 170 },
                      flex: { xs: 1, sm: 'none' }
                    }}
                    size="small"
                  >
                    <InputLabel shrink={hrmsDepartmentFilter === '' ? true : undefined}>
                      Department
                    </InputLabel>
                    <Select
                      value={hrmsDepartmentFilter}
                      label="Department"
                      displayEmpty
                      renderValue={(value) =>
                        renderSelectValueWithTooltip(value ? value : 'All Departments')
                      }
                      onChange={(e) => {
                        const nextDepartment = e.target.value;
                        setHrmsDepartmentFilter(nextDepartment);
                        clearHrmsSearchDebounce();
                        setHrmsQuery('');
                        importHrms(1, false, { department: nextDepartment });
                      }}
                      disabled={!selectedCompanyId || hrmsDepartments.length === 0}
                      MenuProps={{
                        PaperProps: {
                          sx: {
                            mt: 0.5,
                            maxHeight: { xs: 280, sm: 360 },
                            width: { xs: 'calc(100vw - 32px)', sm: 'auto' },
                            maxWidth: { xs: 'calc(100vw - 32px)', sm: 520 },
                          },
                        },
                        MenuListProps: {
                          dense: isMobile,
                          sx: { py: 0 },
                        },
                      }}
                      sx={{
                        '& .MuiSelect-select': {
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        },
                      }}
                    >
                      <SelectMenuItem value="" sx={{ py: 1 }}>All Departments</SelectMenuItem>
                      {hrmsDepartments.map((department) => (
                        <SelectMenuItem
                          key={department}
                          value={department}
                          sx={{
                            py: 1,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: 1.35,
                            alignItems: 'flex-start',
                          }}
                        >
                          {renderMenuItemLabel(department)}
                        </SelectMenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </>
              )}
              {(tab === 'enrolled' || tab === 'company') && (
                <ClearAllFiltersButton
                  onClear={handleClearAllFilters}
                  disabled={tab === 'enrolled' ? !hasActiveEnrolledFilters : !hasActiveCompanyFilters}
                />
              )}
            </Box>

            <Box sx={pageFiltersActionsSx}>
              {tab === 'enrolled' && (
                <>
                  <ToggleButtonGroup
                    value={showDeleted ? 'deleted' : 'active'}
                    exclusive
                    onChange={handleToggleDeleted}
                    size="small"
                    sx={{
                      height: PAGE_FILTER_CONTROL_HEIGHT,
                      '& .MuiToggleButton-root': {
                        minHeight: PAGE_FILTER_CONTROL_HEIGHT,
                        paddingTop: 0,
                        paddingBottom: 0,
                        textTransform: 'none',
                        fontSize: '0.875rem'
                      },
                    }}
                  >
                    <ToggleButton value="active">
                      <Visibility sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                      Active
                    </ToggleButton>
                    <ToggleButton value="deleted">
                      <Delete sx={{ mr: 0.5, fontSize: '1.1rem' }} />
                      Deactive
                    </ToggleButton>
                  </ToggleButtonGroup>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={handleRefresh}
                    disabled={loading}
                    size="small"
                    sx={pageFilterActionButtonSx}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<FileDownload />}
                    onClick={() => setDirectoryExportOpen(true)}
                    disabled={loading}
                    size="small"
                    sx={pageFilterActionButtonSx}
                  >
                    Export
                  </Button>
                </>
              )}

              {tab === 'company_records' && (
                <>
                  <Button
                    variant="outlined"
                    onClick={async () => {
                      // Reuse Kivo company list from mount getKivoConfig (companyOptions), no duplicate API call.
                      const companies = companiesFromKivoConfigData({ configs: companyOptions });
                      setSyncCompanies(companies);
                      setSyncSelectedBranch(null);
                      setSyncSelectedApiKey(null);
                      setSyncApiKeys([]);
                      setUseDateRange(false);
                      const today = format(new Date(), 'yyyy-MM-dd');
                      setSyncFromDate(today);
                      setSyncToDate(today);

                      const defaultCompany =
                        (selectedCompanyId
                          && companies.find((c) => String(c.company_id) === String(selectedCompanyId)))
                        || companies[0]
                        || null;
                      setSyncSelectedCompany(defaultCompany);

                      if (defaultCompany) {
                        try {
                          const branchesData = await getKivoBranches(defaultCompany.company_id);
                          const branches = branchesData?.branches || [];
                          setSyncBranches(branches);
                          if (branches.length === 0) {
                            showSnackbar(
                              'No branches found for this company. Fetch branches',
                              'warning',
                            );
                          }
                        } catch (err) {
                          console.error('[Sync] Failed to load branches:', err);
                          showSnackbar(`Failed to load branches: ${err.message}`, 'error');
                          setSyncBranches([]);
                        }
                      } else {
                        setSyncBranches([]);
                        if (companies.length === 0) {
                          showSnackbar('No companies loaded yet. Wait for Kivo config or refresh the page.', 'warning');
                        }
                      }
                      setSyncDialogOpen(true);
                    }}
                    startIcon={<Sync />}
                    size="small"
                    sx={pageFilterActionButtonSx}
                  >
                    Sync
                  </Button>
                </>
              )}

              <Button
                variant="contained"
                startIcon={<PersonAdd />}
                onClick={handleRegisterEmployee}
                size="small"
                sx={{
                  ...pageFilterActionButtonSx,
                  ml: { xs: 0, md: 'auto' },
                }}
              >
                Register New Employee
              </Button>
            </Box>
          </Stack>
        </Box>

        {tab === 'enrolled' && (
          <Box sx={{ mb: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              flexWrap="wrap"
            >
              <Typography variant="caption" color="text.secondary">
                Selected Filters
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={`Company: ${selectedCompanyName}`}
                sx={{ borderRadius: '8px' }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Branch: ${selectedBranchName}`}
                sx={{ borderRadius: '8px' }}
              />
            </Stack>
          </Box>
        )}

        {kivoFeatureEnabled && tab === 'company_records' && (
          <Box sx={{ mb: 2 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              flexWrap="wrap"
            >
              <Typography variant="caption" color="text.secondary">
                Selected Filters
              </Typography>
              <Chip
                size="small"
                variant="outlined"
                label={`Company: ${selectedHrmsCompanyName}`}
                sx={{ borderRadius: '8px' }}
              />
              <Chip
                size="small"
                variant="outlined"
                label={`Branch: ${selectedHrmsBranchName}`}
                sx={{ borderRadius: '8px' }}
              />
            </Stack>
          </Box>
        )}

        <Tabs value={tab === 'company_records' && !kivoFeatureEnabled ? 'enrolled' : tab} onChange={handleTabChange} sx={{ mb: 2 }} variant="scrollable" allowScrollButtonsMobile>
          <Tab value="enrolled" label="Enrolled" />
          {/* <Tab value="crm" label="CRM Records" /> */}
          {kivoFeatureEnabled && <Tab value="company_records" label="Company Records" />}
        </Tabs>

        {tab === 'enrolled' && (
          <Box>
            {/* Directory sort stays server-side. The persons API has no sort parameter, so this list does not sort only the current page. */}
            {loading ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(5, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {Array.from({ length: Math.min(pagination.limit || 6, 8) }).map((_, index) => (
                  <PersonSummaryCardSkeleton key={`enrolled-skeleton-${index}`} />
                ))}
              </Box>
            ) : enrolledLoadError && persons?.length === 0 ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
                <Alert severity="error" sx={{ mb: 2, textAlign: 'left' }}>
                  {enrolledLoadError}
                </Alert>
                <Button
                  variant="outlined"
                  startIcon={<Refresh />}
                  onClick={() => loadFaces(pagination.limit, pagination.offset)}
                >
                  Retry
                </Button>
              </Paper>
            ) : persons?.length === 0 && !loading ? (
              <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
                  No {showDeleted ? 'deleted' : 'active'} persons found.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: hasActiveEnrolledFilters ? 2 : 0 }}>
                  {hasActiveEnrolledFilters
                    ? 'Adjust the search or filters to see more employees.'
                    : 'Registered employees will appear here.'}
                </Typography>
                {hasActiveEnrolledFilters && (
                  <Button variant="outlined" onClick={handleClearAllFilters}>
                    Clear filters
                  </Button>
                )}
              </Paper>
            ) : (
              <>
                {enrolledLoadError && (
                  <Alert
                    severity="error"
                    sx={{ mb: 2 }}
                    action={(
                      <Button color="inherit" size="small" onClick={() => loadFaces(pagination.limit, pagination.offset)}>
                        Retry
                      </Button>
                    )}
                  >
                    {enrolledLoadError}
                  </Alert>
                )}
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(3, minmax(0, 1fr))',
                      xl: 'repeat(5, minmax(0, 1fr))',
                    },
                    gap: 2,
                    alignItems: 'stretch',
                  }}
                >
                  {persons?.map((person) => {
                    const status = resolveEnrolledStatus(person);
                    const updatedBy = resolveUpdatedByName(person);
                    const updatedAt = resolveUpdatedAt(person);
                    const thumbnail = personThumbnails[person.person_id] || '';
                    return (
                      <PersonSummaryCard
                        key={person.person_id}
                        name={person.name || 'Unknown person'}
                        code={person.employee_code || ''}
                        imageSrc={thumbnail}
                        statusLabel={status.label}
                        statusTone={status.tone}
                        muted={showDeleted || status.tone === 'deactive'}
                        details={[
                          { label: 'Designation', value: displayOrDash(resolveDesignation(person)) },
                          { label: 'Department', value: displayOrDash(resolveDepartment(person)) },
                          { label: 'Updated by', value: updatedBy },
                          { label: 'Last Update at', value: updatedAt }
                        ]}
                        onRequestImage={() => loadPersonThumbnail(person)}
                        onImageClick={thumbnail ? () => {
                          openImagePreview({
                            src: thumbnail,
                            title: person.name || 'Profile image',
                            alt: person.name || 'Profile image',
                            employeeName: person.name || 'Unknown person',
                            employeeId: person.employee_code || person.person_id || '',
                            employeeImage: thumbnail,
                            hideTitle: true,
                          });
                        } : undefined}
                        onClick={() => {
                          const url = buildPersonDetailsUrl(person.person_id, { detailKind: 'employee' });
                          if (url) navigate(url);
                        }}
                        actions={(
                          <Stack
                            direction="row"
                            spacing={0.75}
                            flexWrap="nowrap"
                            useFlexGap
                            sx={{
                              width: '100%',
                              '& > .MuiBox-root': {
                                flex: '1 1 auto',
                                minWidth: 0,
                              },
                              '& .MuiButton-root': {
                                width: '100%',
                                minWidth: 0,
                                px: 0.75,
                                whiteSpace: 'nowrap',
                              },
                              '& .MuiButton-endIcon': { ml: 0.25 },
                            }}
                          >
                            {renderEnrolledAngles(person)}
                            {renderEnrolledVoice(person)}
                            {renderEnrolledActions(person)}
                          </Stack>
                        )}
                      />
                    );
                  })}
                </Box>
              </>
            )}
            {/* Pagination controls for enrolled tab */}
            {!loading && pagination.total > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1.5, sm: 2 },
                  mt: 2,
                  px: { xs: 1, sm: 2 },
                  pb: { xs: 1, sm: 2 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <FormControl size="small">
                    <Select
                      value={pagination.limit}
                      onChange={handleLimitChange}
                      sx={{ minWidth: 80 }}
                    >
                      <SelectMenuItem value={10}>10</SelectMenuItem>
                      <SelectMenuItem value={20}>20</SelectMenuItem>
                      <SelectMenuItem value={50}>50</SelectMenuItem>
                      <SelectMenuItem value={100}>100</SelectMenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    Showing {Math.min(pagination.offset + 1, pagination.total)} - {Math.min(pagination.offset + pagination.limit, pagination.total)} of {pagination.total}
                  </Typography>
                </Box>
                <Pagination
                  count={pagination.total_pages}
                  page={pagination.current_page}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                  size="small"
                  sx={{
                    alignSelf: { xs: 'flex-start', sm: 'center' },
                    '& .MuiPagination-ul': {
                      pl: 0,
                      ml: 0,
                    },
                  }}
                />
              </Box>
            )}
          </Box>
        )}

        <EmployeeFacesDialog
          open={Boolean(facesModalPerson)}
          person={facesModalPerson}
          onClose={() => setFacesModalPerson(null)}
          formatAngleLabel={formatAngleLabel}
          getQualityColor={getQualityColor}
          onFaceImageClick={(face) =>
            handleOpenFaceGallery(facesModalPerson, face)
          }
          formatDate={formatDate}
          showDeleted={showDeleted}
          updatedBy={facesModalPerson ? resolveUpdatedByName(facesModalPerson) : ''}
          onEdit={handleOpenEdit}
          onDeactivate={handleSoftDeleteFace}
          onRestore={handleRestoreFace}
        />

        {false && tab === 'crm' && (
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                <TextField
                  label="Search name"
                  placeholder="Type a name"
                  size="small"
                  value={crmQuery}
                  onChange={(e) => setCrmQuery(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') searchCrm(1); }}
                  InputProps={{
                    endAdornment: crmQuery && (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="clear search"
                          onClick={handleClearSearch}
                          edge="end"
                          size="small"
                        >
                          <Clear />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button variant="outlined" onClick={() => searchCrm(1)} disabled={crmLoading}>Search</Button>
                <Divider flexItem orientation="vertical" />
                <TextField label="Since" type="datetime-local" size="small" value={crmSince} onChange={(e) => setCrmSince(e.target.value)} />
                <TextField label="Until" type="datetime-local" size="small" value={crmUntil} onChange={(e) => setCrmUntil(e.target.value)} />
                <Button variant="outlined" onClick={() => importCrm(1)} disabled={crmLoading}>SYNC CRM RECORDS</Button>
              </Box>
              {lastSyncTime && (
                <Box sx={{ mb: 2, mx: 0.5 }}>
                  <Typography variant="body2" color="text.secondary" fontWeight="bold">
                    Last synced: {formatDateWithSeconds(lastSyncTime)}
                  </Typography>
                </Box>
              )}
              {crmLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
              ) : (
                <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
                  <Table sx={{ minWidth: { xs: 0, sm: 600 } }}>
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>ID</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {crmRecords.length === 0 ? (
                        <TableRow><TableCell colSpan={4} align="center">No CRM records</TableCell></TableRow>
                      ) : (
                        crmRecords.map((rec, idx) => {
                          const name = rec.name || rec.full_name || rec.display || `${rec.first_name || ''} ${rec.last_name || ''}`.trim();
                          const pid = rec.id || rec.person_id || rec.contact_id || '';
                          const created = rec.created_at || rec.createdAt || rec.created || rec.inserted_at || rec.updated_at;
                          return (
                            <TableRow key={idx}>
                              <TableCell>{name || '-'}</TableCell>
                              <TableCell>{formatDate(created)}</TableCell>
                              <TableCell>
                                {(() => {
                                  const info = enrolledByPid[String(pid)];
                                  if (info && info.exists) {
                                    return (
                                      <Chip label={info.deleted ? 'Exists (deleted)' : 'Enrolled'} size="small" color={info.deleted ? 'warning' : 'success'} variant="outlined" />
                                    );
                                  }
                                  return (
                                    <Button size="small" variant="outlined" onClick={() => openEnrollFromCrm(rec)}>Enroll</Button>
                                  );
                                })()}
                                <Chip
                                  style={{
                                    borderRadius: '6px',
                                    border: '1px solid',
                                    borderColor: 'black',
                                    backgroundColor: 'white',
                                  }}
                                  label={String(pid)}
                                  size="small" />
                              </TableCell>
                              <TableCell>
                                {rec.isexist_person_id ? (
                                  <Button size="small" variant="outlined" disabled style={{ color: '#5F7EBC', border: '1px solid green' }}>Enrolled</Button>
                                ) : (
                                  <Button size="small" variant="outlined" onClick={() => openEnrollFromCrm(rec)}>Enroll</Button>
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
              {/* Server-side pagination for CRM records and search */}
              {crmMeta.count > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                  <Pagination
                    count={Math.max(crmMeta.count, 1)}
                    page={crmMeta.current_page}
                    onChange={(e, p) => (crmQuery?.trim() ? searchCrm(p) : importCrm(p))}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Box>
              )}
            </CardContent>
          </Card>
        )}

        {kivoFeatureEnabled && tab === 'company_records' && (
          <>
            {hrmsNotConfigured && (
              <Alert
                severity="warning"
                sx={{ mb: 2 }}
                action={
                  <Button color="inherit" size="small" onClick={() => navigate('/connect-kivo')}>
                    Connect Now
                  </Button>
                }
              >
                Kivo not configured. Please connect with Kivo to fetch company records.
              </Alert>
            )}
            {syncInfo && (
              <Box sx={{ mb: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  <strong>Last Sync:</strong> {syncInfo.completed_at ? formatDateWithSeconds(syncInfo.completed_at) : formatDateWithSeconds(syncInfo.started_at)}
                  {' '}
                  | <strong>Status: </strong>
                  <Typography component="span"
                    variant="body2"
                    sx={{
                      color:
                        syncInfo.status === 'completed'
                          ? 'success.main'
                          : syncInfo.status === 'failed'
                            ? 'error.main'
                            : 'warning.main',
                      fontWeight: 500,
                    }}
                  >
                    {syncInfo.status === 'completed'
                      ? 'Completed'
                      : syncInfo.status === 'failed'
                        ? 'Failed'
                        : 'In Progress'}
                  </Typography>

                  {(syncInfo.company_name) && (
                    <>
                      {' | '}<strong>Company: </strong> {syncInfo.company_name}
                    </>
                  )}
                  {(syncInfo.branch_address) && (
                    <>
                      {' | '}<strong>Branch: </strong>
                      <span
                        style={{
                          display: 'inline-block',
                          maxWidth: '140px',
                          verticalAlign: 'bottom',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis'
                        }}
                        title={syncInfo.branch_address}
                      >
                        {syncInfo.branch_address}
                      </span>
                    </>
                  )}
                </Typography>
                {syncInfo.status === 'in_progress' && (
                  <Box sx={{ mt: 1.5, borderRadius: 1 }}>
                    <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }} color="text.secondary">
                      {/* Always show records synced counter when syncing */}
                      <span style={{ fontSize: '14px', fontWeight: 'bold' }}>
                        Records Synced:
                      </span>
                      {syncInfo.records_processed || 0} / {syncInfo.total_records > 0 ? syncInfo.total_records : '...'}
                      {syncInfo.total_records > 0 && (
                        <span style={{ marginLeft: '4px' }}>
                          ({Math.round((syncInfo.records_processed || 0) / syncInfo.total_records * 100)}%)
                        </span>
                      )}
                      {/* Show page info when available */}
                      {(syncInfo.current_page > 0 || syncInfo.total_pages > 0) && (
                        <span style={{ color: '#ed6c02', fontSize: '14px' }}>
                          Page: {syncInfo.current_page || 1} / {syncInfo.total_pages > 0 ? syncInfo.total_pages : '...'}
                        </span>
                      )}
                    </Typography>
                  </Box>
                )}
                {syncInfo.status === 'completed' && (
                  <Box sx={{ mt: 1, display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                    {syncInfo.total_records > 0 && (
                      <Typography variant="body2" color="text.secondary">
                        <strong>Total Records:</strong> {syncInfo.records_processed || 0} / {syncInfo.total_records}
                      </Typography>
                    )}
                    <Typography variant="body2" color="text.secondary">
                      <strong>Created:</strong> {syncInfo.records_created || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      <strong>Updated:</strong> {syncInfo.records_updated || 0}
                    </Typography>
                  </Box>
                )}
                {syncInfo.error_message && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    <strong>Error:</strong> {syncInfo.error_message}
                  </Typography>
                )}
              </Box>
            )}
            <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Typography variant="body2" color="text.secondary">
                Total: {hrmsMeta.total || hrmsRecords.length || 0} records
              </Typography>
            </Box>
            {hrmsLoading ? (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                    lg: 'repeat(3, minmax(0, 1fr))',
                    xl: 'repeat(4, minmax(0, 1fr))',
                  },
                  gap: 2,
                }}
              >
                {Array.from({ length: 8 }).map((_, index) => (
                  <PersonSummaryCardSkeleton key={`company-record-skeleton-${index}`} />
                ))}
              </Box>
            ) : (
              hrmsRecords.length === 0 ? (
                <Paper variant="outlined" sx={{ p: 4, textAlign: 'center', borderRadius: 2, background: '#fafafa' }}>
                  <Typography variant="body1" color="text.secondary">
                    No employees found for this company.
                  </Typography>
                </Paper>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(2, minmax(0, 1fr))',
                      lg: 'repeat(3, minmax(0, 1fr))',
                      xl: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 2,
                    alignItems: 'stretch',
                  }}
                >
                  {hrmsRecords.map((rec, idx) => {
                    const name = rec.name || rec.full_name || `${rec.first_name || ''} ${rec.last_name || ''}`.trim();
                    const empId = rec.employee_id || rec.id || rec.user_id || '';
                    const designation = resolveDesignation(rec);
                    const department = resolveDepartment(rec);
                    const syncBy = rec.created_by || rec.created_by_name || rec.synced_by || rec.sync_by || '-';
                    const created = rec.hrms_created_at || rec.local_created_at || rec.created_at || '-';
                    const updated = rec.hrms_updated_at || rec.local_updated_at || rec.updated_at || '-';
                    const hasCheckinVoice = rec.checkin_voice_path && rec.checkin_voice_path !== '';
                    const hasCheckoutVoice = rec.checkout_voice_path && rec.checkout_voice_path !== '';
                    const isActive = rec.active !== undefined && rec.active !== null ? Boolean(rec.active) : true;
                    const isEnrolled = rec.is_enrolled || enrolledByPid[String(empId)]?.exists;
                    return (
                      <PersonSummaryCard
                        key={String(empId || idx)}
                        name={name || 'Unknown employee'}
                        code={rec.employee_code || ''}
                        imageSrc={rec.front_picture_url || ''}
                        statusLabel={isActive ? 'Active' : 'Inactive'}
                        statusTone={isActive ? 'active' : 'deactive'}
                        onClick={() => setHrmsDetailDialog({ open: true, record: rec })}
                        details={[
                          { label: 'Designation', value: displayOrDash(designation) },
                          { label: 'Department', value: displayOrDash(department) },
                          { label: 'Sync By', value: displayOrDash(syncBy) },
                          { label: 'Created', value: formatDateWithSecondsWithoutFormat(created) },
                          { label: 'Last Update', value: formatDateWithSecondsWithoutFormat(updated) },
                          { label: 'Enrollment', value: isEnrolled ? 'Enrolled' : 'Not Enrolled' },
                        ]}
                        actions={renderHrmsActions(rec, empId, name || 'Employee', hasCheckinVoice, hasCheckoutVoice)}
                      />
                    );
                  })}
                </Box>
              )
            )}
            {/* Server-side pagination for company records */}
            {hrmsMeta.count > 0 && (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  justifyContent: 'space-between',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  gap: { xs: 1.5, sm: 2 },
                  mt: 2,
                  px: { xs: 1, sm: 2 },
                  pb: { xs: 1, sm: 2 },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    flexWrap: 'wrap',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  <Typography variant="body2" color="text.secondary">
                    Rows per page:
                  </Typography>
                  <FormControl size="small">
                    <Select
                      value={hrmsLimit}
                      onChange={handleHrmsLimitChange}
                      sx={{ minWidth: 80 }}
                    >
                      <SelectMenuItem value={10}>10</SelectMenuItem>
                      <SelectMenuItem value={20}>20</SelectMenuItem>
                      <SelectMenuItem value={50}>50</SelectMenuItem>
                      <SelectMenuItem value={100}>100</SelectMenuItem>
                    </Select>
                  </FormControl>
                  <Typography variant="body2" color="text.secondary">
                    Showing {hrmsRecords.length > 0
                      ? Math.min((hrmsMeta.current_page - 1) * hrmsLimit + 1, hrmsMeta.total || hrmsRecords.length)
                      : 0}
                    {' - '}
                    {Math.min(hrmsMeta.current_page * hrmsLimit, hrmsMeta.total || hrmsRecords.length)}
                    {' of '}
                    {hrmsMeta.total || hrmsRecords.length}
                  </Typography>
                </Box>
                <Pagination
                  count={Math.max(hrmsMeta.count, 1)}
                  page={hrmsMeta.current_page}
                  onChange={(e, p) => (hrmsQuery?.trim() ? searchHrms(p) : importHrms(p, false))}
                  color="primary"
                  showFirstButton
                  showLastButton
                  size="small"
                  sx={{
                    alignSelf: { xs: 'flex-start', sm: 'center' },
                    '& .MuiPagination-ul': { pl: 0, ml: 0 },
                  }}
                />
              </Box>
            )}
          </>
        )}

      </Container>

      <PeopleExportDialog
        open={directoryExportOpen}
        onClose={() => setDirectoryExportOpen(false)}
        title="Employees"
        exportFilters={{
          include_deleted: showDeleted,
          q: enrolledQuery?.trim() || null,
          company_id: enrolledCompanyFilter || null,
          branch_id: enrolledBranchFilter || null,
          is_intrusion: false,
          designation: enrolledDesignationFilter || null,
          department: enrolledDepartmentFilter || null,
          is_vendor: false,
          is_visitor: false,
        }}
      />

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              confirmDialog.onConfirm();
              setConfirmDialog({ ...confirmDialog, open: false });
            }}
            variant="contained"
            color="primary"
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      <PersonProfileSwitchDialog
        open={profileSwitchDialog.open}
        sourceKind="employee"
        person={profileSwitchDialog.person}
        onClose={() => setProfileSwitchDialog({ open: false, person: null })}
        onSuccess={(message) => {
          showSnackbar(message, 'success');
          loadFaces();
        }}
        onError={(message, severity = 'error') => showSnackbar(message, severity)}
        companyOptions={enrolledCompanies}
      />

      <Dialog
        open={nameEditDialog.open}
        onClose={() => {
          if (!nameEditDialog.saving) closeNameEditDialog();
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            boxShadow: isMobile ? 'none' : '0 10px 36px rgba(15, 23, 42, 0.16)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            py: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          Edit Employee
          <IconButton
            size="small"
            onClick={closeNameEditDialog}
            disabled={nameEditDialog.saving}
            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <TextField
              label="Employee Name"
              value={nameEditDialog.name}
              onChange={(e) => setNameEditDialog((prev) => ({ ...prev, name: e.target.value }))}
              size="small"
              autoFocus
              fullWidth
            />

            <TextField
              label="Employee Code"
              value={nameEditDialog.employeeCode ?? ''}
              onChange={(e) => setNameEditDialog((prev) => ({ ...prev, employeeCode: e.target.value }))}
              placeholder="-"
              InputLabelProps={{ shrink: true }}
              size="small"
              fullWidth
              disabled={isIntegerPersonId(nameEditDialog.person?.person_id || nameEditDialog.personId)}
              helperText={
                !isIntegerPersonId(nameEditDialog.person?.person_id || nameEditDialog.personId)
                  ? "Editable for locally created employees"
                  : ""
              }
            />

            {/* Company - editable only when person_id is not numeric */}
            {!isIntegerPersonId(nameEditDialog.person?.person_id || nameEditDialog.personId) ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Company</InputLabel>
                <Select
                  value={nameEditDialog.companyId || ''}
                  onChange={(e) => handleEditDialogCompanyChange(e.target.value)}
                  label="Company"
                  MenuProps={editSelectMenuProps}
                >
                  <SelectMenuItem value="">
                    <em>None</em>
                  </SelectMenuItem>
                  {editDialogCompanies.map((company) => (
                    <SelectMenuItem key={company.company_id} value={company.company_id}>
                      {company.company_name}
                    </SelectMenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Company"
                value={nameEditDialog.person?.company_name || '-'}
                size="small"
                fullWidth
                disabled
              />
            )}

            {/* Branch - editable only when person_id is not numeric */}
            {!isIntegerPersonId(nameEditDialog.person?.person_id || nameEditDialog.personId) ? (
              <FormControl size="small" fullWidth>
                <InputLabel>Branch</InputLabel>
                <Select
                  value={nameEditDialog.branchId || ''}
                  onChange={(e) => handleEditDialogBranchChange(e.target.value)}
                  label="Branch"
                  disabled={!nameEditDialog.companyId}
                  MenuProps={editSelectMenuProps}
                >
                  <SelectMenuItem value="">
                    <em>None</em>
                  </SelectMenuItem>
                  {editDialogBranches.map((branch) => (
                    <SelectMenuItem
                      key={branch.branch_id}
                      value={branch.branch_id}
                      sx={{ whiteSpace: 'normal', lineHeight: 1.3 }}
                    >
                      {branch.branch_address || branch.branch_name}
                    </SelectMenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Branch"
                value={nameEditDialog.person?.branch_address || nameEditDialog.person?.branch_name || '-'}
                size="small"
                fullWidth
                disabled
              />
            )}

            <FormControl
              size="small"
              fullWidth
              disabled={orgMastersRequireBranch && !nameEditDialog.branchId}
            >
              <InputLabel>Designation</InputLabel>
              <Select
                value={nameEditDialog.designation || ''}
                onChange={(e) => setNameEditDialog((prev) => ({ ...prev, designation: e.target.value }))}
                label="Designation"
                MenuProps={editSelectMenuProps}
                renderValue={(value) => orgMasterSelectDisplayLabel(
                  value,
                  editDesignationOptions,
                  nameEditDialog?.person,
                  'designation',
                )}
              >
                <SelectMenuItem value="">
                  <em>None</em>
                </SelectMenuItem>
                {editDesignationOptions.map((designation) => (
                  <SelectMenuItem
                    key={orgMasterOptionId(designation)}
                    value={orgMasterOptionId(designation)}
                  >
                    {orgMasterOptionLabel(designation)}
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl
              size="small"
              fullWidth
              disabled={orgMastersRequireBranch && !nameEditDialog.branchId}
            >
              <InputLabel>Department</InputLabel>
              <Select
                value={nameEditDialog.department || ''}
                onChange={(e) => setNameEditDialog((prev) => ({ ...prev, department: e.target.value }))}
                label="Department"
                MenuProps={editSelectMenuProps}
                renderValue={(value) => orgMasterSelectDisplayLabel(
                  value,
                  editDepartmentOptions,
                  nameEditDialog?.person,
                  'department',
                )}
              >
                <SelectMenuItem value="">
                  <em>None</em>
                </SelectMenuItem>
                {editDepartmentOptions.map((department) => (
                  <SelectMenuItem
                    key={orgMasterOptionId(department)}
                    value={orgMasterOptionId(department)}
                  >
                    {orgMasterOptionLabel(department)}
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider sx={{ my: 1 }} />

            {/* Custom Training Images Section */}
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Custom Training Images
            </Typography>

            {/* Preview Selected Images */}
            {customTrainingImages.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                {customTrainingImages.map((image, index) => (
                  <Box
                    key={image.id}
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: 2,
                      p: 2,
                      border: '1px solid #e0e0e0',
                      borderRadius: 2,
                      backgroundColor: '#fafafa',
                      alignItems: { xs: 'stretch', sm: 'center' },
                    }}
                  >
                    {/* Image Preview */}
                    <Box
                      sx={{
                        position: 'relative',
                        width: { xs: '100%', sm: 80 },
                        height: { xs: 160, sm: 80 },
                        flexShrink: 0,
                        border: '2px solid #e0e0e0',
                        borderRadius: 1,
                        overflow: 'hidden',
                      }}
                    >
                      <img
                        src={image.previewUrl}
                        alt="Preview"
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveCustomImage(image.id)}
                        sx={{
                          position: 'absolute',
                          top: 2,
                          right: 2,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          padding: '2px',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                          },
                        }}
                      >
                        <Close fontSize="small" sx={{ fontSize: 14 }} />
                      </IconButton>
                    </Box>

                    {/* Label Input */}
                    <TextField
                      label={`Image ${index + 1} Label (optional)`}
                      placeholder="e.g., With glasses, Side view, Smiling"
                      value={image.label}
                      onChange={(e) => handleUpdateImageLabel(image.id, e.target.value)}
                      size="small"
                      fullWidth
                      disabled={nameEditDialog.saving}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'white',
                        },
                      }}
                    />
                  </Box>
                ))}
              </Box>
            )}

            {/* Select Images Button */}
            <input
              ref={customImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleCustomImageSelect}
            />
            <Button
              variant="outlined"
              startIcon={<CloudUpload />}
              onClick={() => customImageInputRef.current?.click()}
              fullWidth
              disabled={nameEditDialog.saving}
            >
              Select Images ({customTrainingImages.length} selected)
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={closeNameEditDialog} disabled={nameEditDialog.saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSavePersonNameWithConfirm}
            variant="contained"
            disabled={nameEditDialog.saving}
          >
            {nameEditDialog.saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={hrmsNameEditDialog.open}
        onClose={() => {
          if (!hrmsNameEditDialog.saving) closeHrmsNameEditDialog();
        }}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            boxShadow: isMobile ? 'none' : '0 10px 36px rgba(15, 23, 42, 0.16)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            py: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          Edit Employee
          <IconButton
            size="small"
            onClick={closeHrmsNameEditDialog}
            disabled={hrmsNameEditDialog.saving}
            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>
            <TextField
              label="Employee Name"
              value={hrmsNameEditDialog.name}
              onChange={(e) => setHrmsNameEditDialog((prev) => ({ ...prev, name: e.target.value }))}
              size="small"
              autoFocus
              fullWidth
            />
            <TextField
              label="Employee ID"
              value={
                hrmsNameEditDialog.record?.employee_id ||
                hrmsNameEditDialog.record?.id ||
                hrmsNameEditDialog.record?.user_id ||
                '-'
              }
              size="small"
              fullWidth
              disabled
            />
            <TextField
              label="Company"
              value={hrmsNameEditDialog.record?.company_name || '-'}
              size="small"
              fullWidth
              disabled
            />
            <TextField
              label="Branch"
              value={hrmsNameEditDialog.record?.branch_address || hrmsNameEditDialog.record?.branch_name || '-'}
              size="small"
              fullWidth
              disabled
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={closeHrmsNameEditDialog} disabled={hrmsNameEditDialog.saving}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveHrmsEmployeeNameWithConfirm}
            variant="contained"
            disabled={hrmsNameEditDialog.saving}
          >
            {hrmsNameEditDialog.saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={restrictedCameraDialog.open}
        onClose={() => {
          if (!restrictedCameraDialog.saving) {
            setRestrictedCameraDialog({ open: false, person: null, selectedIds: [], saving: false });
          }
        }}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}
      >
        <DialogTitle sx={{ px: 3, py: 2, borderBottom: '1px solid #eef1f6' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 42,
                height: 42,
                borderRadius: '50%',
                bgcolor: '#e8f1fb',
                color: '#3f51b5',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Videocam fontSize="small" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Restricted Cameras
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Select cameras that this employee cannot access
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Employee Name"
              value={restrictedCameraDialog?.person?.name || ''}
              fullWidth
              size="small"
              disabled
            />
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="subtitle2">
                Activity Cameras ({activityCameras.length})
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() =>
                    setRestrictedCameraDialog((prev) => ({
                      ...prev,
                      selectedIds: activityCameras.map((cam) => cam.id),
                    }))
                  }
                  disabled={restrictedCameraDialog.saving || activityCameras.length === 0}
                >
                  Select All
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => setRestrictedCameraDialog((prev) => ({ ...prev, selectedIds: [] }))}
                  disabled={restrictedCameraDialog.saving || (restrictedCameraDialog.selectedIds || []).length === 0}
                >
                  Clear
                </Button>
              </Box>
            </Box>
            <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 2, maxHeight: 360, overflowY: 'auto', background: '#fcfdff' }}>
              {activityCameras.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                  No activity cameras available.
                </Typography>
              ) : (
                activityCameras.map((cam) => (
                  <Box
                    key={cam.id}
                    onClick={() =>
                      setRestrictedCameraDialog((prev) => {
                        const selected = new Set(prev.selectedIds || []);
                        if (selected.has(cam.id)) selected.delete(cam.id);
                        else selected.add(cam.id);
                        return { ...prev, selectedIds: Array.from(selected) };
                      })
                    }
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      py: 1.1,
                      borderBottom: '1px solid #f2f4f8',
                      cursor: 'pointer',
                      transition: 'background-color 0.15s ease',
                      '&:hover': { backgroundColor: '#f5f8ff' },
                      '&:last-of-type': { borderBottom: 'none' },
                    }}
                  >
                    <Checkbox checked={(restrictedCameraDialog.selectedIds || []).includes(cam.id)} />
                    <ListItemText
                      primary={cam.label}
                      secondary={cam.description || 'No description'}
                      primaryTypographyProps={{ fontWeight: 500 }}
                    />
                  </Box>
                ))
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid #eef1f6', justifyContent: 'space-between' }}>
          <Typography variant="body2" color="text.secondary">
            {`Selected: ${(restrictedCameraDialog.selectedIds || []).length}`}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={() => setRestrictedCameraDialog({ open: false, person: null, selectedIds: [], saving: false })}
              disabled={restrictedCameraDialog.saving}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSaveRestrictedCamerasWithConfirm}
              disabled={restrictedCameraDialog.saving}
            >
              {restrictedCameraDialog.saving ? 'Saving...' : 'Save'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Edit Image Dialog */}

      <Dialog
        open={editDialog.open}
        onClose={handleCloseEdit}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Edit Employee Image</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Guided capture takes the photo automatically when the face pose is valid. You can also upload an image.
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {!editCameraOn && previewUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <img
                  src={previewUrl}
                  alt="Preview"
                  style={{ maxWidth: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 8 }}
                />
              </Box>
            )}

            {previewUrl && editQualityResult?.loading ? (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
                <Skeleton variant="rounded" height={18} width="46%" animation="wave" />
                <Skeleton variant="rounded" height={14} animation="wave" />
              </Box>
            ) : null}

            {previewUrl && editQualityResult && !editQualityResult.loading ? (
              <Box
                sx={{
                  p: 1,
                  borderRadius: 1.5,
                  bgcolor: editQualityResult.success ? '#ecfdf5' : '#fef2f2',
                  border: `1px solid ${editQualityResult.success ? '#86efac' : '#fecaca'}`,
                }}
              >
                <Typography variant="caption" fontWeight={700} sx={{ display: 'block' }}>
                  {editQualityResult.message}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Faces: {editQualityResult.face_count ?? 0} | Pose: {editQualityResult.pose_quality ?? 0} | Lighting: {editQualityResult.lighting_quality ?? 0} | Score: {editQualityResult.quality_score ?? 0} | Similarity: {editQualityResult.similarity ?? 'N/A'} | Embedding: {editQualityResult.encoding_dimension ?? 0}
                </Typography>
              </Box>
            ) : null}

            <Button variant="outlined" component="label">
              Choose Image
              <input hidden accept="image/*" type="file" onChange={onFileChange} />
            </Button>

            <Divider>or</Divider>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {!editCameraOn ? (
                <Button
                  variant="contained"
                  startIcon={<Videocam />}
                  onClick={startEditCapture}
                  sx={{ backgroundColor: primary }}
                >
                  Start Capture
                </Button>
              ) : (
                <Button variant="outlined" onClick={() => setEditCameraOn(false)}>
                  Stop Camera
                </Button>
              )}
              {previewUrl && !editCameraOn ? (
                <Button variant="outlined" onClick={startEditCapture}>
                  Retake
                </Button>
              ) : null}
            </Box>

            {editCameraOn && (
              <WebcamCapture
                key={`edit-capture-${editCaptureNonce}`}
                stepKey={editDialog.face?.face_angle || 'front'}
                expectedDirection={directionForFaceAngle(editDialog.face?.face_angle)}
                onCaptured={handleEditAutoCaptured}
                onPermissionDenied={(message) => {
                  if (message) showSnackbar(message, 'error');
                }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseEdit}>Cancel</Button>
          <Button onClick={submitEdit} variant="contained" disabled={!selectedFile}>Update</Button>
        </DialogActions>
      </Dialog>

      {/* Add Angle Dialog */}
      <Dialog open={addAngleDialog.open} onClose={closeAddAngleDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Add Face Angle</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField label="Name" value={addAngleDialog.name} onChange={(e) => setAddAngleDialog(prev => ({ ...prev, name: e.target.value }))} fullWidth size="small" />
            <TextField label="Person ID" value={addAngleDialog.personId} onChange={(e) => setAddAngleDialog(prev => ({ ...prev, personId: e.target.value }))} fullWidth size="small" />

            {/* Guidance */}
            <Typography variant="body2" color="text.secondary">
              Capture left or right profile to add those angles. Front profile is typically added during initial enrollment.
            </Typography>

            {/* Preview or Camera */}
            {!isCameraOn && previewUrl && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
                <img src={previewUrl} alt="Preview" style={{ maxWidth: '100%', maxHeight: 220, objectFit: 'contain', borderRadius: 8 }} />
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
              <Button
                variant={isCameraOn ? 'outlined' : 'contained'}
                onClick={() => {
                  if (isCameraOn) {
                    // stop camera
                    if (streamRef.current) {
                      try { streamRef.current.getTracks().forEach(t => t.stop()); } catch (_) { }
                      streamRef.current = null;
                    }
                    if (videoRef.current) { try { videoRef.current.pause(); } catch (_) { }; videoRef.current.srcObject = null; }
                    setIsCameraOn(false);
                  } else {
                    // start camera
                    navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user' }, audio: false })
                      .then(stream => { streamRef.current = stream; if (videoRef.current) videoRef.current.srcObject = stream; setIsCameraOn(true); })
                      .catch(() => showSnackbar('Unable to access camera', 'error'));
                  }
                }}
              >
                {isCameraOn ? 'Stop Camera' : 'Start Camera'}
              </Button>
              <Button variant="outlined" onClick={capturePhoto} disabled={!isCameraOn}>Capture</Button>
              <Button variant="outlined" component="label">
                Upload
                <input type="file" hidden accept="image/*" onChange={onFileChange} />
              </Button>
            </Box>

            {isCameraOn && (
              <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                <video ref={videoRef} autoPlay playsInline style={{ width: '100%', maxHeight: 240, borderRadius: 8 }} />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAddAngleDialog}>Cancel</Button>
          <Button onClick={submitAddAngle} variant="contained">Add Angle</Button>
        </DialogActions>
      </Dialog>

      {/* Face Detail Dialog */}
      <Dialog
        open={faceDetailDialog.open}
        onClose={() => setFaceDetailDialog({ ...faceDetailDialog, open: false })}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Face Details</DialogTitle>
        <DialogContent sx={{ pl: 2 }}>
          {faceDetailDialog.face && (
            <Box sx={{ pt: 2 }}>
              <TextField
                fullWidth
                label="Face ID"
                value={faceDetailDialog.face.face_id}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Name"
                value={faceDetailDialog.face.name}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Person ID"
                value={faceDetailDialog.face.person_id}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Face Angle"
                value={formatAngleLabel(faceDetailDialog.face.face_angle, faceDetailDialog.face.metadata)}
                helperText={faceDetailDialog.face.face_angle}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Pose Quality"
                value={`${(faceDetailDialog.face.pose_quality * 100).toFixed(1)}%`}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Lighting Quality"
                value={`${(faceDetailDialog.face.lighting_quality * 100).toFixed(1)}%`}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Created At"
                value={formatDate(faceDetailDialog.face.created_at)}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              <TextField
                fullWidth
                label="Last Updated"
                value={formatDate(faceDetailDialog.face.last_updated)}
                margin="normal"
                InputProps={{ readOnly: true }}
              />
              {faceDetailDialog.face.deleted_at && (
                <TextField
                  fullWidth
                  label="Deleted At"
                  value={formatDate(faceDetailDialog.face.deleted_at)}
                  margin="normal"
                  InputProps={{ readOnly: true }}
                />
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFaceDetailDialog({ ...faceDetailDialog, open: false })}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Face Image Dialog */}
      <Dialog
        open={imageDialog.open}
        onClose={() => {
          if (imageDialog.imageUrl) {
            URL.revokeObjectURL(imageDialog.imageUrl);
          }
          setImageDialog({ open: false, imageUrl: null, faceName: '' });
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Face Image - {imageDialog.faceName}</DialogTitle>
        <DialogContent>
          {imageDialog.imageUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <img
                src={imageDialog.imageUrl}
                alt={`Face for ${imageDialog.faceName}`}
                style={{
                  maxWidth: '100%',
                  maxHeight: '500px',
                  objectFit: 'contain',
                  borderRadius: '8px',
                  boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              if (imageDialog.imageUrl) {
                URL.revokeObjectURL(imageDialog.imageUrl);
              }
              setImageDialog({ open: false, imageUrl: null, faceName: '' });
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Company record Detail Dialog */}
      <Dialog
        open={hrmsDetailDialog.open}
        onClose={() => setHrmsDetailDialog({ open: false, record: null })}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            boxShadow: isMobile ? 'none' : '0 10px 36px rgba(15, 23, 42, 0.16)',
          },
        }}
      >
        <DialogTitle
          sx={{
            fontSize: { xs: '1.1rem', sm: '1.25rem' },
            py: { xs: 1.5, sm: 2 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          Employee Details
          <IconButton
            size="small"
            onClick={() => setHrmsDetailDialog({ open: false, record: null })}
            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <Close fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {hrmsDetailDialog.record && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
                  gap: 1.5,
                }}
              >
                <TextField size="small" label="Name" value={hrmsDetailDialog.record.full_name || hrmsDetailDialog.record.name || '-'} InputProps={{ readOnly: true }} />
                <TextField size="small" label="Employee ID" value={hrmsDetailDialog.record.employee_id || '-'} InputProps={{ readOnly: true }} />
                <TextField size="small" label="Employee Code" value={hrmsDetailDialog.record.employee_code || '-'} InputProps={{ readOnly: true }} />
                <TextField
                  size="small"
                  label="Active Status"
                  value={
                    hrmsDetailDialog.record.active !== undefined && hrmsDetailDialog.record.active !== null
                      ? (Boolean(hrmsDetailDialog.record.active) ? 'Active' : 'Inactive')
                      : 'Active (default)'
                  }
                  InputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
                <TextField size="small" label="First Name" value={hrmsDetailDialog.record.first_name || '-'} InputProps={{ readOnly: true }} />
                <TextField size="small" label="Last Name" value={hrmsDetailDialog.record.last_name || '-'} InputProps={{ readOnly: true }} />
                <TextField
                  size="small"
                  label="Created By"
                  value={
                    hrmsDetailDialog.record.created_by
                    || hrmsDetailDialog.record.created_by_name
                    || hrmsDetailDialog.record.sync_by
                    || hrmsDetailDialog.record.synced_by
                    || '-'
                  }
                  InputProps={{ readOnly: true }}
                />
                <TextField size="small" label="Created At" value={formatDateWithSecondsWithoutFormat(hrmsDetailDialog.record.hrms_created_at || hrmsDetailDialog.record.local_created_at)} InputProps={{ readOnly: true }} />
                <TextField size="small" label="Updated At" value={formatDateWithSecondsWithoutFormat(hrmsDetailDialog.record.hrms_updated_at || hrmsDetailDialog.record.local_updated_at)} InputProps={{ readOnly: true }} />
                <TextField size="small" label="Last Sync" value={formatDateWithSecondsWithoutFormat(hrmsDetailDialog.record.last_sync_time)} InputProps={{ readOnly: true }} />
              </Box>

              {(hrmsDetailDialog.record.front_picture_url || hrmsDetailDialog.record.left_picture_url || hrmsDetailDialog.record.right_picture_url || hrmsDetailDialog.record.custom_angle_picture_url) && (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(220px, 1fr))' }, gap: 1.5 }}>
                  {hrmsDetailDialog.record.front_picture_url && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom align="center" fontWeight="bold">Front</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 180, sm: 220 }, backgroundColor: '#f5f5f5', borderRadius: 2, overflow: 'hidden' }}>
                          <img src={hrmsDetailDialog.record.front_picture_url} alt="Front" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => (e.target.style.display = 'none')} />
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                  {hrmsDetailDialog.record.left_picture_url && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom align="center" fontWeight="bold">Left</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 180, sm: 220 }, backgroundColor: '#f5f5f5', borderRadius: 2, overflow: 'hidden' }}>
                          <img src={hrmsDetailDialog.record.left_picture_url} alt="Left" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => (e.target.style.display = 'none')} />
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                  {hrmsDetailDialog.record.right_picture_url && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom align="center" fontWeight="bold">Right</Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 180, sm: 220 }, backgroundColor: '#f5f5f5', borderRadius: 2, overflow: 'hidden' }}>
                          <img src={hrmsDetailDialog.record.right_picture_url} alt="Right" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => (e.target.style.display = 'none')} />
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                  {hrmsDetailDialog.record.custom_angle_picture_url && (
                    <Card variant="outlined">
                      <CardContent>
                        <Typography variant="subtitle2" gutterBottom align="center" fontWeight="bold">
                          {hrmsDetailDialog.record.custom_angle_label || 'Custom'}
                        </Typography>
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: { xs: 180, sm: 220 }, backgroundColor: '#f5f5f5', borderRadius: 2, overflow: 'hidden' }}>
                          <img src={hrmsDetailDialog.record.custom_angle_picture_url} alt="Custom" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => (e.target.style.display = 'none')} />
                        </Box>
                      </CardContent>
                    </Card>
                  )}
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {(() => {
                  const isActive = hrmsDetailDialog.record.active !== undefined && hrmsDetailDialog.record.active !== null
                    ? Boolean(hrmsDetailDialog.record.active)
                    : true;
                  return isActive ? (
                    <Chip label="Active" color="success" size="small" />
                  ) : (
                    <Chip label="Inactive" color="error" size="small" />
                  );
                })()}
                {hrmsDetailDialog.record.is_new_profile && <Chip label="New Profile" color="success" size="small" />}
                {hrmsDetailDialog.record.is_updated_profile && <Chip label="Updated Profile" color="info" size="small" />}
                {(hrmsDetailDialog.record.is_enrolled || enrolledByPid[String(hrmsDetailDialog.record.employee_id)]?.exists) && <Chip label="Enrolled" color="success" size="small" />}
              </Box>

              {(hrmsDetailDialog.record.checkin_voice_path || hrmsDetailDialog.record.checkout_voice_path) && (
                <Box sx={{ mt: 1, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
                  <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                    Voice Recordings
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
                    {hrmsDetailDialog.record.checkin_voice_path && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                        <Chip
                          color="primary"
                          size="small"
                          onClick={() => playVoice(hrmsDetailDialog.record.employee_id, 'checkin')}
                          disabled={!!playingVoice}
                          sx={{
                            cursor: playingVoice ? 'not-allowed' : 'pointer',
                            '&:hover': { backgroundColor: playingVoice ? undefined : 'rgba(25, 118, 210, 0.08)' },
                            opacity: playingVoice ? 0.6 : 1
                          }}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Login sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                              <span>IN</span>
                              <VolumeUp sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                            </Box>
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              minWidth: 0,
                              maxWidth: '100%',
                              whiteSpace: { xs: 'normal', sm: 'nowrap' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              wordBreak: 'break-all',
                            }}
                          >
                            {hrmsDetailDialog.record.checkin_voice_path.split('/').pop()}
                          </Box>
                        </Typography>
                      </Box>
                    )}
                    {hrmsDetailDialog.record.checkout_voice_path && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0, width: '100%' }}>
                        <Chip
                          color="secondary"
                          size="small"
                          onClick={() => playVoice(hrmsDetailDialog.record.employee_id, 'checkout')}
                          disabled={!!playingVoice}
                          sx={{
                            cursor: playingVoice ? 'not-allowed' : 'pointer',
                            '&:hover': { backgroundColor: playingVoice ? undefined : 'rgba(156, 39, 176, 0.08)' },
                            opacity: playingVoice ? 0.6 : 1
                          }}
                          label={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <Logout sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                              <span>OUT</span>
                              <VolumeUp sx={{ fontSize: 14, verticalAlign: 'middle' }} />
                            </Box>
                          }
                        />
                        <Typography variant="caption" color="text.secondary">
                          <Box
                            component="span"
                            sx={{
                              display: 'block',
                              minWidth: 0,
                              maxWidth: '100%',
                              whiteSpace: { xs: 'normal', sm: 'nowrap' },
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              wordBreak: 'break-all',
                            }}
                          >
                            {hrmsDetailDialog.record.checkout_voice_path.split('/').pop()}
                          </Box>
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, gap: 1, flexWrap: 'wrap' }}>
          <Button onClick={() => setHrmsDetailDialog({ open: false, record: null })}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Company record Image Dialog */}
      <Dialog open={hrmsImageDialog.open} onClose={handleCloseHrmsImageDialog} maxWidth="lg" fullWidth>
        <DialogTitle>{hrmsImageDialog.name} - Profile Images</DialogTitle>
        <DialogContent>
          {hrmsImageLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : hrmsImageDialog.images && (
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 3, pt: 2 }}>
              {hrmsImageDialog.images.front && (
                <Card sx={{ boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom align="center" fontWeight="bold">Front View</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, backgroundColor: '#f5f5f5', borderRadius: 2, overflow: 'hidden' }}>
                      <img src={hrmsImageDialog.images.front} alt="Front" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {hrmsImageDialog.images.left && (
                <Card sx={{ boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom align="center" fontWeight="bold">Left View</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, backgroundColor: '#f5f5f5', borderRadius: 2, overflow: 'hidden' }}>
                      <img src={hrmsImageDialog.images.left} alt="Left" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {hrmsImageDialog.images.right && (
                <Card sx={{ boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom align="center" fontWeight="bold">Right View</Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, backgroundColor: '#f5f5f5', borderRadius: 2, overflow: 'hidden' }}>
                      <img src={hrmsImageDialog.images.right} alt="Right" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
                    </Box>
                  </CardContent>
                </Card>
              )}
              {hrmsImageDialog.images.custom && (
                <Card sx={{ boxShadow: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" gutterBottom align="center" fontWeight="bold">
                      {hrmsImageDialog.images.customLabel || 'Custom'}
                    </Typography>
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 300, backgroundColor: '#f5f5f5', borderRadius: 2, overflow: 'hidden' }}>
                      <img src={hrmsImageDialog.images.custom} alt="Custom" style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }} onError={(e) => e.target.style.display = 'none'} />
                    </Box>
                  </CardContent>
                </Card>
              )}
            </Box>
          )}
          {hrmsImageDialog.images && !hrmsImageDialog.images.front && !hrmsImageDialog.images.left && !hrmsImageDialog.images.right && !hrmsImageDialog.images.custom && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No images available</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseHrmsImageDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Enroll Dialog (from CRM record) */}
      <Dialog open={enrollDialog.open} onClose={closeEnrollDialog} maxWidth="md" fullWidth>
        <DialogTitle>Enroll Person</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Name" value={enrollDialog.name} onChange={(e) => setEnrollDialog(prev => ({ ...prev, name: e.target.value }))} fullWidth size="small" />
            <TextField label="Person ID" value={enrollDialog.personId} onChange={(e) => setEnrollDialog(prev => ({ ...prev, personId: e.target.value }))} fullWidth size="small" />
            {/* Multi-angle slots */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2">Front (required)</Typography>
                {enrollFrontPreview && (
                  <img src={enrollFrontPreview} alt="Front" style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8 }} />
                )}
                <Button variant="outlined" component="label" fullWidth sx={{ mt: 1 }}>Upload
                  <input hidden accept="image/*" type="file" onChange={(e) => onEnrollFileChange('front', e)} />
                </Button>
              </Box>
              <Box>
                <Typography variant="subtitle2">Left (optional)</Typography>
                {enrollLeftPreview && (
                  <img src={enrollLeftPreview} alt="Left" style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8 }} />
                )}
                <Button variant="outlined" component="label" fullWidth sx={{ mt: 1 }}>Upload
                  <input hidden accept="image/*" type="file" onChange={(e) => onEnrollFileChange('left', e)} />
                </Button>
              </Box>
              <Box>
                <Typography variant="subtitle2">Right (optional)</Typography>
                {enrollRightPreview && (
                  <img src={enrollRightPreview} alt="Right" style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8 }} />
                )}
                <Button variant="outlined" component="label" fullWidth sx={{ mt: 1 }}>Upload
                  <input hidden accept="image/*" type="file" onChange={(e) => onEnrollFileChange('right', e)} />
                </Button>
              </Box>
              <Box>
                <Typography variant="subtitle2">Custom Angle (optional)</Typography>
                <TextField
                  label="Custom angle label"
                  size="small"
                  value={enrollCustomLabel}
                  onChange={(e) => setEnrollCustomLabel(e.target.value)}
                  placeholder="e.g., Helmet"
                  sx={{ mt: 1 }}
                />
                {enrollCustomPreview && (
                  <img src={enrollCustomPreview} alt="Custom" style={{ width: '100%', maxHeight: 160, objectFit: 'contain', borderRadius: 8, marginTop: 8 }} />
                )}
                <Button variant="outlined" component="label" fullWidth sx={{ mt: 1 }}>Upload
                  <input hidden accept="image/*" type="file" onChange={(e) => onEnrollFileChange('custom', e)} />
                </Button>
              </Box>
            </Box>

            {/* Camera controls: capture into selected angle */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap', mb: 1 }}>
                <Button
                  variant={isCameraOn ? 'outlined' : 'contained'}
                  onClick={toggleCamera}
                  sx={{ borderColor: primary, backgroundColor: isCameraOn ? 'transparent' : primary, color: isCameraOn ? primary : 'white' }}
                >
                  {isCameraOn ? 'Stop Camera' : 'Start Camera'}
                </Button>
                <Button variant="outlined" onClick={capturePhotoForEnroll} disabled={!isCameraOn}>Capture to</Button>
                <Button size="small" variant={enrollActiveAngle === 'front' ? 'contained' : 'outlined'} onClick={() => setEnrollActiveAngle('front')}>Front</Button>
                <Button size="small" variant={enrollActiveAngle === 'left' ? 'contained' : 'outlined'} onClick={() => setEnrollActiveAngle('left')}>Left</Button>
                <Button size="small" variant={enrollActiveAngle === 'right' ? 'contained' : 'outlined'} onClick={() => setEnrollActiveAngle('right')}>Right</Button>
                <Button size="small" variant={enrollActiveAngle === 'custom' ? 'contained' : 'outlined'} onClick={() => setEnrollActiveAngle('custom')}>Custom</Button>
              </Box>
              {isCameraOn && (
                <Box sx={{ mt: 1, backgroundColor: '#000', border: '3px solid #1976d2', borderRadius: 3, overflow: 'hidden', width: '100%', maxWidth: 560, mx: 'auto' }}>
                  <video ref={videoRef} autoPlay playsInline muted style={{ width: '100%', height: 'auto', display: 'block' }} />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEnrollDialog}>Cancel</Button>
          <Button onClick={submitEnroll} variant="contained">Enroll</Button>
        </DialogActions>
      </Dialog>

      {/* Sync with Date Range Dialog */}
      <Dialog
        open={syncDialogOpen}
        onClose={() => setSyncDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: { maxHeight: '90vh', overflow: 'auto' }
        }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Sync color="primary" />
          Sync Company Records
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mt: 2 }}>
            {/* Company Selector */}
            <FormControl fullWidth>
              <InputLabel>Company *</InputLabel>
              <Select
                value={syncSelectedCompany?.company_id || ''}
                onChange={async (e) => {
                  const companyId = e.target.value;
                  const company = syncCompanies.find(c => c.company_id === companyId);
                  setSyncSelectedCompany(company);
                  setSyncSelectedBranch(null);
                  setSyncSelectedApiKey(null);
                  setSyncApiKeys([]);

                  // Load branches for selected company
                  if (companyId) {
                    try {
                      const branchesData = await getKivoBranches(companyId);
                      const branches = branchesData?.branches || [];
                      setSyncBranches(branches);
                      if (branches.length === 0) {
                        showSnackbar('No branches found for this company. Fetch branches', 'warning');
                      } else {
                        console.log('[Sync] Successfully loaded', branches.length, 'branches');
                      }
                    } catch (err) {
                      showSnackbar(`Failed to load branches: ${err.message}`, 'error');
                      setSyncBranches([]);
                    }
                  } else {
                    setSyncBranches([]);
                  }
                }}
                label="Company *"
              >
                {syncCompanies.map((company) => (
                  <SelectMenuItem key={company.company_id} value={company.company_id}>
                    {company.company_name}
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth disabled={!syncSelectedCompany || syncBranchesRefreshing}>
              {/* shrink/notched: value is '' but renderValue shows text — MUI would otherwise keep label centered and overlap */}
              <InputLabel id="branch-label" shrink={syncBranchFieldLabelNotched}>
                Branch *
              </InputLabel>

              <Select
                labelId="branch-label"
                id="branch-select"
                value={syncSelectedBranch?.branch_id || ''}
                label="Branch *"
                notched={syncBranchFieldLabelNotched}
                displayEmpty
                input={(
                  <OutlinedInput
                    label="Branch *"
                    notched={syncBranchFieldLabelNotched}
                    endAdornment={
                      syncSelectedCompany && syncBranches.length === 0 ? (
                        <InputAdornment position="end" sx={{ mr: 2 }}>
                          <Tooltip title="Fetch branches" placement="top" arrow>
                            <span>
                              <IconButton
                                size="small"
                                disabled={syncBranchesRefreshing}
                                onClick={(e) => {
                                  // Keep click from toggling the Select menu
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleSyncFetchBranches();
                                }}
                                sx={{ p: 0.75 }}
                              >
                                {syncBranchesRefreshing ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <Refresh fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ) : null
                    }
                  />
                )}
                onOpen={(e) => {
                  // Lock menu width to the clicked select display element (prevents overflow on mobile)
                  const w = e?.currentTarget?.getBoundingClientRect?.().width;
                  setSyncBranchMenuWidth(typeof w === 'number' && w > 0 ? w : null);
                }}
                renderValue={(selected) => {
                  if (syncBranchesRefreshing) return 'Fetching braches…';
                  if (!selected && syncSelectedCompany && syncBranches.length === 0) {
                    return 'No branches';
                  }
                  if (!selected) return '';
                  const b = syncBranches.find((x) => x.branch_id === selected);
                  const label = b ? (b.branch_address || b.branch_id) : '';
                  if (!label) return '';
                  return (
                    <Tooltip title={label} placement="top" arrow>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          maxWidth: { xs: 160, sm: 420 },
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {label}
                      </Typography>
                    </Tooltip>
                  );
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: 'bottom',
                    horizontal: 'left',
                  },
                  transformOrigin: {
                    vertical: 'top',
                    horizontal: 'left',
                  },
                  PaperProps: {
                    sx: {
                      maxHeight: 300,
                      // Keep dropdown width aligned with the select field (fixes mobile overflow)
                      minWidth: syncBranchMenuWidth ?? undefined,
                      width: syncBranchMenuWidth ?? undefined,
                      overflowX: 'hidden',
                    },
                  },
                }}
                onChange={async (e) => {
                  const branchId = e.target.value;
                  const branch = syncBranches.find(b => b.branch_id === branchId);
                  setSyncSelectedBranch(branch);
                  setSyncSelectedApiKey(null);
                  setSyncApiKeys([]);

                  // Load active API keys for this company + branch (no branch_devices lookup)
                  if (branchId && syncSelectedCompany?.company_id) {
                    setSyncApiKeysLoading(true);
                    try {
                      const activeKeys = await getApiKeys(1000, 0, {
                        companyId: syncSelectedCompany.company_id,
                        branchId,
                        isActive: true,
                      });
                      const list = Array.isArray(activeKeys) ? activeKeys : [];
                      setSyncApiKeys(list);
                      if (list.length === 0) {
                        showSnackbar('No active API keys found for this company/branch. Create API key', 'warning');
                      }
                    } catch (err) {
                      showSnackbar(err?.message || 'Failed to load API keys', 'error');
                      setSyncApiKeys([]);
                    } finally {
                      setSyncApiKeysLoading(false);
                    }
                  }
                }}
              >
                {syncBranches.map((branch) => (
                  <SelectMenuItem
                    key={branch.branch_id}
                    value={branch.branch_id}
                    sx={{
                      width: '100%',
                    }}
                  >
                    <Tooltip title={branch.branch_address || ''} placement="right" arrow>
                      <Typography
                        variant="body2"
                        noWrap
                        sx={{
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {branch.branch_address}
                      </Typography>
                    </Tooltip>
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>

            {/* API Key Selector */}
            <FormControl fullWidth disabled={!syncSelectedBranch || syncApiKeysLoading}>
              <InputLabel>API Key *</InputLabel>
              <Select
                value={syncSelectedApiKey?.key_id || ''}
                input={(
                  <OutlinedInput
                    label="API Key *"
                    endAdornment={
                      syncSelectedBranch && !syncApiKeysLoading && syncApiKeys.length === 0 ? (
                        <InputAdornment position="end" sx={{ mr: 2 }}>
                          <Tooltip title="Create API key" placement="top" arrow>
                            <span>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  // Keep click from toggling the Select menu
                                  e.preventDefault();
                                  e.stopPropagation();
                                  setSyncDialogOpen(false);
                                  navigate('/api-keys');
                                }}
                                sx={{ p: 0.75 }}
                              >
                                <VpnKey fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </InputAdornment>
                      ) : null
                    }
                  />
                )}
                onChange={(e) => {
                  const keyId = e.target.value;
                  const key = syncApiKeys.find(k => k.key_id === keyId);
                  setSyncSelectedApiKey(key || null);
                }}
                label="API Key *"
              >
                {syncApiKeys.map((k) => (
                  <SelectMenuItem key={k.key_id} value={k.key_id}>
                    {k.key_name || k.key_id}
                  </SelectMenuItem>
                ))}
              </Select>
            </FormControl>

            {/* <Divider sx={{ my: 1 }} /> */}

            {/* Date Range Option */}
            <Box>
              <Button
                onClick={() => {
                  const newState = !useDateRange;
                  setUseDateRange(newState);
                  // Initialize with today's date when opening
                  if (newState && !syncFromDate && !syncToDate) {
                    const today = format(new Date(), 'yyyy-MM-dd');
                    setSyncFromDate(today);
                    setSyncToDate(today);
                  }
                }}
                color="primary"
                endIcon={useDateRange ? <ExpandLess /> : <ExpandMore />}
                sx={{
                  justifyContent: 'space-between',
                  textTransform: 'none',
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: 'rgba(25, 118, 210, 0.04)',
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="body1">
                    Sync with Date Range
                  </Typography>
                </Box>
              </Button>

              <Collapse in={useDateRange} timeout={400}>
                <Box sx={{
                  mt: 2,
                  p: 2.5
                }}>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    <Box sx={{ flex: 1, minWidth: '200px' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                        From
                      </Typography>
                      <TextField
                        fullWidth
                        value={syncFromDate ? format(new Date(syncFromDate), 'MMM dd, yyyy') : ''}
                        onClick={() => {
                          setTempDate(syncFromDate ? new Date(syncFromDate) : new Date());
                          setCurrentMonth(syncFromDate ? new Date(syncFromDate) : new Date());
                          setDatePickerOpen({ ...datePickerOpen, from: true });
                        }}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <CalendarMonth />
                            </InputAdornment>
                          ),
                        }}
                        placeholder="Select From date"
                        size="medium"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            cursor: 'pointer',
                          }
                        }}
                      />
                    </Box>

                    <Box sx={{ flex: 1, minWidth: '200px' }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                        To
                      </Typography>
                      <TextField
                        fullWidth
                        value={syncToDate ? format(new Date(syncToDate), 'MMM dd, yyyy') : ''}
                        onClick={() => {
                          if (!syncFromDate) {
                            showSnackbar('Please select From date first', 'warning');
                            return;
                          }
                          setTempDate(syncToDate ? new Date(syncToDate) : new Date(syncFromDate));
                          setCurrentMonth(syncToDate ? new Date(syncToDate) : new Date(syncFromDate));
                          setDatePickerOpen({ ...datePickerOpen, to: true });
                        }}
                        disabled={!syncFromDate}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <CalendarMonth />
                            </InputAdornment>
                          ),
                        }}
                        placeholder="Select To date"
                        size="medium"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            backgroundColor: 'white',
                            cursor: syncFromDate ? 'pointer' : 'not-allowed',
                          }
                        }}
                      />
                    </Box>
                  </Box>
                </Box>
              </Collapse>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => setSyncDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            disabled={!syncSelectedCompany || !syncSelectedBranch || !syncSelectedApiKey}
            onClick={async () => {
              if (!syncSelectedCompany || !syncSelectedBranch || !syncSelectedApiKey) {
                showSnackbar('Please select company, branch, and API key', 'warning');
                return;
              }

              setSyncDialogOpen(false);

              // Sync with optional date range FROM EXTERNAL API
              // Backend will look up API key from database based on company/branch/device
              const syncOptions = {
                companyId: syncSelectedCompany.company_id,
                branchId: syncSelectedBranch.branch_id,
                apiKeyId: syncSelectedApiKey.key_id,
              };

              // Only include dates if user selected the date range option
              if (useDateRange && syncFromDate && syncToDate) {
                // Validate dates
                const fromDate = new Date(syncFromDate);
                const toDate = new Date(syncToDate);
                if (fromDate > toDate) {
                  showSnackbar('From date cannot be after To date', 'error');
                  return;
                }

                const formattedStart = format(startOfDay(fromDate), "yyyy-MM-dd");
                const formattedEnd = format(endOfDay(toDate), "yyyy-MM-dd");

                setHrmsSince(formattedStart);
                setHrmsUntil(formattedEnd);
                syncOptions.from_date = formattedStart;
                syncOptions.to_date = formattedEnd;
              }

              // Call importHrms with sync=true, dates (if selected), and company/branch/device IDs
              // Backend will fetch API key from database and use it as Branch-token header
              importHrms(1, true, syncOptions);
            }}
            startIcon={<Sync />}
            color="primary"
          >
            Sync
          </Button>
        </DialogActions>
      </Dialog>

      {/* Voice Upload Modal */}
      <Dialog
        open={voiceModalOpen}
        onClose={closeVoiceModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: { borderRadius: '12px', overflow: 'hidden' }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          backgroundColor: primary,
          color: 'white',
          py: 1.5
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <VolumeUp />
            <Typography variant="h6" component="span">
              {voiceModalEmployee?.mode === 'update' ? 'Update' : 'Add'} Voice for {voiceModalEmployee?.employeeName || 'Employee'}
            </Typography>
          </Box>
          <IconButton onClick={closeVoiceModal} sx={{ color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2, mt: 2.0 }}>
          {/* IN Voice Section */}
          <Box sx={{
            mb: 3,
            p: 2.5,
            border: '1px solid',
            borderColor: '#e0e0e0',
            borderRadius: '12px',
            backgroundColor: '#fff'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Login sx={{ fontSize: 20, color: '#555' }} />
              IN Voice (Check-in)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Upload Button */}
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ textTransform: 'none' }}
              >
                Upload WAV
                <input
                  type="file"
                  accept=".wav,audio/wav"
                  hidden
                  onChange={(e) => handleFileUpload(e, 'checkin')}
                />
              </Button>

              <Button
                variant="outlined"
                startIcon={<SmartToy />}
                onClick={() => openAIVoiceDialog('checkin')}
                sx={{ textTransform: 'none' }}
              >
                AI Generate Voice
              </Button>

              {/* Record Button */}
              {!isRecordingCheckin ? (
                <Button
                  variant="outlined"
                  startIcon={<Mic />}
                  onClick={() => startRecording('checkin')}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                    '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                  }}
                >
                  Record
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={() => stopRecording('checkin')}
                  sx={{
                    textTransform: 'none',
                    animation: 'pulse 1.5s ease-in-out infinite',
                    '@keyframes pulse': {
                      '0%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0.4)' },
                      '70%': { boxShadow: '0 0 0 10px rgba(211, 47, 47, 0)' },
                      '100%': { boxShadow: '0 0 0 0 rgba(211, 47, 47, 0)' },
                    }
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      animation: 'blink 0.8s ease-in-out infinite',
                      '@keyframes blink': {
                        '0%, 100%': { opacity: 1 },
                        '50%': { opacity: 0.3 },
                      }
                    }} />
                    Recording...
                  </Box>
                </Button>
              )}
            </Box>

            {/* Recording Animation */}
            {isRecordingCheckin && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 24 }}>
                  {[...Array(5)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 4,
                        backgroundColor: '#d32f2f',
                        borderRadius: 1,
                        animation: `wave 0.8s ease-in-out infinite`,
                        animationDelay: `${i * 0.1}s`,
                        '@keyframes wave': {
                          '0%, 100%': { height: 8 },
                          '50%': { height: 24 },
                        }
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="error">
                  Speak now...
                </Typography>
              </Box>
            )}

            {/* Audio Player */}
            {checkinAudioUrl && !isRecordingCheckin && (
              <Box sx={{ mt: 2.5 }}>
                <AudioPlayer
                  src={checkinAudioUrl}
                  showJumpControls={false}
                  showDownloadProgress={false}
                  showFilledProgress={true}
                  customAdditionalControls={[]}
                  customVolumeControls={[]}
                  layout="horizontal-reverse"
                  style={{
                    borderRadius: '8px',
                    boxShadow: 'none',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              </Box>
            )}
          </Box>

          {/* OUT Voice Section */}
          <Box sx={{
            p: 2.5,
            border: '1px solid',
            borderColor: '#e0e0e0',
            borderRadius: '12px',
            backgroundColor: '#fff'
          }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#333', mb: 2.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Logout sx={{ fontSize: 20, color: '#555' }} />
              OUT Voice (Check-out)
            </Typography>

            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
              {/* Upload Button */}
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUpload />}
                sx={{ textTransform: 'none' }}
              >
                Upload WAV
                <input
                  type="file"
                  accept=".wav,audio/wav"
                  hidden
                  onChange={(e) => handleFileUpload(e, 'checkout')}
                />
              </Button>

              <Button
                variant="outlined"
                startIcon={<SmartToy />}
                onClick={() => openAIVoiceDialog('checkout')}
                sx={{ textTransform: 'none' }}
              >
                AI Generate Voice
              </Button>

              {/* Record Button */}
              {!isRecordingCheckout ? (
                <Button
                  variant="outlined"
                  startIcon={<Mic />}
                  onClick={() => startRecording('checkout')}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#d32f2f',
                    color: '#d32f2f',
                    '&:hover': { borderColor: '#b71c1c', backgroundColor: 'rgba(211, 47, 47, 0.04)' }
                  }}
                >
                  Record
                </Button>
              ) : (
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<Stop />}
                  onClick={() => stopRecording('checkout')}
                  sx={{
                    textTransform: 'none',
                    animation: 'pulse 1.5s ease-in-out infinite',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      backgroundColor: 'white',
                      animation: 'blink 0.8s ease-in-out infinite',
                    }} />
                    Recording...
                  </Box>
                </Button>
              )}
            </Box>

            {/* Recording Animation */}
            {isRecordingCheckout && (
              <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, height: 24 }}>
                  {[...Array(5)].map((_, i) => (
                    <Box
                      key={i}
                      sx={{
                        width: 4,
                        backgroundColor: '#d32f2f',
                        borderRadius: 1,
                        animation: `wave 0.8s ease-in-out infinite`,
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </Box>
                <Typography variant="caption" color="error">
                  Speak now...
                </Typography>
              </Box>
            )}

            {/* Audio Player */}
            {checkoutAudioUrl && !isRecordingCheckout && (
              <Box sx={{ mt: 2.5 }}>
                <AudioPlayer
                  src={checkoutAudioUrl}
                  showJumpControls={false}
                  showDownloadProgress={false}
                  showFilledProgress={true}
                  customAdditionalControls={[]}
                  customVolumeControls={[]}
                  layout="horizontal-reverse"
                  style={{
                    borderRadius: '8px',
                    boxShadow: 'none',
                    backgroundColor: '#f5f5f5'
                  }}
                />
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={closeVoiceModal}
            disabled={voiceUploading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveVoices}
            disabled={voiceUploading || (!checkinVoiceBlob && !checkoutVoiceBlob)}
            startIcon={voiceUploading ? <CircularProgress size={16} color="inherit" /> : <CloudUpload />}
            sx={{ backgroundColor: primary, '&:hover': { backgroundColor: '#2e3d66' } }}
          >
            {voiceUploading ? 'Uploading...' : 'Save Voices'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={aiVoiceDialogOpen}
        onClose={closeAIVoiceDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          AI Generate {aiVoiceType === 'checkin' ? 'IN' : 'OUT'} Voice
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            Key Type: Cartesia API Key
          </Typography>
          <Autocomplete
            freeSolo
            fullWidth
            options={aiVoiceSavedKeys}
            value={aiVoiceSavedKeys.find((key) => key.credential_id === aiVoiceSelectedKeyId) || null}
            inputValue={aiVoiceApiKeyInput}
            onChange={(_, newValue) => {
              if (typeof newValue === 'string') {
                setAiVoiceSelectedKeyId('');
                setAiVoiceApiKeyInput(newValue);
                return;
              }
              if (newValue?.credential_id) {
                setAiVoiceSelectedKeyId(newValue.credential_id);
                setAiVoiceApiKeyInput(newValue.credential_hint || '');
                return;
              }
              setAiVoiceSelectedKeyId('');
              setAiVoiceApiKeyInput('');
            }}
            onInputChange={(_, newInputValue, reason) => {
              setAiVoiceApiKeyInput(newInputValue);
              if (reason === 'input') {
                setAiVoiceSelectedKeyId('');
              }
            }}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.credential_hint || option.credential_id)}
            loading={aiVoiceLoadingKeys}
            disabled={aiVoiceGenerating}
            sx={{ mb: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Saved API Key"
                type={aiVoiceSelectedKeyId ? 'text' : 'password'}
                placeholder="Select saved key or enter new API key"
                autoComplete="off"
                inputProps={{ ...params.inputProps, autoComplete: 'off' }}
              />
            )}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            label="Voice Text"
            value={aiVoiceText}
            onChange={(e) => setAiVoiceText(e.target.value)}
            disabled={aiVoiceGenerating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeAIVoiceDialog} disabled={aiVoiceGenerating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleGenerateAIVoice}
            disabled={aiVoiceGenerating || (!aiVoiceSelectedKeyId && !aiVoiceApiKeyInput.trim())}
            startIcon={aiVoiceGenerating ? <CircularProgress size={16} color="inherit" /> : <SmartToy />}
            sx={{ backgroundColor: primary, '&:hover': { backgroundColor: '#2e3d66' } }}
          >
            {aiVoiceGenerating ? 'Generating...' : 'Generate Voice'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Find Person Modal */}
      <Dialog
        open={findPersonModalOpen}
        onClose={handleFindPersonModalClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 2,
            boxShadow: isMobile ? 'none' : '0 10px 36px rgba(15, 23, 42, 0.16)',
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          py: { xs: 1.5, sm: 2 },
          borderBottom: '1px solid',
          borderColor: 'divider',
          fontSize: { xs: '1.1rem', sm: '1.25rem' },
        }}>
          <Typography variant="inherit" sx={{ fontWeight: 600 }}>Find Person in Logs</Typography>
          <IconButton
            size="small"
            onClick={handleFindPersonModalClose}
            disabled={findPersonLoading}
            sx={{ bgcolor: '#f1f5f9', '&:hover': { bgcolor: '#e2e8f0' } }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
          {findPersonModalPerson && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {/* Person Info */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Employee
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  {findPersonModalPerson.name} ({findPersonModalPerson.person_id})
                </Typography>
              </Box>

              {/* Select which enrolled face image to use for the search */}
              <Box>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Select face for search
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  value={selectedAngle}
                  onChange={(e, newVal) => {
                    if (newVal != null) setSelectedAngle(String(newVal));
                  }}
                  disabled={findPersonLoading}
                  sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 2,
                    py: 0.5,
                    '& .MuiToggleButtonGroup-grouped': {
                      margin: 0,
                      borderRadius: '12px !important',
                      border: '2px solid !important',
                    },
                  }}
                >
                  {findPersonModalPerson.faces
                    .filter((f) => !f.deleted_at)
                    .map((face) => {
                      const faceIdStr = String(face.face_id);
                      const isSelected = String(selectedAngle) === faceIdStr;
                      const thumb = findPersonFaceThumbUrls[faceIdStr];
                      const angleLabel = formatAngleLabel(face.face_angle, face.metadata || {});
                      return (
                        <ToggleButton
                          key={faceIdStr}
                          value={faceIdStr}
                          aria-label={angleLabel}
                          aria-pressed={isSelected}
                          sx={{
                            position: 'relative',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                            px: 1.5,
                            py: 1.5,
                            minWidth: { xs: 112, sm: 128 },
                            maxWidth: { xs: 140, sm: 148 },
                            textTransform: 'none',
                            borderColor: 'divider !important',
                            bgcolor: 'background.paper',
                            transition: 'border-color 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease',
                            '&:hover': {
                              borderColor: 'rgba(100, 116, 139, 0.9) !important',
                              bgcolor: 'action.hover',
                            },
                            '&.Mui-selected': {
                              borderColor: `${primary} !important`,
                              bgcolor: 'rgba(64, 81, 138, 0.1) !important',
                              boxShadow: `0 0 0 3px rgba(64, 81, 138, 0.28), 0 6px 16px rgba(15, 23, 42, 0.1)`,
                            },
                            '&.Mui-selected:hover': {
                              bgcolor: 'rgba(64, 81, 138, 0.14) !important',
                              borderColor: `${primary} !important`,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: { xs: 88, sm: 100 },
                              height: { xs: 88, sm: 100 },
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              bgcolor: '#eef2f6',
                              flexShrink: 0,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              border: '1px solid rgba(148, 163, 184, 0.35)',
                            }}
                          >
                            {thumb ? (
                              <Box
                                component="img"
                                src={thumb}
                                alt={angleLabel}
                                sx={{
                                  maxWidth: '100%',
                                  maxHeight: '100%',
                                  width: 'auto',
                                  height: 'auto',
                                  objectFit: 'contain',
                                  objectPosition: 'center center',
                                  display: 'block',
                                }}
                              />
                            ) : thumb === '' ? (
                              <Box
                                sx={{
                                  width: '100%',
                                  height: '100%',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  px: 0.5,
                                  textAlign: 'center',
                                }}
                              >
                                <Typography variant="caption" color="text.secondary">
                                  No preview
                                </Typography>
                              </Box>
                            ) : (
                              <Skeleton variant="rectangular" width="100%" height="100%" animation="wave" />
                            )}
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              fontWeight: 700,
                              maxWidth: 120,
                              textAlign: 'center',
                              lineHeight: 1.3,
                              color: isSelected ? primary : 'text.primary',
                              letterSpacing: 0.02,
                            }}
                          >
                            {angleLabel}
                          </Typography>
                        </ToggleButton>
                      );
                    })}
                </ToggleButtonGroup>
              </Box>

              <FormControl component="fieldset" variant="standard" sx={{ width: '100%' }}>
                <Typography component="legend" variant="subtitle2" color="text.secondary" sx={{ mb: 0.75 }}>
                  Find person within
                </Typography>
                <RadioGroup
                  row={!isMobile}
                  value={findPersonLogType}
                  onChange={(e) => setFindPersonLogType(e.target.value)}
                >
                  <FormControlLabel
                    value="audit"
                    control={<Radio size="small" />}
                    label="In/Out logs"
                    disabled={findPersonLoading}
                  />
                  <FormControlLabel
                    value="activity"
                    control={<Radio size="small" />}
                    label="Activity logs"
                    disabled={findPersonLoading}
                  />
                </RadioGroup>
              </FormControl>

              {/* Similarity Threshold */}
              <Box sx={{ width: '100%', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'flex-start' }, gap: { xs: 1.25, sm: 3 } }}>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    minWidth: 'fit-content',
                    whiteSpace: 'nowrap',
                    flexShrink: 0,
                    lineHeight: { xs: 1.4, sm: '40px' },
                  }}
                >
                  Similarity Threshold: {parseFloat(similarityThreshold || 0).toFixed(2)}
                </Typography>
                <Box sx={{ flex: 1, minWidth: 0, maxWidth: { xs: '100%', sm: 300 }, mt: { xs: 0, sm: 0.5 } }}>
                  <Slider
                    value={parseFloat(similarityThreshold || 0.3)}
                    onChange={(e, newValue) => setSimilarityThreshold(String(newValue))}
                    min={0}
                    max={1}
                    step={0.01}
                    marks={[
                      { value: 0, label: '0' },
                      { value: 0.25, label: '0.25' },
                      { value: 0.5, label: '0.5' },
                      { value: 0.75, label: '0.75' },
                      { value: 1, label: '1' },
                    ]}
                    disabled={findPersonLoading}
                    sx={{
                      width: '100%',
                      '& .MuiSlider-thumb': {
                        width: { xs: 14, sm: 10 },
                        height: { xs: 14, sm: 10 },
                      },
                      '& .MuiSlider-markLabel': {
                        fontSize: { xs: '0.75rem', sm: '0.65rem' },
                        top: { xs: 26, sm: 20 },
                      },
                      '& .MuiSlider-track, & .MuiSlider-rail': {
                        height: { xs: 5, sm: 4 },
                      },
                      '& .MuiSlider-mark': {
                        top: '50%',
                      },
                    }}
                  />
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2, sm: 3 }, py: { xs: 1.5, sm: 2 }, borderTop: '1px solid', borderColor: 'divider', gap: 1, flexWrap: 'wrap' }}>
          <Button
            onClick={handleFindPersonModalClose}
            disabled={findPersonLoading}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleFindPersonSubmit}
            disabled={findPersonLoading || !selectedAngle}
            startIcon={findPersonLoading ? <CircularProgress size={16} color="inherit" /> : null}
            sx={{ backgroundColor: primary, '&:hover': { backgroundColor: '#2e3d66' } }}
          >
            {findPersonLoading ? 'Processing...' : 'Find Person'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Custom Date Picker Modal */}
      <Dialog
        open={datePickerOpen.from || datePickerOpen.to}
        onClose={() => setDatePickerOpen({ from: false, to: false })}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: 'hidden'
          }
        }}
      >
        <DialogTitle sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          borderBottom: '1px solid',
          borderColor: 'divider'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h6">
              Select {datePickerOpen.from ? 'From' : 'To'} Date
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setDatePickerOpen({ from: false, to: false })}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 2 }}>
          {/* Month/Year Navigation */}
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            m: 2
          }}>
            <IconButton
              size="small"
              onClick={() => setCurrentMonth(subMonthsFn(currentMonth, 1))}
              sx={{ color: 'text.secondary' }}
            >
              <ArrowBackIos fontSize="small" />
            </IconButton>

            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Select
                value={format(currentMonth, 'MMMM')}
                onChange={(e) => {
                  const monthIndex = new Date(e.target.value + ' 1, 2000').getMonth();
                  setCurrentMonth(new Date(currentMonth.getFullYear(), monthIndex, 1));
                }}
                size="small"
                sx={{ minWidth: 100 }}
              >
                {['January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'].map((month) => (
                    <SelectMenuItem key={month} value={month}>{month}</SelectMenuItem>
                  ))}
              </Select>

              <Select
                value={currentMonth.getFullYear()}
                onChange={(e) => {
                  setCurrentMonth(new Date(parseInt(e.target.value), currentMonth.getMonth(), 1));
                }}
                size="small"
                sx={{ minWidth: 80 }}
              >
                {Array.from({ length: 10 }, (_, i) => new Date().getFullYear() - 5 + i).map((year) => (
                  <SelectMenuItem key={year} value={year}>{year}</SelectMenuItem>
                ))}
              </Select>
            </Box>

            <IconButton
              size="small"
              onClick={() => {
                const nextMonth = addMonths(currentMonth, 1);
                if (nextMonth <= new Date()) {
                  setCurrentMonth(nextMonth);
                }
              }}
              disabled={addMonths(currentMonth, 1) > new Date()}
              sx={{ color: 'text.secondary' }}
            >
              <ArrowForwardIos fontSize="small" />
            </IconButton>
          </Box>

          {/* Calendar Grid */}
          <Box>
            {/* Weekday Headers */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.5,
              mb: 1
            }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
                <Typography
                  key={day}
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 600,
                    color: 'text.secondary',
                    py: 0.5
                  }}
                >
                  {day}
                </Typography>
              ))}
            </Box>

            {/* Calendar Days */}
            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.5
            }}>
              {(() => {
                const monthStart = startOfMonth(currentMonth);
                const monthEnd = endOfMonth(currentMonth);
                const startDate = startOfWeek(monthStart);
                const endDate = endOfWeek(monthEnd);
                const days = eachDayOfInterval({ start: startDate, end: endDate });
                const today = new Date();
                const maxDate = new Date();
                maxDate.setHours(23, 59, 59, 999);

                return days.map((day, idx) => {
                  const isCurrentMonth = day >= monthStart && day <= monthEnd;
                  const isSelected = tempDate && isSameDay(day, tempDate);
                  const isToday = isSameDay(day, today);
                  const isFuture = day > maxDate;
                  const isDisabled = isFuture || (datePickerOpen.to && syncFromDate && day < new Date(syncFromDate));

                  return (
                    <Button
                      key={idx}
                      onClick={() => {
                        if (!isDisabled) {
                          setTempDate(day);
                        }
                      }}
                      disabled={isDisabled}
                      sx={{
                        minWidth: 36,
                        height: 36,
                        p: 0,
                        fontSize: '0.875rem',
                        color: isSelected
                          ? 'white'
                          : isCurrentMonth
                            ? 'text.primary'
                            : 'text.disabled',
                        backgroundColor: isSelected
                          ? 'primary.main'
                          : isToday
                            ? 'action.hover'
                            : 'transparent',
                        '&:hover': {
                          backgroundColor: isDisabled
                            ? 'transparent'
                            : isSelected
                              ? 'primary.dark'
                              : 'action.hover',
                        },
                        '&.Mui-disabled': {
                          color: 'text.disabled',
                          opacity: 0.4
                        }
                      }}
                    >
                      {format(day, 'd')}
                    </Button>
                  );
                });
              })()}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              setTempDate(null);
              setDatePickerOpen({ from: false, to: false });
            }}
            sx={{ textTransform: 'uppercase' }}
          >
            Clear
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (tempDate) {
                const selectedDate = format(tempDate, 'yyyy-MM-dd');
                if (datePickerOpen.from) {
                  setSyncFromDate(selectedDate);
                  // If To date is before new From date, reset it
                  if (syncToDate && new Date(syncToDate) < tempDate) {
                    setSyncToDate('');
                  }
                } else {
                  setSyncToDate(selectedDate);
                }
              }
              setDatePickerOpen({ from: false, to: false });
            }}
            sx={{ textTransform: 'uppercase' }}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validity Dialog */}
      <Dialog
        open={validityDialog.open}
        onClose={() => { if (!validityDialog.saving) closeValidityDialog(); }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Set Employee Validity</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 1.5, '&&': { pt: 2.5 } }}>
          <TextField
            size="small"
            label="Employee Name"
            value={validityDialog.person?.name || '-'}
            InputProps={{ readOnly: true }}
          />
          <TextField size="small"
            label="Employee ID"
            value={validityDialog.person?.person_id || '-'}
            InputProps={{ readOnly: true }}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!validityDialog.unlimited}
                onChange={(e) => setValidityDialog((prev) => ({ ...prev, unlimited: e.target.checked }))}
                disabled={validityDialog.saving}
              />
            }
            label="Unlimited Validity"
          />
          <TextField
            size="small"
            label="Validity (start → end)"
            value={
              validityDialog.unlimited
                ? ''
                : (() => {
                  const vf = validityDialog.validFrom;
                  const vt = validityDialog.validTill;
                  if (!vf && !vt) return 'Select date time range';
                  try {
                    const s = vf ? new Date(vf) : null;
                    const e = vt ? new Date(vt) : null;
                    if (s && e && !Number.isNaN(s.getTime()) && !Number.isNaN(e.getTime())) {
                      return `${format(s, 'MMM d, yyyy h:mm a')} → ${format(e, 'MMM d, yyyy h:mm a')}`;
                    }
                  } catch {
                    /* ignore */
                  }
                  return [vf, vt].filter(Boolean).join(' → ') || 'Click calendar to set';
                })()
            }
            placeholder="Select range"
            onClick={() => {
              if (!validityDialog.unlimited && !validityDialog.saving) setValidityPickerOpen(true);
            }}
            disabled={validityDialog.unlimited || validityDialog.saving}
            InputProps={{
              readOnly: true,
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    edge="end"
                    size="small"
                    disabled={validityDialog.unlimited || validityDialog.saving}
                    onClick={() => setValidityPickerOpen(true)}
                    aria-label="Open validity calendar"
                  >
                    <CalendarMonth fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            InputLabelProps={{ shrink: true }}
            sx={{
              '& .MuiOutlinedInput-root': { cursor: validityDialog.unlimited ? 'default' : 'pointer' },
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeValidityDialog} disabled={validityDialog.saving}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveValidityWithConfirm} disabled={validityDialog.saving}>
            {validityDialog.saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Validity: reuse same DateRangePicker + times as elsewhere in this file */}
      <Dialog
        open={validityPickerOpen}
        onClose={() => setValidityPickerOpen(false)}
        maxWidth={false}
        fullWidth={false}
        PaperProps={{
          sx: {
            maxHeight: '90vh',
            overflow: 'auto',
            width: 'fit-content',
            maxWidth: 'calc(100vw - 24px)',
            m: { xs: 1.5, sm: 2 },
          },
        }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 1, py: 1.5 }}>
          <Typography variant="subtitle1" component="span" sx={{ fontWeight: 600 }}>
            Select validity range
          </Typography>
          <IconButton size="small" onClick={() => setValidityPickerOpen(false)} aria-label="Close">
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0, width: 'fit-content', maxWidth: '100%', overflowX: 'auto' }}>
          <DateRangePickerContent
            dateRange={validityDateRange}
            onDateRangeChange={setValidityDateRange}
            disableMaxDate
            showTimeFields
            startTime={validityStartTime}
            endTime={validityEndTime}
            onStartTimeChange={setValidityStartTime}
            onEndTimeChange={setValidityEndTime}
          />
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => {
              const today = new Date();
              setValidityDateRange([{ startDate: today, endDate: today, key: 'selection' }]);
              setValidityStartTime('00:00');
              setValidityEndTime('23:59');
            }}
            sx={{ textTransform: 'none' }}
          >
            Reset
          </Button>
          <Button variant="outlined" onClick={() => setValidityPickerOpen(false)}>
            Cancel
          </Button>
          <Button variant="contained" onClick={applyValidityRangePicker}>
            Apply
          </Button>
        </DialogActions>
      </Dialog>

      <SimilarityThresholdDialog
        open={similarityThresholdDialog.open}
        person={similarityThresholdDialog.person}
        onClose={() => setSimilarityThresholdDialog({ open: false, person: null })}
        onSaved={handleSimilarityThresholdSaved}
        showSnackbar={showSnackbar}
      />

    </Box>
  );
};

export default Employees;
